import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  OnInit,
  OnDestroy,
  ViewChild,
  runInInjectionContext,
} from '@angular/core';
import { Location } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription, interval, EMPTY, of, timer, defer, Subject } from 'rxjs';
import { filter, switchMap, takeWhile, take, catchError, first, timeout, finalize, takeUntil, tap } from 'rxjs/operators';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import {
  CleanupDecision,
  OnboardingChecklistService,
  OnboardingState,
  OnboardingStep,
} from './onboarding-checklist.service';
import {
  ActivationGoalId,
  ActivationJourneyService,
  ActivationJourneyState,
  ActivationStatusPhase,
  ActivationStepId,
  ACTIVATION_GOALS,
  ACTIVATION_SLUG_TITLES,
  ACTIVATION_STATUS_PHASES,
  ACTIVATION_STEPS,
  TRUST_FIRST_PURPOSE_GOALS,
  activationSlugToStep,
  activationStepToSlug,
  ActorRoute,
  InstallRecoveryBranch,
  JOURNEY_GUIDE_COPY,
  JOURNEY_TRENDS_PREVIEW,
  SetupMethod,
  TrustFirstPurposeId,
  installModeFromCollection,
  shouldSkipAnnounce,
} from './activation-journey.service';
import { TrustFirstAnnounceFact, TrustFirstFaqItem } from './trust-first-copy';
import { ProjectService } from 'src/app/Services/project.service';
import { ShiftService } from 'src/app/Services/shift.service';
import { LoginService } from 'src/app/Services/login.service';
import { SharedService } from 'src/app/Services/sharedService';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { AuthService } from 'src/app/Services/auth.service';
import { passwordValidator } from 'src/app/password-validators';
import Swal from 'sweetalert2';
import {
  trackingModeLabel,
  trackingModeLabelForVersionType,
  TrackingModeId,
} from 'src/app/utils/tracking-mode-labels.util';
import { EntitlementsService } from 'src/app/Services/entitlements.service';
import { EntitlementKey } from 'src/app/entitlements/entitlement-keys';
import { isOnboardingTrackingModeAllowed } from 'src/app/entitlements/plan-software-type.util';
import { buildNamedAgentPackageFileName } from 'src/app/agent-deploy/build-windows-rust-bat';
import { sysConfig } from 'src/app/sysConfig';
import {
  RUST_AGENT_ASSET_PATHS,
  resolveRustAgentUsesMsiPackage,
  resolveWindowsAgentRuntime,
  type AgentInstallerMode,
} from 'src/app/agent-deploy/agent-deploy.config';
import { rustAgentMsiNeedsSidecar } from 'src/app/agent-deploy/build-windows-rust-msi';
import { environment } from 'src/environments/environment';
import { ExampleWorkdayService } from 'src/app/example-workday/example-workday.service';
import { ActivationDestinationService } from './activation-destination.service';
import { ActivationNavigationService } from './activation-navigation.service';
import { ActivationReadinessService } from './activation-readiness.service';
import { ActivationEvidenceService } from './activation-evidence.service';
import { ActivationEmployeeHandoffService } from './activation-employee-handoff.service';
import { ModalService } from 'src/app/Services/modal.service';
import { DownloadModalComponent } from 'src/app/Admin/create-user/download-modal/download-modal.component';
import { DownloadModalCloseResult } from 'src/app/Admin/create-user/create-user-dialog.types';
import { resolveEmployeeDisplayName } from 'src/app/utils/employee-display-name.util';
import { trackProductEvent } from '../utils/product-analytics.util';
import { DeviceDetectionService, DesktopAgentOs } from 'src/app/Services/device-detection.service';
import { UserService } from 'src/app/Services/user.service';
import { WhatYouControlDialogComponent } from 'src/app/components/what-you-control-dialog/what-you-control-dialog.component';
import { ContactPhoneCaptureService } from './contact-phone-capture.service';
import { JourneyContactPhoneStripComponent } from './journey-contact-phone-strip/journey-contact-phone-strip.component';
import { JourneyAssistedSetupDialogComponent } from './journey-assisted-setup-dialog/journey-assisted-setup-dialog.component';
import {
  TEAM_SIZE_OPTIONS,
  assistTier,
  isKnownTeamSize,
  normalizeTeamSizeForSelect,
  type AssistTier,
} from './team-size.util';

@Component({
    selector: 'app-onboarding-checklist',
    templateUrl: './onboarding-checklist.component.html',
    styleUrl: './onboarding-checklist.component.css',
    standalone: false
})
export class OnboardingChecklistComponent implements OnInit, OnDestroy {

  state: OnboardingState;
  currentStepId: string = 'mode_selection';
  isLoading: boolean = false;
  userName: string = '';
  userEmail: string = '';

  /** Guided activation journey (local/staging when flag on). */
  journeyEnabled = false;
  journeyState: ActivationJourneyState | null = null;
  readonly activationGoals = ACTIVATION_GOALS;
  readonly activationStatusPhases = ACTIVATION_STATUS_PHASES;
  selectedGoal: ActivationGoalId | null = null;
  modeEducationPage: 'compare' | 'choose' = 'compare';
  /** True when Adjust mode is opened from first-day review (must not auto-skip mode_education). */
  adjustingModeFromReview = false;
  installRecoveryBranch: InstallRecoveryBranch | null = null;
  actorRoute: ActorRoute | null = null;
  firstEmployeeSilent = false;
  privateTestReady = false;
  lastDownloadedPackageName = '';
  /** Dual-path first employee: invite (Visible) vs computer setup (Silent). */
  setupMethod: SetupMethod | null = null;
  authorisedUseConfirmed = false;
  showLoginDetails = false;
  showCaptureFacts = false;
  /** @deprecated Private test is always Visible; kept for transitional specs. */
  privateTestVisibility: 'visible' | 'hidden' = 'visible';
  showHireFairGuide = false;
  showRemovalHow = false;
  /** Fair-setup progressive disclosure (privacy/policy basics). */
  showFairSetupBasics = false;
  showFairSetupMore = false;
  showProofObjections = false;
  showProofMore = false;
  cleanupDecisionSaving: CleanupDecision | null = null;
  cleanupDecisionError = '';
  @ViewChild('firstEmployeeHeading') firstEmployeeHeading?: ElementRef<HTMLHeadingElement>;
  @ViewChild('workspaceNameInput') workspaceNameInput?: ElementRef<HTMLInputElement>;
  @ViewChild('readyContactPhoneStrip') readyContactPhoneStrip?: JourneyContactPhoneStripComponent;
  @ViewChild('proofContactPhoneStrip') proofContactPhoneStrip?: JourneyContactPhoneStripComponent;
  @ViewChild('fairContactPhoneStrip') fairContactPhoneStrip?: JourneyContactPhoneStripComponent;
  /** Collapsed by default so the one-employee CTA is not buried under education. */
  showProofBounds = false;
  showFirstDayObjections = false;
  /** Quiet tertiary: opens guided Example workday route (never recommended over one-employee). */
  showSampleWorkday = false;
  /** Ready-screen product story tabs (`live` | `replay` | `trends` | `act`). */
  selectedPreviewId: (typeof JOURNEY_GUIDE_COPY.outcomes)[number]['id'] = 'live';
  removalDownloadStarted = false;
  connectWaitStartedAt: number | null = null;
  /** Ticks once a second while waiting so the elapsed clock and 90s help stay live. */
  connectWaitTick = 0;
  private connectWaitClockSub?: Subscription;
  /** True after an offline Firebase snapshot in this wait — used to tell watched vs already online. */
  private connectWaitSawOffline = false;
  smartScreenPreviewOpen = false;
  browserKeepGuidanceOpen = false;
  installOpenHelpOpen = false;
  /** Collapsed desktop-login fallback under the download CTA. */
  privateTestLoginOpen = false;
  /** Collapsed antivirus tip under the download CTA. */
  installAntivirusOpen = false;
  installHelpNudged = false;
  workspaceName = '';
  workspaceNameSaved = '';
  workspaceNameEditing = false;
  workspaceNameStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  /** Required team size on Ready — stores meta.teamSize; Continue stays disabled until set. */
  teamSize = '';
  teamSizeSaved = '';
  teamSizeStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  readonly teamSizeOptions = TEAM_SIZE_OPTIONS;
  assistedSetupRequested = false;
  private teamSizeSavedStatusTimer: ReturnType<typeof setTimeout> | null = null;
  /** Optional contact capture on Ready — stores meta.phone_number, never OTP / never blocks CTA. */
  contactPhone = '';
  contactPhoneSaved = '';
  contactPhoneEditing = false;
  contactPhoneStatus: 'idle' | 'saving' | 'saved' | 'error' | 'invalid' = 'idle';
  /** Soft insist once before Ready CTAs if no number yet — never a hard gate / never OTP. */
  contactPhoneNudgeVisible = false;
  /** One-time hold on Continue later before this computer connects. Second click still leaves. */
  privateTestExitNudgeVisible = false;
  private contactPhoneNudgeDismissed = false;
  /** Session dismiss for after-proof / fair-setup re-asks. */
  proofContactPhoneDismissed = false;
  fairContactPhoneDismissed = false;
  private workspacePromptShownTracked = false;
  private workspaceNameSavedStatusTimer: ReturnType<typeof setTimeout> | null = null;
  private contactPhoneSavedStatusTimer: ReturnType<typeof setTimeout> | null = null;
  private helpNudgeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private helpNudgeSnackRef?: MatSnackBarRef<any>;
  private helpNudgeShown = false;
  private hasTrackedSmartScreenPreview = false;
  private hasTrackedBrowserKeepGuidance = false;
  private screenshotAutoRetried = false;
  private screenshotRetryTimeout: ReturnType<typeof setTimeout> | null = null;
  private installDownloadCtaUnlockTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly HELP_NUDGE_DELAY_MS = 90000;
  /** Reactive recovery flag (replaces non-reactive Date.now() getter). */
  showConnectionRecovery = false;
  connectionCheckTimedOut = false;
  isReissuingPackage = false;
  /** Hero-install: expand missing/blocked download help (not a primary CTA). */
  showMissingSetupFileHelp = false;
  isRefreshingFirstDayEvidence = false;
  firstDayEvidenceRefreshFailed = false;
  private connectionRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private evidenceRefreshSub?: Subscription;
  /** Employee id currently bound to the Firebase connection watch. */
  private connectionWatchEmployeeId: number | null = null;
  /** Help chips / recovery only after ~90s — do not dump failure UI on first paint. */
  private readonly CONNECTION_RECOVERY_MS = 90000;
  readonly guideCopy = JOURNEY_GUIDE_COPY;
  readonly trendsPreview = JOURNEY_TRENDS_PREVIEW;
  readonly trustFirstPurposes = TRUST_FIRST_PURPOSE_GOALS;

  /** Purpose step */
  selectedPurposeId: TrustFirstPurposeId | null = null;
  purposeOtherText = '';

  /** Collection step */
  collectionAppsOn = true;
  collectionScreenshotsOn = true;
  /** Recommended default; plan floor may be lower (e.g. 5). */
  collectionScreenshotInterval = 15;
  collectionLiveViewAllowed = true;
  collectionLiveAuditAvailable = false;
  collectionEmployeeControlled = true;
  collectionTrayOn = true;
  collectionShiftLabel: string | null = null;
  collectionShiftId: number | null = null;
  collectionShiftStart = '09:00';
  collectionShiftEnd = '18:00';
  collectionShiftDaysLabel = '';
  collectionBeat = 0;
  readonly collectionBeatDots = [0, 1, 2];
  /** URL `beat` values — one page per collection answer for GA and Clarity. */
  readonly collectionBeatSlugs = ['screenshots', 'mode', 'confirm'];
  /** Older links from when mode and visibility were two separate beats. */
  private readonly legacyCollectionBeatSlugs: Record<string, number> = { pause: 1, tray: 1 };
  scheduleSaveStatus: '' | 'saving' | 'saved' = '';
  private collectionShiftRaw: any = null;
  collectionSaveStatus: '' | 'saving' | 'saved' = '';
  private readonly collectionSaveDestroyed = new Subject<void>();
  /**
   * Memo for template *ngFor sources. A getter that returns a fresh array/object every
   * change-detection pass never lets the view settle, so Angular's detectChangesInViewWhileDirty
   * keeps re-running (the Collection "mode" beat freeze). Cache by a cheap signature and
   * return the SAME reference until the inputs actually change.
   */
  private _modeOptionsCache: Array<{ id: TrackingModeId; label: string; description: string; icon: string; locked: boolean; recommended: boolean; caution: string; facts: Array<{ icon: string; text: string }> }> | null = null;
  private _modeOptionsSig = '';
  private _journeyModesCache: Array<{ id: string; title: string; description: string; icon: string; features: string[]; locked: boolean }> | null = null;
  private _journeyModesSig = '';
  /** Coalesce URL syncs so a slow Purpose→Collection nav cannot drop a beat advance. */
  private pendingSetupStepSync: ActivationStepId | null = null;
  /** True when the latest NavigationStart was browser back/forward (allow beat regression). */
  private lastSetupNavWasPopstate = false;
  /**
   * Menu/restart re-walk: furthest step the founder has opened this session.
   * Blocks state$/hydrate from jumping UI to leftover private_test / hire steps.
   */
  private reentryWalkCeiling: ActivationStepId | null = null;
  /**
   * After Continue, keep this UI step even if a synchronous state$ regress
   * (e.g. setTrustFirstPurpose still on plan_viewed) would overwrite it.
   * Cleared after a short window or when advancing further.
   */
  private uiStepHold: ActivationStepId | null = null;
  private uiStepHoldUntil = 0;
  /** Guard: auto-advance (announce→preview, mode-education) must never recurse into state$. */
  private autoAdvancing = false;
  canUseAdminControlledMode = false;

  /** Access step */
  accessTeams: Array<{ name: string; managerLabel: string; leadLabel: string }> = [];

  /** Announce step */
  announcementDraft = '';
  announcementFacts: TrustFirstAnnounceFact[] = [];
  announcementFaq: TrustFirstFaqItem[] = [];
  announcementAckTemplate = '';
  announceEditing = false;
  showAnnounceFaq = false;
  showAnnounceAck = false;

  /** Invite warn */
  showInviteAnnounceWarn = false;
  /** Evidence from the private-test screenshot — shown before dashboard. */
  privateProofEvidence: {
    imgURL: string;
    appName: string;
    userName: string;
    capturedAt: number;
    screenshotId?: string | null;
    uploadAcknowledged?: boolean;
  } | null = null;
  /** True when the evidence image URL fails to render (common on local/dev). */
  proofPreviewFailed = false;

  /** True when the captured screenshot can be shown as pixels on this page. */
  get proofHasPreview(): boolean {
    return !!(this.privateProofEvidence?.imgURL && !this.proofPreviewFailed);
  }

  get workspaceNameDisplay(): string {
    const name = String(this.workspaceNameSaved || '').trim();
    if (!name) {
      return '';
    }
    return name.length > 36 ? `${name.slice(0, 36)}…` : name;
  }

  get workspaceNameFullTitle(): string | null {
    const name = String(this.workspaceNameSaved || '').trim();
    return name || null;
  }

  get contactPhoneDisplay(): string {
    return String(this.contactPhoneSaved || '').trim();
  }

  get contactPhoneFullTitle(): string | null {
    const phone = String(this.contactPhoneSaved || '').trim();
    return phone || null;
  }

  private isWorkspacePromptCompleted(): boolean {
    const user = this.userService.getCurrentUserValue() || this.onboardingService.getCurrentUser() || {};
    const value = user?.meta?.ss_workspace_prompt_completed;
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
  }
  firstDaySignals = {
    trackedMinutes: null as number | null,
    activeMinutes: null as number | null,
    idleMinutes: null as number | null,
    appsCount: null as number | null,
    sitesCount: null as number | null,
    screenshotsCount: null as number | null,
    gapsNote: 'Gaps appear when the agent is offline, paused, or not yet connected.',
  };

  // Progress indicator
  get currentStepNumber(): number {
    if (this.journeyEnabled) {
      return this.journeyChapterHighlightIndex + 1;
    }
    return this.state?.steps.findIndex(s => s.id === this.currentStepId) + 1 || 1;
  }

  /** Chapter to highlight — stay on Install until proof evidence exists. */
  get journeyChapterHighlightId(): string {
    return this.activationJourney.getChapterId(this.currentStepId as ActivationStepId);
  }

  get journeyChapterHighlightIndex(): number {
    const chapters = this.journeyVisibleChapters;
    const idx = chapters.findIndex((c) => c.id === this.journeyChapterHighlightId);
    return idx >= 0 ? idx : this.activationJourney.getVisibleChapterIndex(this.currentStepId as ActivationStepId);
  }

  /** Hide Restart on Ready — new founders should never see “restart” as their first control. */
  get showRestartJourney(): boolean {
    if (!this.journeyEnabled) {
      return false;
    }
    const step = this.journeyState?.currentStep || this.currentStepId;
    if (
      step === 'goal_selected' ||
      step === 'plan_viewed' ||
      step === 'private_test' ||
      step === 'private_proof'
    ) {
      return false;
    }
    return true;
  }

  /** Friendly employee label — prefers canonical name/display_name without duplication. */
  get firstEmployeeDisplayName(): string {
    return resolveEmployeeDisplayName(
      this.createdEmployee || { name: this.employeeForm?.name },
      'Your employee'
    );
  }

  get firstEmployeeWaitingCompanion(): string {
    if (this.isHeroInstallsDelivery) {
      return 'Take SuperSee Setup to their computer, run it, and leave it open.';
    }
    if (this.isEmployeeRunsDelivery) {
      return 'They install from the credentials email, sign in once, and leave SuperSee open.';
    }
    return 'Finish setup on their computer. This page updates when they’re online.';
  }

  get firstEmployeeInitials(): string {
    const parts = this.firstEmployeeDisplayName.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (parts[0][0] + last).toUpperCase();
  }

  /** One reassurance under the status: the timeout guidance when stalled, otherwise that closing the page is safe. */
  get firstEmployeeWaitBody(): string {
    return this.connectionCheckTimedOut
      ? this.firstEmployeeConnectionSupportCopy
      : 'Once SuperSee is running on their computer, it keeps going even if you close this page.';
  }

  get firstEmployeeConnectionSupportCopy(): string {
    if (this.connectionCheckTimedOut) {
      return 'Auto-check paused after two minutes. Use Check again after installation, or Continue later — your place is saved.';
    }
    return 'Watching for connection. You can leave this page anytime.';
  }

  get totalSteps(): number {
    if (this.journeyEnabled) {
      return this.activationJourney.getTotalSteps();
    }
    return this.state?.steps.length || 4;
  }

  get journeyVisibleChapters() {
    return this.activationJourney.getVisibleChapters(this.currentStepId as ActivationStepId);
  }

  get isJourneyB(): boolean {
    return this.activationJourney.isJourneyB(this.currentStepId as ActivationStepId);
  }

  get showConnectWaitReassurance(): boolean {
    return this.showConnectionRecovery && !this.isAgentOnline;
  }

  /** Install wizard UI state derived from existing flags — no fake “installing”. */
  get privateTestUiState(): 'need_package' | 'ready_download' | 'open_file' | 'connected' {
    if (this.isAgentOnline) {
      return 'connected';
    }
    if (!this.createdEmployee) {
      return 'need_package';
    }
    if (!this.isDownloaded) {
      return 'ready_download';
    }
    return 'open_file';
  }

  /** Win 10/11 lightweight path uses the signed MSI, not the generated BAT. */
  get isWindowsRustInstall(): boolean {
    return (
      resolveWindowsAgentRuntime(this.getCreatedEmployeeOS(), sysConfig.rustAgentBetaEnabled) ===
      'rust'
    );
  }

  /** Recovery chips only after ~90s help window. */
  get showInstallHelp(): boolean {
    if (this.isAgentOnline || this.privateTestUiState === 'need_package' || this.privateTestUiState === 'ready_download') {
      return false;
    }
    if (this.showConnectionRecovery || this.installHelpNudged) {
      return true;
    }
    if (!this.connectWaitStartedAt) {
      return false;
    }
    const now = this.connectWaitTick || Date.now();
    return now - this.connectWaitStartedAt >= this.CONNECTION_RECOVERY_MS;
  }

  /** Count-up only for the first ~90s. Hide once help takes over so a stale wait never shows 46:01. */
  get connectWaitElapsedLabel(): string {
    if (!this.connectWaitStartedAt || this.showInstallHelp) {
      return '';
    }
    const now = this.connectWaitTick || Date.now();
    const elapsedSec = Math.max(0, Math.floor((now - this.connectWaitStartedAt) / 1000));
    if (elapsedSec >= Math.floor(this.CONNECTION_RECOVERY_MS / 1000)) {
      return '';
    }
    const minutes = Math.floor(elapsedSec / 60);
    const seconds = elapsedSec % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get installProgressDownloadStarted(): boolean {
    return this.isDownloaded && !this.isAgentOnline;
  }

  get installProgressDownloadDone(): boolean {
    return this.isAgentOnline;
  }

  get installProgressInstallDone(): boolean {
    return this.isAgentOnline;
  }

  get installProgressConnectDone(): boolean {
    return this.isAgentOnline;
  }

  get installDownloadChipLabel(): string {
    return this.installProgressDownloadStarted ? 'Download started' : 'Download';
  }

  get installOpenFileLabel(): string {
    return 'SuperSee Setup';
  }

  get installOpenCompanion(): string {
    return 'Find this file in your browser’s downloads, open it, sign in with the email and password below, then come back here.';
  }

  /** Email shown on Open Setup — session employee or journey private-test email. */
  get privateTestLoginEmail(): string {
    const fromEmployee = String(this.createdEmployee?.email || '').trim();
    if (fromEmployee) {
      return fromEmployee;
    }
    return String(this.journeyState?.privateTestEmail || this.activationJourney.getState().privateTestEmail || '').trim();
  }

  get showReadyVerifiedKicker(): boolean {
    return !!(
      this.journeyState?.emailOtpTracked ||
      this.activationJourney.getState()?.emailOtpTracked ||
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('supersee_email_otp_verified') === '1') ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('supersee_just_verified_otp') === '1')
    );
  }

  get connectionStatusLabel(): string {
    if (this.isAgentOnline) {
      return 'Connected';
    }
    if (this.connectionCheckTimedOut) {
      return 'Not connected yet';
    }
    if (this.isCheckingStatus) {
      return 'Checking connection…';
    }
    if (this.pollingActive) {
      return 'Watching automatically';
    }
    return 'Waiting to connect';
  }

  get firstDayReviewReady(): boolean {
    return this.activationReadiness.evaluate().earlyReviewReady;
  }

  get recommendedModeForGoal(): string {
    return this.activationJourney.getGoalMeta(this.selectedGoal)?.recommendedMode || 'employee_controlled';
  }

  get recommendedModeNote(): string {
    return this.activationJourney.getGoalMeta(this.selectedGoal)?.diagnosticNote || '';
  }

  get goalReflection(): string {
    return this.activationJourney.getGoalReflection(this.selectedGoal);
  }

  /** Journey chrome — story progress, not a SaaS step pill. */
  get journeySteps() {
    return ACTIVATION_STEPS;
  }

  get journeyStepMeta() {
    return ACTIVATION_STEPS.find((s) => s.id === this.currentStepId) || ACTIVATION_STEPS[0];
  }

  /**
   * Companion line for the hero’s current emotional job.
   * Kept short — one breath, not a paragraph. Inner monologue only when triggered.
   */
  get journeyCompanionLine(): string {
    if (!this.journeyEnabled) {
      return '';
    }
    switch (this.currentStepId) {
      case 'goal_selected':
        return this.guideCopy.productSentence;
      case 'plan_viewed':
        return this.guideCopy.purposeBody;
      case 'collection':
        return this.guideCopy.collectionBody;
      case 'setup_confirmed':
        return this.guideCopy.accessBody;
      case 'announce':
        return this.guideCopy.announceBody;
      case 'mode_education':
        return this.guideCopy.focusTime;
      case 'private_test':
        if (this.isAgentOnline) {
          return this.guideCopy.installConnectedBody;
        }
        if (this.privateTestUiState === 'open_file') {
          return this.installOpenCompanion;
        }
        if (this.showConnectWaitReassurance) {
          return this.guideCopy.waitingConnect;
        }
        return this.guideCopy.installConfigured;
      case 'private_proof':
        return this.isScreenshotCaptured || this.privateProofEvidence
          ? this.guideCopy.proofWorking
          : this.guideCopy.safeScreen;
      case 'first_employee':
        return this.guideCopy.invitePilotBody;
      case 'first_day_review':
        return this.guideCopy.reviewLead;
      default:
        return this.journeyStepMeta?.description || '';
    }
  }

  get journeyPhaseLabel(): string {
    if (!this.journeyEnabled) {
      return this.journeyStepMeta?.label || 'Setup';
    }
    const chapters = this.journeyVisibleChapters;
    const idx = this.activationJourney.getVisibleChapterIndex(this.currentStepId as ActivationStepId);
    const chapter = chapters[idx];
    return chapter?.label || this.journeyStepMeta?.label || 'Setup';
  }

  get journeySectionLabel(): string {
    const chapter = this.journeyChapterHighlightId;
    switch (chapter) {
      case 'purpose':
        return this.currentStepId === 'goal_selected' ? 'Workspace' : 'Purpose';
      case 'collection':
        return 'Collection';
      case 'access':
        return 'Access';
      case 'announce':
        return 'Announce';
      case 'invite':
        return 'Preview';
      case 'review':
        return 'Review';
      default:
        return this.journeyPhaseLabel || 'Workspace';
    }
  }

  get dailyInsightsEligible(): boolean {
    return this.activationReadiness.isDailyInsightsEligible();
  }

  get firstSignalReady(): boolean {
    return this.activationReadiness.isFirstSignal();
  }

  // Mode selection (classic checklist titles)
  selectedMode: string = 'employee_controlled';
  private readonly allModes: Array<{
    id: string;
    title: string;
    description: string;
    icon: string;
    features: string[];
  }> = [
    {
      id: 'hidden',
      title: 'Hidden',
      description: 'Runs in the background without a day-to-day widget. Still introduce SuperSee openly — not secret monitoring.',
      icon: 'visibility_off',
      features: ['Background operation', 'No employee widget', 'Open introduction required']
    },
    {
      id: 'employee_controlled',
      title: 'Manual',
      description: 'Employees can see tracking status and start and stop tracking themselves.',
      icon: 'person',
      features: ['Visible to employees', 'Start/stop controls', 'Transparency focused']
    },
    {
      id: 'admin_controlled',
      title: 'Automatic',
      description: 'Employees can see tracking, but it runs automatically and they can’t start or stop it.',
      icon: 'admin_panel_settings',
      features: ['Visible to employees', 'Runs automatically', 'Consistent capture']
    }
  ];

  /** Journey-only mode cards — private-test framing, not company policy. */
  private readonly journeyModeCopy: Record<string, { title: string; description: string; features: string[] }> = {
    employee_controlled: {
      title: 'Manual',
      description: 'They start and stop tracking on their computer.',
      features: ['Employee can see SuperSee', 'Start/stop on their side', 'Best for early trust'],
    },
    admin_controlled: {
      title: 'Automatic',
      description: 'Tracking runs automatically. They cannot start or stop it.',
      features: ['Employee can see SuperSee', 'Runs automatically', 'Good for consistent capture'],
    },
    hidden: {
      title: 'Hidden · background only',
      description: 'Runs in the background without a day-to-day widget. Same work visibility — different employee experience. Still introduce SuperSee openly; not secret monitoring.',
      features: ['Background operation', 'No employee widget', 'Open introduction still required'],
    },
  };

  modes: Array<{
    id: string;
    title: string;
    description: string;
    icon: string;
    features: string[];
    locked: boolean;
  }> = this.allModes.map((mode) => ({ ...mode, locked: false }));

  /** Modes list for activation journey choose screen. */
  get journeyModes() {
    // Stable reference unless lock state changes (see _modeOptionsCache note).
    const sig = this.modes.map((m) => `${m.id}:${m.locked ? 1 : 0}`).join('|');
    if (this._journeyModesCache && this._journeyModesSig === sig) {
      return this._journeyModesCache;
    }
    const list = this.modes.map((mode) => {
      const copy = this.journeyModeCopy[mode.id];
      return copy
        ? { ...mode, title: copy.title, description: copy.description, features: copy.features }
        : mode;
    });
    this._journeyModesSig = sig;
    this._journeyModesCache = list;
    return list;
  }

  // Employee form
  employeeForm = {
    name: '',
    email: '',
    department: '',
    password: '',
    os: ''
  };
  /** Password control for validation (same rules as create employee / reset password). Empty is valid (auto-generate). */
  passwordControl = new FormControl('', [passwordValidator()]);
  showPassword: boolean = false;
  useCustomPassword: boolean = false;
  departments = ['Engineering', 'Design', 'Marketing', 'Sales', 'Human Resources', 'Finance', 'Operations'];
  osOptions = [
    { id: 'windows', label: 'Windows 10 & 11', icon: 'desktop_windows' },
    { id: 'windows_legacy', label: 'Windows 7 & 8', icon: 'desktop_windows' },
    { id: 'macos', label: 'macOS', icon: 'laptop_mac' },
    { id: 'linux', label: 'Linux', icon: 'computer' }
  ];

  // Created employee data (for install_agent step)
  createdEmployee: any = null;
  createdEmployeePassword: string = '';
  /** Prevents a recreate loop when the stored private-test user was deleted. */
  private privateTestIdentityRecreateInFlight = false;

  // Agent installation
  isDownloading = false;
  isDownloaded = false;
  /** True only after the Install “Download SuperSee” click — never from Ready. */
  privateTestDownloadRequestedByUser = false;
  /** Blocks the Install download CTA briefly so Ready’s click cannot land on it. */
  installDownloadCtaLocked = false;
  isCheckingStatus = false;
  isAgentOnline = false;
  private pollingSubscription: any;
  pollingActive = false;
  private pollingStartTime: number = 0;
  private readonly POLLING_TIMEOUT_MS = 120000; // 2 minutes
  copiedField: string = '';

  // Screenshot
  isRequestingScreenshot = false;
  isResolvingScreenshotTarget = false;
  isScreenshotCaptured = false;
  screenshotFailed = false;
  private screenshotCompleted = false;
  private screenshotSubscription: any;
  screenshotRequestCount = 0;


  private subscription: Subscription;
  private entitlementsSub?: Subscription;
  private journeySub?: Subscription;
  private routeSub?: Subscription;
  private querySub?: Subscription;
  private navEndSub?: Subscription;
  /** Prevents URL↔step sync loops while navigating. */
  private syncingSetupUrl = false;
  /** Last setup path sent to GA/Clarity so repeat state updates do not double-count. */
  private lastSetupAnalyticsKey = '';

  constructor(
    private onboardingService: OnboardingChecklistService,
    private activationJourney: ActivationJourneyService,
    private router: Router,
    private route: ActivatedRoute,
    private title: Title,
    private _snackBar: MatSnackBar,
    private _projectService: ProjectService,
    private _loginService: LoginService,
    private _sharedService: SharedService,
    private db: AngularFireDatabase,
    private tokenEncryptionService: TokenEncryptionService,
    private _authService: AuthService,
    private injector: EnvironmentInjector,
    private entitlementsService: EntitlementsService,
    private exampleWorkday: ExampleWorkdayService,
    private activationDestination: ActivationDestinationService,
    private activationNavigation: ActivationNavigationService,
    private activationReadiness: ActivationReadinessService,
    private activationEvidence: ActivationEvidenceService,
    private employeeHandoff: ActivationEmployeeHandoffService,
    private modalService: ModalService,
    private deviceDetection: DeviceDetectionService,
    private userService: UserService,
    private dialog: MatDialog,
    private contactPhoneCapture: ContactPhoneCaptureService,
    private shiftService: ShiftService,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) { }

  /**
   * Debug only: add `?debugJourney=1` (or localStorage supersee_debug_journey=1).
   * Filter console for `[JourneyFreeze]` if Continue freezes again.
   */
  // #region agent log
  private agentDbg(_hypothesisId: string, _location: string, _message: string, _data?: Record<string, unknown>): void {
    // No-op. (Was posting to a local debug ingest server on every journey action; the
    // failed fetches churned zone.js and polluted the console. Kept as a no-op so the
    // existing call sites compile; remove the calls in a later cleanup pass.)
  }
  // #endregion

  private journeyFreezeLog(event: string, data?: Record<string, unknown>): void {
    let enabled = false;
    try {
      enabled =
        this.route.snapshot.queryParamMap.get('debugJourney') === '1' ||
        localStorage.getItem('supersee_debug_journey') === '1';
    } catch {
      enabled = false;
    }
    if (!enabled) {
      return;
    }
    const state = this.activationJourney.getState();
    console.warn('[JourneyFreeze]', event, {
      ...(data || {}),
      uiStep: this.currentStepId,
      stateStep: state.currentStep,
      beat: this.collectionBeat,
      url: this.router.url,
      loc: this.currentSetupLocationUrl(),
      reentry: this.setupReentryFrom(),
      ceiling: this.reentryWalkCeiling,
      syncing: this.syncingSetupUrl,
      navSyncing: this.activationNavigation.isSyncingSetupUrl,
      pending: this.pendingSetupStepSync,
      popstate: this.lastSetupNavWasPopstate,
      journeyStatus: state.journeyStatus,
      finished: state.finished,
    });
  }

  /** Prefer the real address bar — Location.replaceState does not update Router.url. */
  private currentSetupLocationUrl(): string {
    try {
      if (typeof window !== 'undefined' && window.location) {
        return `${window.location.pathname}${window.location.search || ''}`.split(';')[0];
      }
    } catch {
      /* ignore */
    }
    return (this.router.url || '').split(';')[0];
  }

  private raiseReentryWalkCeiling(stepId: ActivationStepId): void {
    if (!this.setupReentryFrom()) {
      return;
    }
    const order = ACTIVATION_STEPS.map((s) => s.id);
    const nextIdx = order.indexOf(stepId);
    const curIdx = this.reentryWalkCeiling ? order.indexOf(this.reentryWalkCeiling) : -1;
    if (nextIdx >= 0 && nextIdx >= curIdx) {
      this.reentryWalkCeiling = stepId;
      this.journeyFreezeLog('reentry_ceiling_raised', { stepId });
    }
  }

  /** Pin the visible step through the next state$/route ticks (Purpose→Collection freeze). */
  private holdUiStep(stepId: ActivationStepId, ms = 3000): void {
    this.uiStepHold = stepId;
    this.uiStepHoldUntil = Date.now() + ms;
    this.currentStepId = stepId;
  }

  private uiStepHoldActive(): boolean {
    if (!this.uiStepHold) {
      return false;
    }
    if (Date.now() > this.uiStepHoldUntil) {
      this.uiStepHold = null;
      return false;
    }
    return true;
  }

  /**
   * Pin UI step for this click turn. Do NOT call detectChanges() here — a sync
   * detectChanges inside Purpose/Collection Continue was locking the Chrome tab
   * (state$ + template getters + Clarity/GTM on the same stack).
   */
  private paintJourneyStep(stepId: ActivationStepId): void {
    this.holdUiStep(stepId);
    if (stepId === 'collection') {
      this.hydrateCollectionFromState();
    }
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.journeyEnabled = this.activationJourney.isEnabled;
    this.entitlementsService.syncFromSession();
    this.refreshAvailableModes();
    this.entitlementsSub = this.entitlementsService.stateChanged$.subscribe(() => {
      this.refreshAvailableModes();
    });
    this.checkingDBPath();
    this._sharedService.isOpenCloseSidebar.next(false);
    this.loadUserInfo();
    this.loadTrackingMode();
    this.restoreCreatedEmployee();

    if (this.journeyEnabled) {
      // Seed immediately so remounts (e.g. leaving Example workday) do not flash a blank page
      // while hydrateFromServer is in flight. Default currentStepId is classic-only.
      this.pinReadyOnSetupReentry();
      const seeded = this.activationJourney.getState();
      this.journeyState = seeded;
      this.currentStepId = seeded.currentStep;
      this.maybeTrackWorkspacePromptShown();
      this.restorePrivateProofEvidence();
      this.initActivationJourney();
      return;
    }

    // Classic checklist path
    this.onboardingService.detectCompletedSteps().subscribe({
      next: () => {
        console.log('[Onboarding] Detection complete, syncing with backend');
        this.getNextStepFromBackend();
      },
      error: (err) => console.error('[Onboarding] Detection error:', err)
    });

    this.subscription = this.onboardingService.state$.subscribe(state => {
      this.state = state;
      if (state.steps[state.currentStepIndex]) {
        console.log('[Onboarding] state$ emitted -> currentStepId:', state.steps[state.currentStepIndex].id, 'index:', state.currentStepIndex);
        this.currentStepId = state.steps[state.currentStepIndex].id;

        // Auto-start polling when entering install_agent step
        if (this.currentStepId === 'install_agent' && this.createdEmployee && !this.isAgentOnline) {
          this.startPollingAgentStatus();
        }

        // Reset screenshot state only when first entering request_screenshot step (not on every state emission)
        if (this.currentStepId === 'request_screenshot' && !this.isScreenshotCaptured && !this.isRequestingScreenshot) {
          // Only reset if we haven't started or completed a screenshot request
        }
      }
    });
  }

  private initActivationJourney(): void {
    this.activationJourney.hydrateFromServer().subscribe({
      next: () => this.bindActivationJourneyUi(),
      error: () => this.bindActivationJourneyUi(),
    });
  }

  private setupReentryFrom(): 'menu' | 'restart' | null {
    const fromQuery = this.route.snapshot.queryParamMap.get('from');
    // Prefer the real address bar — replaceState leaves Router.url on /setup/ready.
    const locSearch = (this.currentSetupLocationUrl().split('?')[1] || '');
    const fromLoc = new URLSearchParams(locSearch).get('from');
    const fromRouter = new URLSearchParams((this.router.url || '').split('?')[1] || '').get('from');
    const from = fromQuery || fromLoc || fromRouter;
    if (from === 'menu' || from === 'restart') {
      return from;
    }
    // Survive query-param loss mid-funnel (beat navigation / history sync).
    return this.activationJourney.getIntentionalReentry();
  }

  /** Profile Journey / Restart must stay on Ready — do not snap to a later stored step. */
  private pinReadyOnSetupReentry(): void {
    if (!this.setupReentryFrom()) {
      return;
    }
    const slug = this.route.snapshot.paramMap.get('step');
    const path = this.currentSetupLocationUrl().split('?')[0];
    if (slug !== 'ready' && path !== '/setup/ready' && path !== '/setup') {
      return;
    }
    // Clear stale in-memory UI from a prior walk on this same component instance.
    // isLoading left true after Install disables Capture Screenshots Continue.
    this.collectionBeat = 0;
    this.collectionSaveStatus = '';
    this.scheduleSaveStatus = '';
    this.isLoading = false;
    this.isDownloading = false;
    this.privateTestIdentityRecreateInFlight = false;
    this.privateTestDownloadRequestedByUser = false;
    this.installDownloadCtaLocked = false;
    this.uiStepHold = null;
    this.uiStepHoldUntil = 0;
    this.reentryWalkCeiling = 'goal_selected';
    this.currentStepId = 'goal_selected';
    if (this.activationJourney.getState().currentStep !== 'goal_selected') {
      this.activationJourney.goToStep('goal_selected');
    }
  }

  private bindActivationJourneyUi(): void {
    this.activationJourney.maybeTrackEmailOtpVerified();
    this.restorePrivateProofEvidence();
    this.pinReadyOnSetupReentry();
    // Do not clear continuedLater on remount — only Resume / intentional journey actions clear it.
    this.journeySub?.unsubscribe();
    this.journeySub = this.activationJourney.state$.subscribe((js) => {
      this.journeyState = js;
      const prevUi = this.currentStepId;
      const reentry = !!this.setupReentryFrom();
      // The screen follows the step the founder just set. Step-list order is not
      // walk order (Install is stored before Preview), so do not drop “backward” steps.
      this.currentStepId = js.currentStep;
      if (prevUi !== js.currentStep) {
        this.journeyFreezeLog('state_step_changed', { from: prevUi, to: js.currentStep });
        // The previous button's request is done. Leftover isLoading from Install
        // greys out the next Continue.
        this.isLoading = false;
        this.isDownloading = false;
        if (js.currentStep !== 'collection' && this.collectionSaveStatus === 'saving') {
          this.collectionSaveStatus = '';
        }
      }
      this.maybeTrackWorkspacePromptShown();
      this.selectedGoal = js.goal;
      if (
        js.goal === 'understand_team_work' ||
        js.goal === 'catch_issues_early' ||
        js.goal === 'prove_compliance' ||
        js.goal === 'reduce_idle_waste'
      ) {
        this.selectedPurposeId = js.goal;
      }
      if (js.purposeOtherText) {
        this.purposeOtherText = js.purposeOtherText;
      }
      if (js.currentStep === 'collection') {
        this.hydrateCollectionFromState();
        if (this.collectionSaveStatus !== 'saving') {
          this.isLoading = false;
        }
      }
      if (js.currentStep === 'setup_confirmed') {
        this.loadAccessTeams();
      }
      // Phone/tablet cannot run the desktop agent — leave install nest for Invite.
      if (this.isPhoneOrTabletReady && js.currentStep === 'private_test') {
        this.activationJourney.divertMobileFromPrivateTestInstall();
        return;
      }
      // A menu/restart walk is driven by clicks. Do not bounce Invite back to Preview.
      // Defer the auto-advance out of this state$ tick so it can never recurse into the
      // subscriber on the same stack (that synchronous recursion froze the tab).
      if (js.currentStep === 'announce' && !js.wantsEmployeeInvite && !reentry) {
        if (!this.autoAdvancing) {
          this.autoAdvancing = true;
          queueMicrotask(() => {
            this.autoAdvancing = false;
            if (
              this.activationJourney.getState().currentStep === 'announce' &&
              !this.activationJourney.getState().wantsEmployeeInvite
            ) {
              this.activationJourney.goToStep('first_employee');
            }
          });
        }
        return;
      }
      if (js.currentStep === 'announce') {
        this.refreshAnnouncementDraft();
      }
      this.installRecoveryBranch = js.installRecoveryBranch;
      this.actorRoute = js.actorRoute;
      this.setupMethod = this.activationJourney.setupMethodFromActorRoute(js.actorRoute);
      if (js.mode) {
        this.selectedMode = js.mode;
      }
      if (js.firstEmployeeMode === 'hidden') {
        this.firstEmployeeSilent = true;
      } else if (js.firstEmployeeMode) {
        this.firstEmployeeSilent = false;
        if (js.firstEmployeeMode === 'employee_controlled' || js.firstEmployeeMode === 'admin_controlled') {
          this.selectedMode = js.firstEmployeeMode;
        }
      }

      if (js.privateTestEmployeeId && !this.createdEmployee?.id) {
        this.hydratePrivateTestEmployee(js);
      }

      if (js.currentStep === 'private_test' && js.privateTestEmployeeId) {
        this.ensurePrivateTestConnectionTarget(js);
      }
      if (js.currentStep === 'private_proof' && js.privateTestEmployeeId) {
        this.ensurePrivateTestConnectionTarget(js);
        // Tray already chosen in Collection — do not dump a teach panel on the win screen.
        if (this.isScreenshotCaptured || this.privateProofEvidence) {
          this.activationJourney.markVisibilitySwitchTaught();
        }
      }

      // Prefer real first hire over stale private-test blob after handoff create.
      if (
        js.firstEmployeeId &&
        js.currentStep === 'first_employee' &&
        (!this.createdEmployee?.id ||
          this.createdEmployee.id === js.privateTestEmployeeId ||
          this.createdEmployee.id !== js.firstEmployeeId)
      ) {
        this.hydrateFirstEmployee(js.firstEmployeeId);
      }

      // Mid-resume: skip mode question for the private-test path only.
      // Do not auto-skip when Adjust mode explicitly opened the chooser.
      if (
        !reentry &&
        js.currentStep === 'mode_education' &&
        !js.privateEvaluationActive &&
        this.modeEducationPage !== 'choose' &&
        !this.adjustingModeFromReview
      ) {
        // Deferred + guarded: never complete mode-education synchronously inside state$.
        if (!this.autoAdvancing) {
          this.autoAdvancing = true;
          queueMicrotask(() => {
            this.autoAdvancing = false;
            if (
              this.activationJourney.getState().currentStep === 'mode_education' &&
              this.modeEducationPage !== 'choose' &&
              !this.adjustingModeFromReview
            ) {
              this.activationJourney.completeModeEducation();
            }
          });
        }
        return;
      }

      if (js.currentStep === 'private_test' && this.createdEmployee && !this.isAgentOnline) {
        this.hydratePrivateTestConnectWait();
        if (this.shouldWatchPrivateTestConnection()) {
          this.ensureConnectionWatchRunning();
        }
      }
      if (js.currentStep === 'first_employee') {
        // Done-for-now / collecting pause already proved connection — restore Connected
        // immediately; in-memory isAgentOnline resets when leaving /setup.
        this.restoreFirstEmployeeConnectedFromJourney(js);
        if (!this.isAgentOnline && (this.hasRealFirstEmployee || js.firstEmployeeId)) {
          this.connectWaitStartedAt = this.connectWaitStartedAt || Date.now();
          this.syncConnectWaitClock();
          this.ensureConnectionWatchRunning();
        }
        // Do not auto-open Add employee — Invite is a deliberate click on See it / Invite.
        // Auto-open + inviteFirstFromReady trapped Cancel/refresh in a reopen loop.
      }
      if (
        (js.currentStep === 'first_employee' || js.currentStep === 'first_day_review') &&
        this.hasRealFirstEmployee &&
        this.isAgentOnline
      ) {
        if (js.currentStep === 'first_day_review') {
          // Track once; never call from a path that always patches state (avoids state$ loops).
          this.activationJourney.markFirstDayReviewViewed();
        }
        this.syncFirstDayEvidenceRefreshTimer();
      } else {
        this.stopFirstDayEvidenceRefresh();
      }

      // Same-step updates (collection beats) keep the address bar the click just set.
      if (reentry && prevUi === js.currentStep) {
        return;
      }
      this.syncSetupStepUrl(js.currentStep);
    });

    this.routeSub?.unsubscribe();
    this.routeSub = this.route.paramMap.subscribe((params) => {
      this.applySetupStepFromRoute(params.get('step'));
    });

    this.querySub?.unsubscribe();
    this.querySub = this.route.queryParamMap.subscribe(() => {
      this.applySetupAnswerFromRoute();
    });

    this.navEndSub?.unsubscribe();
    this.navEndSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.lastSetupNavWasPopstate = e.navigationTrigger === 'popstate';
        return;
      }
      if (!(e instanceof NavigationEnd)) {
        return;
      }
      if (!this.journeyEnabled || this.syncingSetupUrl || this.activationNavigation.isSyncingSetupUrl) {
        return;
      }
      const url = e.urlAfterRedirects || e.url || '';
      if (!url.startsWith('/setup') || url.startsWith('/setup/example-workday')) {
        return;
      }
      const allowReentry = !!this.setupReentryFrom();
      // State is the source of truth and we only ever replaceState the bar. The only
      // NavigationEnd that may drive state is a genuine browser Back/Forward (popstate).
      // Everything else is our own mirror or a stale Router race — never reconcile it.
      if (!this.lastSetupNavWasPopstate) {
        this.journeyFreezeLog('nav_end_reconcile_skipped', { url });
        return;
      }
      this.journeyFreezeLog('nav_end_reconcile', {
        url,
        allowReentry,
        triggerPopstate: this.lastSetupNavWasPopstate,
      });
      this.activationNavigation.reconcileSetupUrl(url, { allowReentry });
    });

    // Seed a local classic state shell so shared helpers (footer, etc.) still work.
    this.state = this.onboardingService.getState();
  }

  /**
   * Keep `/setup/:step` and the answer query in the address bar.
   * pushState (not replace) so GA4 history page views and Clarity record each stage.
   */
  private syncSetupStepUrl(stepId: ActivationStepId): void {
    if (!this.journeyEnabled) {
      return;
    }
    // ONE source of truth: journey state drives the address bar — never the reverse.
    // We only ever replaceState (mirror) + publish analytics. We never navigate via the
    // Router for a step, because a Router nav fires NavigationEnd → reconcileSetupUrl →
    // goToStep → state$ → syncSetupStepUrl again: the re-entrant loop that froze the tab.
    const reentry = this.setupReentryFrom();
    const queryParams = this.setupAnalyticsQuery(stepId);
    // Completed-journey re-walk: Ready stays Ready; never mirror a stored later step.
    const pathOnly = this.currentSetupLocationUrl().split('?')[0];
    if (
      reentry &&
      (pathOnly === '/setup/ready' || pathOnly === '/setup') &&
      (stepId === 'goal_selected' || activationStepToSlug(stepId) === 'ready')
    ) {
      this.publishSetupPage('goal_selected', { from: reentry });
      return;
    }
    const path = this.setupPagePath(activationStepToSlug(stepId), queryParams);
    if (this.currentSetupLocationUrl() !== path) {
      this.location.replaceState(path);
    }
    this.pendingSetupStepSync = null;
    this.publishSetupPage(stepId, queryParams);
  }

  /** Answer tokens that should show up as separate pages in GA and Clarity. */
  private setupAnalyticsQuery(stepId: ActivationStepId): Params {
    const state = this.activationJourney.getState();
    const params: Params = {};
    const reentry = this.setupReentryFrom();
    if (reentry) {
      params['from'] = reentry;
    }
    if (stepId === 'plan_viewed' && state.goal) {
      params['choice'] = state.goal;
    }
    if (stepId === 'collection') {
      params['beat'] = this.collectionBeatSlugs[this.collectionBeat] || this.collectionBeatSlugs[0];
    }
    if (stepId === 'private_proof') {
      params['view'] = this.isScreenshotCaptured || this.privateProofEvidence ? 'done' : 'capture';
    }
    if (stepId === 'first_employee' && !this.hasRealFirstEmployee) {
      params['choice'] = state.wantsEmployeeInvite ? 'invite' : 'see-it';
    }
    return params;
  }

  private setupUrlsMatch(stepId: ActivationStepId, query: Params): boolean {
    // Must use the real bar — replaceState leaves Router.url stale on re-entry walks.
    const currentUrl = this.currentSetupLocationUrl();
    const targetPath = `/setup/${activationStepToSlug(stepId)}`;
    const qs = new URLSearchParams();
    (['beat', 'choice', 'from', 'view'] as const).forEach((key) => {
      if (query[key] != null && String(query[key]) !== '') {
        qs.set(key, String(query[key]));
      }
    });
    const search = qs.toString();
    const targetUrl = search ? `${targetPath}?${search}` : targetPath;
    return currentUrl === targetUrl || currentUrl === `${targetUrl}/`;
  }

  private publishSetupPage(stepId: ActivationStepId, query: Params): void {
    const slug = activationStepToSlug(stepId);
    const label = this.setupPageLabel(slug, stepId);
    this.title.setTitle(`${label} · Setup - Supersee`);
    const pagePath = this.setupPagePath(slug, query);
    if (this.lastSetupAnalyticsKey === pagePath) {
      return;
    }
    this.lastSetupAnalyticsKey = pagePath;
    trackProductEvent('setup_page_view', {
      page_path: pagePath,
      page_title: label,
      step: slug,
      beat: query['beat'] != null ? String(query['beat']) : '',
      choice: query['choice'] != null ? String(query['choice']) : '',
      view: query['view'] != null ? String(query['view']) : '',
    });
  }

  private setupPagePath(slug: string, query: Params): string {
    const qs = new URLSearchParams();
    (['beat', 'choice', 'from', 'view'] as const).forEach((key) => {
      if (query[key] != null && String(query[key]) !== '') {
        qs.set(key, String(query[key]));
      }
    });
    const search = qs.toString();
    return search ? `/setup/${slug}?${search}` : `/setup/${slug}`;
  }

  private setupPageLabel(slug: string, stepId: ActivationStepId): string {
    const label = ACTIVATION_SLUG_TITLES[slug] || 'Setup';
    if (stepId === 'collection') {
      const beatLabel = ['Screenshots', 'Mode', 'Confirm'][this.collectionBeat];
      return beatLabel ? `${label} · ${beatLabel}` : label;
    }
    if (stepId === 'first_employee' && !this.hasRealFirstEmployee) {
      return this.activationJourney.getState().wantsEmployeeInvite ? `${label} · Invite` : `${label} · See it`;
    }
    return label;
  }

  /** Browser back/forward restores the collection beat and purpose shown in the URL. */
  private applySetupAnswerFromRoute(): void {
    if (!this.journeyEnabled) {
      return;
    }
    // State is the source of truth. The URL only drives the beat on a genuine browser
    // Back/Forward — never on our own replaceState (ActivatedRoute stays stale after it,
    // which is what used to snap the beat back to "screenshots").
    if (!this.lastSetupNavWasPopstate) {
      return;
    }
    const step = this.activationJourney.getState().currentStep;
    if (step !== 'collection') {
      return;
    }
    const beat = this.route.snapshot.queryParamMap.get('beat');
    const idx = this.collectionBeatSlugs.includes(beat || '')
      ? this.collectionBeatSlugs.indexOf(beat || '')
      : (this.legacyCollectionBeatSlugs[beat || ''] ?? -1);
    if (idx >= 0 && idx !== this.collectionBeat) {
      this.collectionBeat = idx;
      this.publishSetupPage(step, this.setupAnalyticsQuery(step));
    }
  }

  private applySetupStepFromRoute(slug: string | null): void {
    if (!this.journeyEnabled) {
      return;
    }
    // Menu/Restart actually navigated to /setup/ready — reset to Goal once, so a
    // completed account re-enters clean. (ActivatedRoute can report "ready" after a
    // replaceState further into the walk, so only pin when the real bar is Ready.)
    if (this.setupReentryFrom() && (slug === 'ready' || !slug)) {
      const path = this.currentSetupLocationUrl().split('?')[0];
      if (path === '/setup/ready' || path === '/setup') {
        this.pinReadyOnSetupReentry();
        this.setSetupPageTitle('ready');
      }
      return;
    }
    // State is the single source of truth and our own step changes rewrite the URL
    // one-way (replaceState). The route listener therefore does nothing on our own
    // navigations — only a genuine browser Back/Forward (popstate) may drive state.
    if (!this.lastSetupNavWasPopstate) {
      if (slug) {
        this.setSetupPageTitle(slug);
      }
      return;
    }
    if (!slug) {
      return;
    }
    const stepId = activationSlugToStep(slug);
    if (!stepId) {
      return;
    }
    const current = this.activationJourney.getState().currentStep;
    if (stepId === current || activationStepToSlug(stepId) === activationStepToSlug(current)) {
      this.setSetupPageTitle(slug);
      return;
    }
    // Browser Back/Forward only: move to a step the user already reached; otherwise
    // snap the bar back to state without mutating the journey.
    if (this.canOpenSetupStep(stepId)) {
      this.journeyFreezeLog('step_route_goto', { slug, stepId, current });
      this.activationJourney.goToStep(stepId);
    } else {
      this.journeyFreezeLog('step_route_snap_url', { slug, stepId, current });
      this.syncSetupStepUrl(current);
    }
  }

  private canOpenSetupStep(stepId: ActivationStepId): boolean {
    const state = this.activationJourney.getState();
    if (state.completedSteps.includes(stepId) || state.currentStep === stepId) {
      return true;
    }
    const order = ACTIVATION_STEPS.map((s) => s.id);
    const targetIdx = order.indexOf(stepId);
    const currentIdx = order.indexOf(state.currentStep);
    // Allow revisiting earlier steps; block skipping ahead.
    return targetIdx >= 0 && targetIdx <= currentIdx;
  }

  private setSetupPageTitle(slug: string): void {
    const label = ACTIVATION_SLUG_TITLES[slug] || 'Setup';
    this.title.setTitle(`${label} · Setup - Supersee`);
  }

  private hydratePrivateTestEmployee(js: ActivationJourneyState): void {
    const saved = this.onboardingService?.getCreatedEmployee?.();
    const savedMatches =
      saved?.employee?.id && Number(saved.employee.id) === Number(js.privateTestEmployeeId);
    const email = String(
      js.privateTestEmail || (savedMatches ? saved?.employee?.email : '') || ''
    ).trim();
    this.createdEmployee = {
      ...(savedMatches && saved?.employee ? saved.employee : {}),
      id: js.privateTestEmployeeId,
      email: email || undefined,
      name: email || saved?.employee?.name || 'This computer',
      first_name: saved?.employee?.first_name || 'This',
      last_name: saved?.employee?.last_name || 'computer',
      operating_system:
        saved?.employee?.operating_system || this.deviceDetection.detectDesktopAgentOs(),
      version_type: this.getModeToVersionType(js.mode || this.selectedMode),
    };
    this.createdEmployeePassword =
      js.privateTestPassword || (savedMatches ? saved.password : '') || '';
    this.privateTestReady = true;
    // Repair missing email from WP when journey only stored the id.
    if (!this.createdEmployee.email && js.privateTestEmployeeId) {
      this._loginService.getUser(js.privateTestEmployeeId).subscribe({
        next: (user: any) => {
          if (!user?.id || Number(user.id) !== Number(js.privateTestEmployeeId)) {
            return;
          }
          const repaired = String(user.email || user.user_email || '').trim();
          if (!repaired) {
            return;
          }
          this.createdEmployee = { ...this.createdEmployee, email: repaired };
          this.activationJourney.savePrivateTestIdentity({
            employeeId: Number(js.privateTestEmployeeId),
            email: repaired,
            password: null,
          });
        },
        error: () => undefined,
      });
    }
  }

  /**
   * Private-test must watch privateTestEmployeeId even if session still holds a later first-hire blob.
   */
  private ensurePrivateTestConnectionTarget(js: ActivationJourneyState): void {
    const privateId = js.privateTestEmployeeId;
    if (!privateId) {
      return;
    }
    if (Number(this.createdEmployee?.id) !== Number(privateId)) {
      this.hydratePrivateTestEmployee(js);
    }
  }

  /** Employee id Firebase should watch for the current setup step. */
  private resolveConnectionWatchEmployeeId(): number | null {
    const js = this.journeyState || this.activationJourney.getState();
    if (this.currentStepId === 'private_test' || this.currentStepId === 'private_proof') {
      const id = js?.privateTestEmployeeId || this.createdEmployee?.id;
      return id ? Number(id) : null;
    }
    if (this.currentStepId === 'first_employee') {
      const id = js?.firstEmployeeId || this.createdEmployee?.id;
      return id ? Number(id) : null;
    }
    return this.createdEmployee?.id ? Number(this.createdEmployee.id) : null;
  }

  /** Resume Download → online wait after refresh; do not start a wait from merely opening install. */
  private hydratePrivateTestConnectWait(): void {
    const started = this.activationJourney.getState()?.connectWaitStartedAt;
    if (!started) {
      return;
    }
    this.connectWaitStartedAt = started;
    this.isDownloaded = true;
    this.ensurePrivateTestPasswordForDisplay();
    this.openPrivateTestLoginForDownload();
    this.syncConnectWaitClock();
  }

  private shouldWatchPrivateTestConnection(): boolean {
    if (this.isAgentOnline || !this.createdEmployee) {
      return false;
    }
    return this.isDownloaded || !!this.activationJourney.getState()?.connectWaitStartedAt;
  }

  private beginPrivateTestConnectWait(): void {
    this.ensurePrivateTestPasswordForDisplay();
    this.openPrivateTestLoginForDownload();
    this.connectWaitSawOffline = false;
    this.connectWaitStartedAt = this.activationJourney.beginConnectWait({
      employeeId: this.createdEmployee?.id ?? this.activationJourney.getState()?.privateTestEmployeeId,
      os: this.getPrivateTestOs(),
      reset: true,
    });
    this.syncConnectWaitClock();
    this.ensureConnectionWatchRunning();
  }

  /** Restore in-memory password from session save so Open Setup can show copyable login. */
  private ensurePrivateTestPasswordForDisplay(): void {
    if (this.createdEmployeePassword) {
      return;
    }
    const employeeId =
      this.createdEmployee?.id || this.activationJourney.getState().privateTestEmployeeId;
    if (!employeeId) {
      return;
    }
    const saved = this.onboardingService?.getCreatedEmployee?.();
    if (
      saved?.password &&
      saved?.employee?.id &&
      Number(saved.employee.id) === Number(employeeId)
    ) {
      this.createdEmployeePassword = saved.password;
      if (!this.createdEmployee?.email && saved.employee?.email) {
        this.createdEmployee = {
          ...(this.createdEmployee || { id: employeeId }),
          id: employeeId,
          email: saved.employee.email,
        };
      }
      return;
    }
    // No session password — mint one so the founder can still sign in on Open Setup.
    const email = this.privateTestLoginEmail;
    if (!email || this.isLoading) {
      return;
    }
    const password = this._projectService.generatePassword();
    this._projectService
      .resetUserPassword({ id: employeeId, email, password } as any)
      .subscribe({
        next: () => {
          this.createdEmployeePassword = password;
          if (this.createdEmployee) {
            this.onboardingService.saveCreatedEmployee(this.createdEmployee, password);
          }
        },
        error: () => undefined,
      });
  }

  /**
   * Start or retarget the Firebase watch. Resume must not keep listening to a prior employee.
   */
  private ensureConnectionWatchRunning(): void {
    if (this.isAgentOnline) {
      return;
    }
    const watchEmployeeId = this.resolveConnectionWatchEmployeeId();
    if (!watchEmployeeId) {
      return;
    }
    if (this.pollingActive && this.connectionWatchEmployeeId === watchEmployeeId) {
      return;
    }
    if (this.pollingActive) {
      this.stopPolling({ preserveRecovery: true });
    }
    this.startPollingAgentStatus();
  }

  /**
   * After “Done for now”, journey keeps first_employee + waitingReason employee_data.
   * Connection itself is not re-proven in memory — restore Connected from that pause
   * (and from auth_heartbeat) so resume does not flash Watching for an already-live agent.
   */
  private restoreFirstEmployeeConnectedFromJourney(js: ActivationJourneyState): void {
    if (this.isAgentOnline) {
      return;
    }
    const completed = js.completedSteps || [];
    const collectingPause =
      js.waitingReason === 'employee_data' && completed.includes('first_employee');
    const heartbeatPhase =
      js.activationStatusPhase === 'auth_heartbeat' ||
      js.activationStatusPhase === 'signaling';
    if (!collectingPause && !heartbeatPhase) {
      return;
    }
    if (!js.firstEmployeeId && !this.hasRealFirstEmployee) {
      return;
    }

    this.isAgentOnline = true;
    this.isCheckingStatus = false;
    this.connectionCheckTimedOut = false;
    this.showConnectionRecovery = false;
    this.activationReadiness.setEvidence({
      packagePrepared: true,
      privateTestEmployeeId: js.privateTestEmployeeId,
      firstEmployeeId: js.firstEmployeeId ? Number(js.firstEmployeeId) : undefined,
      agentAuthenticated: true,
      recentHeartbeat: true,
      onlineNow: true,
    });
  }

  /** Load the real first hire when journey has firstEmployeeId but UI still shows private-test or nothing. */
  private hydrateFirstEmployee(firstEmployeeId: number): void {
    if (!firstEmployeeId) {
      return;
    }
    if (this.createdEmployee?.id === firstEmployeeId) {
      return;
    }
    this._loginService.getUser(firstEmployeeId).subscribe({
      next: (response: any) => {
        if (!response?.id) {
          return;
        }
        this.createdEmployee = response;
        this.createdEmployeePassword = '';
        this.onboardingService.saveCreatedEmployee(response, '');
        this.setupMethod =
          this.activationJourney.setupMethodFromActorRoute(this.journeyState?.actorRoute || null) ||
          this.setupMethod;
        if (this.journeyState?.firstEmployeeMode === 'hidden') {
          this.firstEmployeeSilent = true;
        }
        if (!this.isAgentOnline && this.currentStepId === 'first_employee') {
          this.ensureConnectionWatchRunning();
        }
      },
      error: () => {
        /* leave UI in recovery; founder can reopen Add employee */
      },
    });
  }

  get hasRealFirstEmployee(): boolean {
    const firstId = this.journeyState?.firstEmployeeId;
    return !!(
      firstId &&
      this.createdEmployee?.id &&
      this.createdEmployee.id === firstId &&
      this.createdEmployee.id !== this.journeyState?.privateTestEmployeeId
    );
  }

  get isEmployeeRunsDelivery(): boolean {
    return (this.actorRoute || this.journeyState?.actorRoute) === 'employee_runs';
  }

  get isHeroInstallsDelivery(): boolean {
    return (this.actorRoute || this.journeyState?.actorRoute) === 'hero_installs';
  }

  openAddEmployeeHandoff(): void {
    if (this.employeeHandoff.isActive()) {
      this.employeeHandoff.launchEmployeesCreate();
      return;
    }
    this.employeeHandoff.begin();
    this.activationJourney.trackEvent('first_employee_handoff_started');
    this.activationJourney.markInviteSent();
    this.employeeHandoff.launchEmployeesCreate();
  }

  chooseInviteEmployee(): void {
    this.activationJourney.beginEmployeeInvite();
  }

  openAddEmployeeHandoffWithWarn(): void {
    if (this.activationJourney.needsInviteAnnouncementWarn()) {
      this.showInviteAnnounceWarn = true;
      return;
    }
    this.openAddEmployeeHandoff();
  }

  dismissInviteWarnAndOpenAdd(): void {
    this.activationJourney.dismissInviteWarn();
    this.showInviteAnnounceWarn = false;
    this.openAddEmployeeHandoff();
  }

  goBackToAnnounceFromInviteWarn(): void {
    this.showInviteAnnounceWarn = false;
    this.activationJourney.goToStep('announce');
  }

  get inviteChecklist(): {
    purpose: boolean;
    collection: boolean;
    access: boolean;
    announce: boolean;
  } {
    const js = this.journeyState || this.activationJourney.getState();
    const done = new Set(js.completedSteps || []);
    return {
      purpose: done.has('plan_viewed') || !!js.goal,
      collection: done.has('collection'),
      access: done.has('setup_confirmed') || !!js.fairSetupAckSeen,
      announce: shouldSkipAnnounce(js),
    };
  }

  get canContinueFromPurpose(): boolean {
    return !!this.selectedPurposeId;
  }

  selectTrustFirstPurpose(id: TrustFirstPurposeId): void {
    this.selectedPurposeId = id;
    this.activationJourney.setTrustFirstPurpose(id, null);
  }

  continueFromPurpose(): void {
    this.journeyFreezeLog('continueFromPurpose_enter', {
      purpose: this.selectedPurposeId,
      can: this.canContinueFromPurpose,
    });
    if (!this.canContinueFromPurpose || !this.selectedPurposeId) {
      this.journeyFreezeLog('continueFromPurpose_blocked');
      return;
    }
    const purpose = this.selectedPurposeId;
    // Hold Collection before any journey patch so state$ cannot snap back to Purpose.
    this.raiseReentryWalkCeiling('collection');
    this.collectionBeat = 0;
    this.paintJourneyStep('collection');
    this.syncSetupStepUrl('collection');
    this.journeyFreezeLog('continueFromPurpose_forced_collection');
    try {
      this.activationJourney.setTrustFirstPurpose(purpose, null);
      this.activationJourney.completePurposeAndAdvance();
    } finally {
      this.holdUiStep('collection');
      this.currentStepId = 'collection';
      this.cdr.markForCheck();
    }
    queueMicrotask(() => {
      if (this.currentStepId !== 'collection') {
        this.journeyFreezeLog('continueFromPurpose_reassert_ui', {
          was: this.currentStepId,
        });
        this.holdUiStep('collection');
        this.cdr.markForCheck();
      }
      this.journeyFreezeLog('continueFromPurpose_microtask_done');
      // #region agent log
      this.agentDbg('H5', 'onboarding-checklist.component.ts:continueFromPurpose', 'purpose continue settled', {
        ui: this.currentStepId,
        state: this.activationJourney.getState().currentStep,
        beat: this.collectionBeat,
        reentry: this.setupReentryFrom(),
        ceiling: this.reentryWalkCeiling,
        hold: this.uiStepHold,
        isLoading: this.isLoading,
        save: this.collectionSaveStatus,
      });
      // #endregion
    });
  }

  private hydrateCollectionFromState(): void {
    const snap = this.activationJourney.getCollectionSnapshotFromState();
    this.collectionAppsOn = snap.appsOn;
    this.collectionScreenshotsOn = snap.screenshotsOn;
    this.collectionScreenshotInterval =
      snap.screenshotIntervalMinutes || this.collectionScreenshotInterval || 15;
    this.collectionLiveViewAllowed = snap.liveViewAllowed;
    this.collectionEmployeeControlled = snap.employeeControlled;
    this.collectionTrayOn = snap.trayOn;
    this.collectionShiftLabel = snap.shiftLabel;
    this.canUseAdminControlledMode = this.entitlementsService.hasCompany(EntitlementKey.hidden_mode);
    this.collectionLiveAuditAvailable = this.entitlementsService.hasCompany(EntitlementKey.live_wall);
    this.refreshAvailableModes();
    this.loadCollectionShiftLabel();
  }

  get screenshotIntervalFloor(): number {
    return this.entitlementsService.getMinScreenshotCaptureMinutes() || 10;
  }

  get collectionBeatLabel(): string {
    return `Question ${this.collectionBeat + 1} of ${this.collectionBeatDots.length}`;
  }

  get collectionBeatTitle(): string {
    const c = this.guideCopy;
    return [
      c.collectionBeatShotsTitle,
      c.collectionBeatModeTitle,
      c.collectionConfirmTitle,
    ][this.collectionBeat] || c.collectionHeadline;
  }

  get collectionBeatBody(): string {
    const c = this.guideCopy;
    return [
      c.collectionBeatShotsBody,
      c.collectionBeatModeBody,
      c.collectionConfirmBody,
    ][this.collectionBeat] || c.collectionBody;
  }

  get collectionRecap(): string[] {
    const c = this.guideCopy;
    return [
      this.collectionAppsOn ? c.collectionAppsOnTitle : c.collectionAppsOffTitle,
      c.collectionHoursIncluded,
      this.collectionScreenshotsOn
        ? `${c.collectionShotsOnTitle}, about every ${this.collectionScreenshotInterval} minutes`
        : c.collectionShotsOffTitle,
      this.collectionLiveViewAllowed ? c.collectionLiveOnTitle : c.collectionLiveOffTitle,
      this.collectionModeLabel,
    ];
  }

  /** Workspace default: one choice that maps to valid combinations of the tray and auto-tracking settings. */
  get collectionMode(): TrackingModeId {
    if (!this.collectionTrayOn) {
      return 'hidden';
    }
    return this.collectionEmployeeControlled ? 'employee_controlled' : 'admin_controlled';
  }

  get collectionModeLabel(): string {
    return trackingModeLabel(this.collectionMode);
  }

  get collectionModeOptions(): Array<{
    id: TrackingModeId;
    label: string;
    description: string;
    icon: string;
    locked: boolean;
    recommended: boolean;
    caution: string;
    facts: Array<{ icon: string; text: string }>;
  }> {
    // Stable reference unless the lock state actually changes — otherwise this getter
    // re-dirties the view every CD pass and freezes the "mode" beat.
    const sig = this.modes.map((m) => `${m.id}:${m.locked ? 1 : 0}`).join('|');
    if (this._modeOptionsCache && this._modeOptionsSig === sig) {
      return this._modeOptionsCache;
    }
    const c = this.guideCopy;
    const selfStart = { icon: 'touch_app', text: c.collectionModeFactSelfStart };
    const autoStart = { icon: 'autorenew', text: c.collectionModeFactAutoStart };
    const visible = { icon: 'visibility', text: c.collectionModeFactVisible };
    const unseen = { icon: 'visibility_off', text: c.collectionModeFactHidden };
    const meta: Record<
      TrackingModeId,
      { description: string; icon: string; caution?: string; facts: Array<{ icon: string; text: string }> }
    > = {
      employee_controlled: {
        description: c.collectionModeEmployeeDetail,
        icon: 'touch_app',
        facts: [selfStart, visible],
      },
      admin_controlled: {
        description: c.collectionModeAdminDetail,
        icon: 'autorenew',
        facts: [autoStart, visible],
      },
      hidden: {
        description: c.collectionModeHiddenDetail,
        icon: 'visibility_off',
        caution: c.collectionModeHiddenCaution,
        facts: [autoStart, unseen],
      },
    };
    const list = (['employee_controlled', 'admin_controlled', 'hidden'] as TrackingModeId[]).map((id) => ({
      id,
      label: trackingModeLabel(id),
      description: meta[id].description,
      icon: meta[id].icon,
      locked: !!this.modes.find((mode) => mode.id === id)?.locked,
      recommended: id === 'employee_controlled',
      caution: meta[id].caution || '',
      facts: meta[id].facts,
    }));
    this._modeOptionsSig = sig;
    this._modeOptionsCache = list;
    return list;
  }

  /** Stable identity for mode-card *ngFor so Angular reuses DOM instead of recreating it each CD pass. */
  trackByModeId(_index: number, item: { id: string }): string {
    return item.id;
  }

  pickCollectionMode(option: { id: TrackingModeId; locked: boolean }): void {
    if (option.locked) {
      this._snackBar.open('This tracking mode requires a Professional plan or higher.', 'OK', { duration: 4000 });
      return;
    }
    // Hidden turns the app off and leaves auto-tracking off; the product treats Silent and Automatic as exclusive.
    this.collectionTrayOn = option.id !== 'hidden';
    this.collectionEmployeeControlled = option.id !== 'admin_controlled';
  }

  backFromCollection(): void {
    if (!this.isPhoneOrTabletReady && this.collectionBeat > 0) {
      this.collectionBeat -= 1;
      this.syncSetupStepUrl('collection');
      return;
    }
    this.goBack();
  }

  /** Confirm screen "Change" links jump straight back to the question that set the value. */
  goToCollectionBeat(beat: number): void {
    if (beat < 0 || beat >= this.collectionBeatDots.length || beat === this.collectionBeat) {
      return;
    }
    this.collectionBeat = beat;
    this.syncSetupStepUrl('collection');
  }

  get collectionShotsSummary(): string {
    const c = this.guideCopy;
    return this.collectionScreenshotsOn
      ? c.collectionConfirmShotsEvery.replace('{n}', String(this.collectionScreenshotInterval))
      : c.collectionConfirmOff;
  }

  advanceCollectionBeat(): void {
    const t0 = Date.now();
    // #region agent log
    this.agentDbg('H1', 'onboarding-checklist.component.ts:advanceCollectionBeat', 'screenshots continue entered', {
      beat: this.collectionBeat,
      ui: this.currentStepId,
      state: this.activationJourney.getState().currentStep,
      isLoading: this.isLoading,
      save: this.collectionSaveStatus,
      reentry: this.setupReentryFrom(),
      ceiling: this.reentryWalkCeiling,
      hold: this.uiStepHold,
    });
    // #endregion
    this.journeyFreezeLog('advanceCollectionBeat_enter');
    this.raiseReentryWalkCeiling('collection');
    this.holdUiStep('collection');
    if (this.collectionBeat < this.collectionBeatDots.length - 1) {
      const prev = this.collectionBeat;
      this.collectionBeat += 1;
      this.cdr.markForCheck();
      this.journeyFreezeLog('advanceCollectionBeat_local', { prev, next: this.collectionBeat });
      // Re-entry: replaceState only (see syncSetupStepUrl). Never read beat from
      // ActivatedRoute — it stays stale after replaceState and would snap back.
      this.syncSetupStepUrl('collection');
      queueMicrotask(() => {
        if (this.collectionBeat !== prev + 1) {
          this.journeyFreezeLog('advanceCollectionBeat_reassert', {
            beatStill: this.collectionBeat,
            expected: prev + 1,
          });
          this.collectionBeat = prev + 1;
          this.cdr.markForCheck();
        }
        this.journeyFreezeLog('advanceCollectionBeat_microtask', {
          beatStill: this.collectionBeat,
          expected: prev + 1,
        });
        // #region agent log
        this.agentDbg('H2', 'onboarding-checklist.component.ts:advanceCollectionBeat', 'beat after microtask', {
          prev,
          beat: this.collectionBeat,
          expected: prev + 1,
          ui: this.currentStepId,
          ms: Date.now() - t0,
          reentry: this.setupReentryFrom(),
        });
        // #endregion
      });
      return;
    }
    this.journeyFreezeLog('advanceCollectionBeat_save');
    this.saveCollectionAndContinue();
  }

  saveCollectionSchedule(): void {
    const shift = this.collectionShiftRaw;
    if (!shift?.id) {
      return;
    }
    const start = this.toShiftTime(this.collectionShiftStart);
    const end = this.toShiftTime(this.collectionShiftEnd);
    this.scheduleSaveStatus = 'saving';
    this.shiftService
      .updateShift(shift.id, {
        shift_name: shift.shift_name || this.collectionShiftLabel || 'Work schedule',
        timezone: shift.timezone || 'Asia/Kolkata',
        shift_start_time: start,
        shift_end_time: end,
        working_days: Array.isArray(shift.working_days) && shift.working_days.length ? shift.working_days : [1, 2, 3, 4, 5],
        is_overnight: !!shift.is_overnight,
      })
      .subscribe({
        next: (updated) => {
          this.collectionShiftRaw = { ...shift, ...updated, start_time: start, end_time: end };
          this.collectionShiftStart = this.toTimeInput(start);
          this.collectionShiftEnd = this.toTimeInput(end);
          this.scheduleSaveStatus = 'saved';
        },
        error: () => {
          this.scheduleSaveStatus = '';
          this._snackBar.open('Could not update these hours. Try again.', 'OK', { duration: 4000 });
        },
      });
  }

  private toTimeInput(value: string): string {
    const match = String(value || '').match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '09:00';
  }

  private toShiftTime(value: string): string {
    const match = String(value || '').match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}:00` : '09:00:00';
  }

  private formatShiftDays(days: number[] | undefined): string {
    const names = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const list = (days || []).map((day) => names[(Number(day) - 1 + 7) % 7]).filter(Boolean);
    return list.length ? list.join(', ') : '';
  }

  private toSettingsHms(value: unknown, fallbackMinutes: number): string {
    const text = String(value ?? '').trim();
    if (/^\d{2}:\d{2}:\d{2}$/.test(text)) {
      return text;
    }
    if (/^\d{1,2}:\d{2}$/.test(text)) {
      const [hours, minutes] = text.split(':');
      return `${hours.padStart(2, '0')}:${minutes}:00`;
    }
    const parsed = Number(text);
    const total = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallbackMinutes;
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  }

  private loadCollectionShiftLabel(): void {
    try {
      const user =
        this.tokenEncryptionService.getEncryptedItem<any>('user') ||
        this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
      const companyId = user?.id || user?.meta?.company_id;
      if (!companyId) {
        return;
      }
      this.shiftService.getShifts(companyId).subscribe({
        next: (shifts) => {
          const first = Array.isArray(shifts) && shifts.length ? shifts[0] : null;
          if (!first) {
            return;
          }
          const row = first as any;
          this.collectionShiftRaw = row;
          this.collectionShiftId = Number(row.id || row.shift_id) || null;
          this.collectionShiftLabel = row.shift_name || row.name || 'company work schedule';
          this.collectionShiftStart = this.toTimeInput(row.start_time || row.shift_start_time);
          this.collectionShiftEnd = this.toTimeInput(row.end_time || row.shift_end_time);
          this.collectionShiftDaysLabel = this.formatShiftDays(row.working_days);
        },
        error: () => undefined,
      });
    } catch {
      /* ignore */
    }
  }

  saveCollectionAndContinue(): void {
    if (this.collectionSaveStatus === 'saving') return;
    this.collectionSaveStatus = 'saving';
    let stage = 'save these hours';
    // Bound every request and cancel the entire sequence when this view is destroyed.
    defer(() => {
      const minInterval = this.entitlementsService.getMinScreenshotCaptureMinutes() || 10;
      let interval = Number(this.collectionScreenshotInterval) || minInterval;
      if (interval < minInterval) {
        interval = minInterval;
        this.collectionScreenshotInterval = minInterval;
      }
      const trayOn = !!this.collectionTrayOn;
      const employeeControlled = !!this.collectionEmployeeControlled;
      const snapshot = {
        appsOn: !!this.collectionAppsOn,
        hoursIncluded: true,
        screenshotsOn: !!this.collectionScreenshotsOn,
        screenshotIntervalMinutes: this.collectionScreenshotsOn ? interval : null,
        liveViewAllowed: !!this.collectionLiveViewAllowed,
        liveViewAuditNote: !!this.collectionLiveViewAllowed && this.collectionLiveAuditAvailable,
        employeeControlled,
        trayOn,
        shiftLabel: this.collectionShiftLabel,
      };
      const shift = this.collectionShiftRaw;
      const saveHours$ = shift?.id
        ? this.shiftService.updateShift(shift.id, {
            shift_name: shift.shift_name || this.collectionShiftLabel || 'Work schedule',
            timezone: shift.timezone || 'Asia/Kolkata',
            shift_start_time: this.toShiftTime(this.collectionShiftStart),
            shift_end_time: this.toShiftTime(this.collectionShiftEnd),
            working_days: Array.isArray(shift.working_days) && shift.working_days.length ? shift.working_days : [1, 2, 3, 4, 5],
            is_overnight: !!shift.is_overnight,
          }).pipe(first(), timeout(15000))
        : of(null);
      return saveHours$.pipe(
        switchMap(() => {
          stage = 'load collection settings';
          return defer(() => this._projectService.getGlobalSettings(true)).pipe(first(), timeout(15000));
        }),
        switchMap((res: any) => {
          stage = 'save collection settings';
          const data = res?.data || res || {};
          const payload = {
            ss_screen_capture_time: this.toSettingsHms(
              snapshot.screenshotsOn ? interval : data.ss_screen_capture_time,
              minInterval
            ),
            ss_appchange_interval: this.toSettingsHms(data.ss_appchange_interval, 5),
            ss_idle_time: this.toSettingsHms(data.ss_idle_time, 5),
            ss_appchange: snapshot.appsOn ? 'true' : 'false',
            ss_periodic: snapshot.screenshotsOn ? 'true' : 'false',
            ss_silent: trayOn ? 'false' : 'true',
            ss_automatic_tracking: employeeControlled ? 'false' : 'true',
            ss_strict_time_tracking:
              data.ss_strict_time_tracking === true || data.ss_strict_time_tracking === 'true' ? 'true' : 'false',
            ss_webcam: data.ss_webcam === true || data.ss_webcam === 'true' ? 'true' : 'false',
          };
          return defer(() => this._projectService.setGlobalSettings(payload, false)).pipe(first(), timeout(15000));
        }),
        tap(() => {
          // Record confirmed choices only after all server writes succeed.
          this.activationJourney.savePilotCollection(snapshot);
          this.collectionSaveStatus = 'saved';
          const before = this.activationJourney.getState();
          const keepHireUi =
            before.waitingReason === 'employee_data' &&
            (before.completedSteps || []).includes('first_employee') &&
            !!before.firstEmployeeId;
          // Preview is first_employee in the step order (after install leftovers). Raise
          // ceiling + hold BEFORE goToStep or reentry ignoreJumpAhead keeps confirm frozen.
          this.raiseReentryWalkCeiling('first_employee');
          this.holdUiStep('first_employee');
          this.cdr.markForCheck();
          this.syncSetupStepUrl('first_employee');
          this.journeyFreezeLog('saveCollection_forced_preview');
          this.activationJourney.completeCollectionAndAdvance();
          this.holdUiStep('first_employee');
          this.currentStepId = 'first_employee';
          this.cdr.markForCheck();
          if (!keepHireUi) {
            // Drop stale hire/private-test employee so Preview fork renders, not wait UI.
            this.createdEmployee = null;
            this.createdEmployeePassword = '';
            this.isAgentOnline = false;
            this.isDownloaded = false;
            this.connectWaitStartedAt = null;
            this.syncConnectWaitClock();
            this.stopPolling({ preserveRecovery: false });
          }
          this.loadAccessTeams();
          this.refreshAnnouncementDraft();
        })
      );
    }).pipe(
      takeUntil(this.collectionSaveDestroyed),
      finalize(() => {
        if (this.collectionSaveStatus === 'saving') this.collectionSaveStatus = '';
      })
    ).subscribe({
      error: (error) => {
        this.collectionSaveStatus = '';
        const message = error?.name === 'TimeoutError'
          ? 'Taking too long to ' + stage + '. Check your connection and try again.'
          : 'Could not ' + stage + '. Try again.';
        this._snackBar.open(message, 'OK', { duration: 6000 });
      },
    });
  }

  private loadAccessTeams(): void {
    this._projectService.getTeamList().subscribe({
      next: (teams: any) => {
        const list = Array.isArray(teams) ? teams : teams?.data || [];
        this.accessTeams = (list || []).map((team: any) => {
          const managers = String(team?.meta?.team_manager || '')
            .split('~')
            .filter(Boolean);
          const leads = String(team?.meta?.team_lead || '')
            .split('~')
            .filter(Boolean);
          return {
            name: team?.name || team?.title?.rendered || 'Team',
            managerLabel: managers.length ? `${managers.length} assigned` : 'None yet',
            leadLabel: leads.length ? `${leads.length} assigned` : 'None yet',
          };
        });
      },
      error: () => {
        this.accessTeams = [];
      },
    });
  }

  openTeamsFromAccess(): void {
    void this.router.navigate(['/teams']);
  }

  continueAccess(): void {
    this.activationJourney.completeAccessAndAdvance();
    this.refreshAnnouncementDraft();
  }

  private refreshAnnouncementDraft(): void {
    this.announcementDraft = this.activationJourney.getAnnouncementText();
    this.announcementFacts = this.activationJourney.getAnnouncementFacts();
    this.announcementFaq = this.activationJourney.getAnnouncementFaq();
    this.announcementAckTemplate = this.activationJourney.getAnnouncementAckTemplate();
  }

  get announcementParagraphs(): string[] {
    const text = (this.announcementDraft || '').trim();
    if (!text) {
      return [];
    }
    if (text.includes('\n')) {
      return text.split(/\n+/).map((part) => part.trim()).filter(Boolean);
    }
    return text.split(/(?<=\.)\s+/).map((part) => part.trim()).filter(Boolean);
  }

  onAnnouncementDraftChange(text: string): void {
    this.announcementDraft = text;
    this.activationJourney.setAnnouncementCustomText(text);
  }

  resetAnnouncementDraft(): void {
    this.activationJourney.resetAnnouncementToSettings();
    this.refreshAnnouncementDraft();
  }

  private copyText(text: string, okMessage: string): void {
    if (navigator?.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).then(
        () => this._snackBar.open(okMessage, 'OK', { duration: 2500 }),
        () => this._snackBar.open('Could not copy. Select the text manually.', 'OK', { duration: 3500 })
      );
      return;
    }
    this._snackBar.open('Could not copy. Select the text manually.', 'OK', { duration: 3500 });
  }

  copyAnnouncementText(): void {
    this.activationJourney.markAnnouncementCopied('announcement');
    this.copyText(this.announcementDraft, 'Announcement copied');
  }

  copyAnnouncementFaq(): void {
    this.activationJourney.markAnnouncementCopied('faq');
    this.copyText(this.activationJourney.getAnnouncementFaqCopy(), 'Questions copied');
  }

  copyAnnouncementAck(): void {
    this.activationJourney.markAnnouncementCopied('ack');
    this.copyText(this.announcementAckTemplate, 'Template copied');
  }

  openAnnouncementEmailDraft(): void {
    this.activationJourney.markAnnouncementCopied('email');
    const subject = encodeURIComponent('Introducing SuperSee');
    const body = encodeURIComponent(this.announcementDraft || '');
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  completeAnnounceShared(): void {
    this.activationJourney.completeAnnounceShared();
  }

  completeAnnounceSkipped(): void {
    this.activationJourney.completeAnnounceSkipped();
  }

  openFirstUsefulWorkday(): void {
    this.activationJourney.markFirstReviewOpened();
    const employeeId =
      this.journeyState?.firstEmployeeId ||
      this.createdEmployee?.id ||
      this.activationJourney.getState().firstEmployeeId;
    if (employeeId) {
      void this.router.navigate(['/work-replay', employeeId]);
      return;
    }
    void this.router.navigate(['/work-replay']);
  }

  restoreCreatedEmployee(): void {
    const saved = this.onboardingService.getCreatedEmployee();
    console.log('[Onboarding] restoreCreatedEmployee - saved:', saved);
    if (saved && saved.employee) {
      this.createdEmployee = saved.employee;
      this.createdEmployeePassword = saved.password || '';
      console.log('[Onboarding] Restored employee id:', saved.employee.id);
      // Validate the employee still exists on the server
      this.validateCreatedEmployee(saved.employee.id);
    } else {
      console.log('[Onboarding] No saved employee found in localStorage');
    }
  }

  validateCreatedEmployee(employeeId: number | string): void {
    if (!employeeId) return;
    console.log('[Onboarding] Validating employee exists, id:', employeeId);
    this._loginService.getUser(employeeId).subscribe({
      next: (response: any) => {
        if (Number(this.createdEmployee?.id) !== Number(employeeId)) {
          return;
        }
        console.log('[Onboarding] getUser response:', response);
        if (!response || !response.id) {
          console.log('[Onboarding] Employee not found in response, resetting');
          this.handleEmployeeDeleted();
        } else {
          console.log('[Onboarding] Employee still exists:', response.id);
        }
      },
      error: (err) => {
        if (Number(this.createdEmployee?.id) !== Number(employeeId)) {
          return;
        }
        console.log('[Onboarding] getUser error, status:', err.status);
        if (err.status === 404 || err.status === 400) {
          this.handleEmployeeDeleted();
        }
      }
    });
  }

  handleEmployeeDeleted(): void {
    this.forgetStalePrivateTestIdentity();
    this.isDownloaded = false;
    this.isAgentOnline = false;
    this.isScreenshotCaptured = false;
    if (this.journeyEnabled) {
      this._snackBar.open(
        'That install user was removed. Download SuperSee again to continue setup.',
        'OK',
        { duration: 5000 }
      );
      return;
    }
    this.onboardingService.resetToCreateEmployee();
    this._snackBar.open('The previously created employee was removed. Please create a new one.', 'OK', { duration: 5000 });
  }

  /** True when the WP user behind a stored private-test / setup employee no longer exists. */
  private isMissingEmployeeError(err: any): boolean {
    const status = err?.status;
    if (status === 404 || status === 400) {
      return true;
    }
    const code = String(err?.error?.code || '');
    if (code === 'rest_user_invalid_id' || code === 'rest_user_invalid_parameter') {
      return true;
    }
    const msg = String(err?.error?.message || err?.message || '').toLowerCase();
    return msg.includes('invalid user') || msg.includes('unknown user');
  }

  /**
   * The install-on-this-PC login is a dedicated test user (…@supersee.testaccount),
   * never a real employee already in the company.
   */
  private isDedicatedPrivateTestEmployee(emp: {
    email?: string;
    name?: string;
    first_name?: string;
  } | null): boolean {
    const email = String(emp?.email || '')
      .trim()
      .toLowerCase();
    if (
      email.endsWith('@supersee.testaccount') ||
      email.endsWith('.supersee.testaccount') ||
      email.startsWith('private-test@') ||
      email.startsWith('private-test-')
    ) {
      return true;
    }
    const label = `${emp?.first_name || ''} ${emp?.name || ''}`.toLowerCase();
    return label.includes('test employee') || label.includes('this computer');
  }

  /**
   * New private-test users only. Reused employees keep their existing email.
   * `unique` avoids a collision when another company already took the slug.
   */
  private buildPrivateTestPlaceholderEmail(unique = false): string {
    const slug = String(this.workspaceName || this.workspaceNameSaved || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32);
    const local = unique ? `private-test-${Date.now().toString(36)}` : 'private-test';
    const domain = slug ? `${slug}.supersee.testaccount` : 'supersee.testaccount';
    return `${local}@${domain}`;
  }

  /** Obvious fake domains from older private-try placeholders — not real hire emails. */
  private isJunkPrivateTestEmail(email: string | null | undefined): boolean {
    const domain = String(email || '')
      .trim()
      .toLowerCase()
      .split('@')[1]
      ?.replace(/>$/, '')
      ?.trim();
    if (!domain) {
      return false;
    }
    const junkExact = new Set([
      'emailnotreal.com',
      'example.com',
      'test.com',
      'mailinator.com',
      'tempmail.com',
      'yopmail.com',
    ]);
    if (junkExact.has(domain)) {
      return true;
    }
    return domain.endsWith('.emailnotreal.com') || domain.endsWith('.example.com');
  }

  /**
   * Forget a private-test identity that was deleted from the dashboard so the next
   * download provisions a new user instead of resetting a dead id.
   */
  private forgetStalePrivateTestIdentity(): void {
    this.createdEmployee = null;
    this.createdEmployeePassword = '';
    this.privateTestReady = false;
    this.connectWaitStartedAt = null;
    this.connectWaitSawOffline = false;
    this.syncConnectWaitClock();
    this.stopPolling();
    this.onboardingService.clearCreatedEmployee();
    this.activationJourney.clearPrivateTestIdentity('employee_missing');
    this.activationReadiness.setEvidence({
      packagePrepared: false,
      privateTestEmployeeId: null,
    });
  }

  loadTrackingMode(): void {
    const savedMode = this.onboardingService.getTrackingMode();
    if (savedMode) {
      this.selectedMode = savedMode;
    }
  }

  getNextStepFromBackend(): void {
    this.onboardingService.getNextStep().subscribe({
      next: (response) => {
        console.log('[Onboarding] getNextStep response:', response);
        if (!response.completed) {
          this.updateCurrentStepFromBackend(response.next_step);
        } else {
          // All steps completed - show the last step (request_screenshot) with completion message
          console.log('[Onboarding] All steps completed - showing completion screen');
          this.currentStepId = 'request_screenshot';
          this.isScreenshotCaptured = true;
        }
      },
      error: (error) => {
        console.error('[Onboarding] getNextStep error:', error);
        if (error?.status === 404) {
          this.onboardingService.finalizeWhenNextStepUnavailable('getNextStepFromBackend');
          this.currentStepId = 'request_screenshot';
          this.isScreenshotCaptured = true;
        }
        this.loadTrackingMode();
      }
    });
  }

  updateCurrentStepFromBackend(step: any): void {
    console.log('[Onboarding] updateCurrentStepFromBackend - step_id:', step?.step_id, 'createdEmployee:', !!this.createdEmployee);
    
    // Update current step ID - trust the backend state
    // The intelligent detection will have already marked create_employee as complete if employees exist
    this.currentStepId = step.step_id;

    // Load any existing data for this step
    if (step.step_data) {
      this.loadStepData(step.step_id, step.step_data);
    }

    // If it's a navigation step, automatically navigate
    if (step.type === 'navigation') {
      this.router.navigate([step.route]);
      // Mark as completed after navigation
      setTimeout(() => {
        this.onboardingService.completeStepLocalOnly(step.step_id);
      }, 1000);
    }
  }

  loadStepData(stepId: string, data: any): void {
    switch (stepId) {
      case 'mode_selection':
        this.selectedMode = data.selected_mode || '';
        break;
      case 'create_employee':
        // Load employee data if needed
        break;
      // Add other steps as needed
    }
  }

  loadUserInfo(): void {
    const user = this.onboardingService.getCurrentUser();
    if (user) {
      this.userName = user.name || 'User';
      this.userEmail = user.email || '';
      const companyName = (user.meta?.company_name || '').toString();
      this.workspaceName = companyName;
      this.workspaceNameSaved = companyName;
      const size = normalizeTeamSizeForSelect(user.meta?.teamSize);
      this.teamSize = size;
      this.teamSizeSaved = size;
      const phone = this.contactPhoneCapture.normalizePhone(user.meta?.phone_number);
      this.contactPhone = phone;
      this.contactPhoneSaved = phone;
      // Ready setup uses always-visible inputs — keep both "open" for blur-save.
      this.workspaceNameEditing = true;
      this.ensureReadyContactCaptureOpen();
      this.assistedSetupRequested = this.readAssistedSetupRequestedFlag(user);
    }
  }

  ngOnDestroy(): void {
    this.collectionSaveDestroyed.next();
    this.collectionSaveDestroyed.complete();
    this.entitlementsSub?.unsubscribe();
    this.journeySub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.querySub?.unsubscribe();
    this.navEndSub?.unsubscribe();
    this.stopFirstDayEvidenceRefresh();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.stopPolling();
    this.stopConnectWaitClock();
    this.activationJourney.markConnectWaitLeft('destroy');
    // Leaving /setup ends the menu/restart walkthrough latch.
    const path = (this.router.url || '').split('?')[0];
    if (!path.startsWith('/setup')) {
      this.activationJourney.clearIntentionalReentry();
    }
    if (this.screenshotSubscription) {
      this.screenshotSubscription.unsubscribe();
      this.screenshotSubscription = null;
    }
    if (this.screenshotSub) {
      this.screenshotSub.unsubscribe();
      this.screenshotSub = null;
    }
    if (this.screenshotTimeout) {
      clearTimeout(this.screenshotTimeout);
      this.screenshotTimeout = null;
    }
    this._sharedService.endOnboardingScreenshotCapture();
    this.clearHelpNudge();
    if (this.workspaceNameSavedStatusTimer) {
      clearTimeout(this.workspaceNameSavedStatusTimer);
      this.workspaceNameSavedStatusTimer = null;
    }
    if (this.teamSizeSavedStatusTimer) {
      clearTimeout(this.teamSizeSavedStatusTimer);
      this.teamSizeSavedStatusTimer = null;
    }
    if (this.contactPhoneSavedStatusTimer) {
      clearTimeout(this.contactPhoneSavedStatusTimer);
      this.contactPhoneSavedStatusTimer = null;
    }
    if (this.screenshotRetryTimeout) {
      clearTimeout(this.screenshotRetryTimeout);
      this.screenshotRetryTimeout = null;
    }
    if (this.installDownloadCtaUnlockTimer) {
      clearTimeout(this.installDownloadCtaUnlockTimer);
      this.installDownloadCtaUnlockTimer = null;
    }
  }

  @HostListener('window:pagehide')
  onConnectWaitPageHide(): void {
    this.activationJourney.markConnectWaitLeft('pagehide');
  }

  private refreshAvailableModes(): void {
    const enforcementActive = this.entitlementsService.isEnforcementActive();
    this.modes = this.allModes.map((mode) => ({
      ...mode,
      locked: !isOnboardingTrackingModeAllowed(
        mode.id,
        this.entitlementsService.getPlanCode(),
        enforcementActive,
        this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
      ),
    }));
    const selectedMode = this.modes.find((mode) => mode.id === this.selectedMode);
    if (!selectedMode || selectedMode.locked) {
      this.selectedMode = this.modes.find((mode) => !mode.locked)?.id || 'employee_controlled';
    }
    if (this.privateTestVisibility === 'hidden' && !this.canShowHiddenPrivateTest) {
      this.privateTestVisibility = 'visible';
    }
  }

  // Mode Selection
  selectMode(modeId: string): void {
    const mode = this.modes.find((item) => item.id === modeId);
    if (mode?.locked) {
      this._snackBar.open('This tracking mode requires a Professional plan or higher.', 'OK', { duration: 4000 });
      return;
    }
    this.selectedMode = modeId;
  }

  confirmModeSelection(): void {
    if (!this.selectedMode) {
      this._snackBar.open('Please select a tracking mode', 'OK', { duration: 3000 });
      return;
    }

    if (this.journeyEnabled) {
      if (this.adjustingModeFromReview) {
        this.activationJourney.setFirstEmployeeMode(this.selectedMode);
      } else {
        this.activationJourney.setMode(this.selectedMode);
      }
      this.onboardingService.setTrackingMode(this.selectedMode as any);
      this.isLoading = true;
      const afterSave = () => {
        this.isLoading = false;
        if (this.adjustingModeFromReview) {
          this.reprovisionFirstEmployeeMode(this.selectedMode, () => {
            this.activationJourney.setFirstEmployeeMeta({ mode: this.selectedMode });
            this.activationJourney.goToStep('first_day_review');
            this.adjustingModeFromReview = false;
            this.modeEducationPage = 'compare';
          });
          return;
        }
        this.activationJourney.completeModeEducation();
      };
      this.onboardingService.saveTrackingMode(this.selectedMode).subscribe({
        next: () => afterSave(),
        error: () => {
          afterSave();
        }
      });
      return;
    }

    this.isLoading = true;

    this.onboardingService.saveTrackingMode(this.selectedMode).subscribe({
      next: (response: any) => {
        this._snackBar.open(response.message, 'OK', { duration: 3000 });

        if (response?.completed) {
          this.currentStepId = 'request_screenshot';
          this.isScreenshotCaptured = true;
        } else if (response?.next_step) {
          this.updateCurrentStepFromBackend(response.next_step);
        } else {
          this.getNextStepFromBackend();
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error saving tracking mode:', error);
        let errorMessage = 'Failed to save tracking mode';

        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.status === 400) {
          errorMessage = 'Invalid tracking mode selected';
        }

        this._snackBar.open(errorMessage, 'OK', { duration: 5000 });
        this.isLoading = false;
      }
    });
  }

  goToNextStep(): void {
    const state = this.onboardingService.getState();
    const nextIncomplete = state.steps.findIndex((s, i) => i > state.currentStepIndex && !s.completed);
    if (nextIncomplete !== -1) {
      this.onboardingService.goToStep(nextIncomplete);
    } else {
      // If all steps are completed, go to dashboard
      this.router.navigate(['/dashboard']);
    }
  }

  // Create Employee
  isEmployeeFormValid(): boolean {
    const base = !!(this.employeeForm.name && this.employeeForm.email && this.employeeForm.os);
    const pwd = this.employeeForm.password;
    const passwordOk = !pwd || this.passwordControl.valid;
    return base && passwordOk;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onPasswordChange(value: string): void {
    this.passwordControl.setValue(value || '', { emitEvent: false });
  }

  generateAndSetPassword(): void {
    this.employeeForm.password = this.generateRandomPassword();
    this.passwordControl.setValue(this.employeeForm.password, { emitEvent: false });
    this.passwordControl.markAsTouched();
    this.showPassword = true;
  }

  copyToClipboard(text: string, field: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedField = field;
      const message = field === 'email' ? 'Email copied to clipboard!' : 
                      field === 'password' ? 'Password copied to clipboard!' :
                      field === 'introScript' ? 'Intro copied — tell them before they install.' :
                      'Copied to clipboard!';
      this._snackBar.open(message, 'Close', { 
        duration: 2000,
        panelClass: ['success-snackbar']
      });
      setTimeout(() => {
        this.copiedField = '';
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      this._snackBar.open('Failed to copy to clipboard', 'Close', { duration: 2000 });
    });
  }

  /** Re-apply version_type + settings after Adjust; require re-download if installer already ran. */
  private reprovisionFirstEmployeeMode(mode: string, done: () => void): void {
    const employeeId =
      this.journeyState?.firstEmployeeId ||
      (this.createdEmployee?.id !== this.journeyState?.privateTestEmployeeId
        ? this.createdEmployee?.id
        : null);
    if (!employeeId) {
      this._snackBar.open('Mode saved. Add your first employee, then Adjust again if needed.', 'OK', {
        duration: 4500,
      });
      done();
      return;
    }
    const versionType = this.getModeToVersionType(mode);
    const prevType = this.createdEmployee?.version_type;
    this._projectService.updateEmployeeVersionType(employeeId, versionType).subscribe({
      next: () => {
        this.applyPrivateTestModeSettings(employeeId, versionType);
        if (this.createdEmployee?.id === employeeId) {
          this.createdEmployee = { ...this.createdEmployee, version_type: versionType };
        }
        const needsRedownload =
          this.isDownloaded ||
          (prevType && prevType !== versionType) ||
          this.setupMethod === 'computer_setup';
        if (needsRedownload) {
          this.isDownloaded = false;
          this._snackBar.open(
            'Mode updated on the hire. Download setup again (or have them reinstall from invite) so their computer matches.',
            'OK',
            { duration: 7000 }
          );
        } else {
          this._snackBar.open('Tracking mode updated for this employee.', 'OK', { duration: 4000 });
        }
        done();
      },
      error: () => {
        this._snackBar.open(
          'Could not update employee mode on the server. Try again, or re-download after fixing the hire.',
          'OK',
          { duration: 6000 }
        );
        done();
      },
    });
  }

  selectOS(osId: string): void {
    this.employeeForm.os = osId;
  }

  createEmployee(): void {
    // Prevent accidental multiple employee creation by navigating back/forth in steps.
    // Activation journey first_employee may still have the private-test identity loaded — allow a new hire.
    if (this.createdEmployee?.id && !(this.journeyEnabled && this.currentStepId === 'first_employee')) {
      this._snackBar.open('An employee is already created for setup. Continue to the next step.', 'OK', { duration: 4000 });
      this.getNextStepFromBackend();
      return;
    }
    if (
      !this.journeyEnabled &&
      this.onboardingService.getState()?.steps?.find(s => s.id === 'create_employee')?.completed
    ) {
      this._snackBar.open('Employee step is already completed. Continuing setup.', 'OK', { duration: 4000 });
      this.getNextStepFromBackend();
      return;
    }

    if (this.isEmployeeFormValid()) {
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.employeeForm.email)) {
        this._snackBar.open('Please enter a valid email address', 'Close', { duration: 3000 });
        return;
      }

      this.isLoading = true;

      const password = this.employeeForm.password ? this.employeeForm.password : this.generateRandomPassword();
      const versionType = this.getModeToVersionType(this.selectedMode);
      const osMap = { windows: 'windows', macos: 'mac', linux: 'linux' };
      const os = osMap[this.employeeForm.os] || 'windows';

      // Split name into first and last name, handling multiple spaces and edge cases
      const nameParts = this.employeeForm.name.trim().split(/\s+/);
      const firstName = nameParts[0] || 'Employee';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      const payload: any = {
        first_name: firstName,
        last_name: lastName,
        email: this.employeeForm.email,
        username: this.employeeForm.email,
        password: password,
        send_email: versionType !== 'silent',
        roles2: 'employee',
        version_type: versionType,
        operating_system: os
      };

      this._loginService.addUser(payload).subscribe(
        (response: any) => {
          this.isLoading = false;

          // Store created employee data for install_agent step
          this.createdEmployee = {
            ...response,
            first_name: payload.first_name,
            last_name: payload.last_name,
            email: payload.email,
            operating_system: os,
            version_type: versionType
          };
          this.createdEmployeePassword = password;

          // Persist to localStorage so it survives page reload
          this.onboardingService.saveCreatedEmployee(this.createdEmployee, password);

          const finishCreate = () => {
            this._projectService.updateEmployeeList({});

            if (this.journeyEnabled) {
              this.activationJourney.setFirstEmployeeMeta({
                employeeId: response.id,
                mode: this.selectedMode,
                actorRoute: this.actorRoute,
              });
              this.activationJourney.setStatusPhase('package_prepared');
              if (this.setupMethod === 'invite' && !this.pollingActive) {
                this.startPollingAgentStatus();
              }
              this._snackBar.open(
                this.setupMethod === 'invite'
                  ? `${this.firstEmployeeDisplayName} invited. Tell them first, then wait until Connected.`
                  : `${this.firstEmployeeDisplayName} is ready. Download setup for their computer next.`,
                'OK',
                { duration: 4500 }
              );
              return;
            }

            this._snackBar.open('Employee created successfully!', 'OK', { duration: 3000 });

            // Mark step complete via onboarding API
            this.onboardingService.completeStep('create_employee', { employee_id: response.id }).subscribe({
              next: () => this.getNextStepFromBackend(),
              error: () => {
                // Fallback: move to next step locally
                this.onboardingService.completeStepLocal('create_employee');
                this.goToNextStep();
              }
            });
          };

          if (versionType === 'restricted') {
            this._projectService.createAdminControlUserSettings(response.id).subscribe({
              next: () => finishCreate(),
              error: () => {
                this._snackBar.open('Employee created, but failed to apply Automatic mode settings.', 'OK', { duration: 3000 });
                finishCreate();
              }
            });
          } else if (versionType === 'silent') {
            this._projectService.createSilentUserSettings(response.id).subscribe({
              next: () => finishCreate(),
              error: () => {
                this._snackBar.open('Employee created, but failed to enable Silent Mode.', 'OK', { duration: 3000 });
                finishCreate();
              }
            });
          } else {
            finishCreate();
          }
        },
        (error) => {
          this.isLoading = false;
          const errorMsg = error?.error?.message || 'Failed to create employee. Please try again.';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMsg,
            timer: 3000,
            showConfirmButton: false
          });
        }
      );
    }
  }

  private getModeToVersionType(mode: string): 'general' | 'silent' | 'restricted' {
    switch (mode) {
      case 'hidden': return 'silent';
      case 'admin_controlled': return 'restricted';
      case 'employee_controlled':
      default: return 'general';
    }
  }

  getInstallerTypeLabel(): string {
    const vt = this.createdEmployee?.version_type || this.getModeToVersionType(this.selectedMode);
    return trackingModeLabelForVersionType(vt);
  }

  getCreatedEmployeeOS(): string {
    return this.createdEmployee?.operating_system || this.employeeForm.os || 'windows';
  }

  getCreatedEmployeeOSLabel(): string {
    const os = this.getCreatedEmployeeOS();
    switch (os) {
      case 'mac': return 'macOS';
      case 'linux': return 'Linux';
      case 'windows_legacy': return 'Windows 7 & 8';
      default: return 'Windows 10 & 11';
    }
  }

  get isPhoneOrTabletReady(): boolean {
    return this.deviceDetection.isMobileOrTablet();
  }

  get readyAssistTier(): AssistTier {
    return assistTier(this.teamSizeSaved, { phoneOrTablet: this.isPhoneOrTabletReady });
  }

  get showReadyAssistQuiet(): boolean {
    return !this.assistedSetupRequested && this.readyAssistTier === 'quiet';
  }

  get showReadyAssistSecondary(): boolean {
    return !this.assistedSetupRequested && this.readyAssistTier === 'secondary';
  }

  get showReadyAssistStrong(): boolean {
    return !this.assistedSetupRequested && this.readyAssistTier === 'strong';
  }

  get showDesktopPrivateTestFromInvite(): boolean {
    const js = this.journeyState;
    const alreadySawPrivateProof = !!(
      js?.privateTestEmployeeId ||
      (js?.completedSteps || []).includes('private_proof') ||
      this.privateProofEvidence
    );
    return !!(
      !alreadySawPrivateProof &&
      !this.hasRealFirstEmployee &&
      !this.deviceDetection.isMobileOrTablet() &&
      (js?.inviteFirstFromReady || js?.wantsEmployeeInvite)
    );
  }

  get invitePilotCompanion(): string {
    const js = this.journeyState;
    const alreadySawPrivateProof = !!(
      js?.privateTestEmployeeId ||
      (js?.completedSteps || []).includes('private_proof') ||
      this.privateProofEvidence
    );
    return alreadySawPrivateProof
      ? this.guideCopy.invitePilotBodyAfterPrivate
      : this.guideCopy.invitePilotBody;
  }

  getPrivateTestOs(): DesktopAgentOs {
    // Private test installs on this computer — always follow the current machine,
    // not a stale operating_system left on a reused test employee.
    return this.deviceDetection.detectDesktopAgentOs();
  }

  get privateTestBeforeDownloadCopy(): string {
    switch (this.getPrivateTestOs()) {
      case 'mac':
        return 'A small SuperSee app. macOS may ask for permission — that is expected.';
      case 'linux':
        return 'A small SuperSee app. Linux may ask for your password — that is expected.';
      default:
        return this.guideCopy.beforeDownload;
    }
  }

  get privateTestPermissionHint(): string {
    switch (this.getPrivateTestOs()) {
      case 'mac':
        return 'macOS may warn the file is from the internet. Choose Open — setup continues normally.';
      case 'linux':
        return 'Your system may ask for a password to install. That is expected.';
      default:
        return this.guideCopy.smartScreen;
    }
  }

  get proofCapturePermissionCopy(): string {
    switch (this.getPrivateTestOs()) {
      case 'mac':
        return 'If it still fails, allow SuperSee under Screen & Audio Recording in System Settings.';
      case 'linux':
        return 'If it still fails, screenshot permission or a Wayland session may be blocking capture.';
      default:
        return this.guideCopy.proofCaptureCheckPermission;
    }
  }

  isPrivateTestWindowsFamily(): boolean {
    const os = this.getPrivateTestOs();
    return os === 'windows' || os === 'windows_legacy';
  }

  /** Short OS name for the install confirmation row. */
  get privateTestOsLabel(): string {
    switch (this.getPrivateTestOs()) {
      case 'mac':
        return 'macOS';
      case 'linux':
        return 'Linux';
      case 'windows_legacy':
        return 'Windows 7 & 8';
      default:
        return 'Windows';
    }
  }

  get privateTestDetectedOsCopy(): string {
    return `We detected ${this.privateTestOsLabel} on this computer`;
  }

  get privateTestOsIcon(): string {
    switch (this.getPrivateTestOs()) {
      case 'mac':
        return 'laptop_mac';
      case 'linux':
        return 'terminal';
      default:
        return 'desktop_windows';
    }
  }

  getPrivateTestDownloadLabel(): string {
    if (this.isLoading || this.isDownloading) {
      return 'Preparing your download…';
    }
    if (this.isDownloaded) {
      return 'Download again';
    }
    switch (this.getPrivateTestOs()) {
      case 'mac':
        return 'Download SuperSee for macOS';
      case 'linux':
        return 'Download SuperSee for Linux';
      case 'windows_legacy':
        return 'Download SuperSee for Windows 7 & 8';
      default:
        return 'Download SuperSee for Windows';
    }
  }

  /** Same logic as create employee / reset password: 8–12 chars, upper, lower, number, special. */
  private generateRandomPassword(): string {
    const minLength = 8;
    const maxLength = 12;
    const specialChars = ['@', '!', '$', '%', '^', '&', '*', '(', ')', '_', '+', '=', '-', '|', ':', '<', '>', '?', '{', '}', '[', ']'];
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const allChars = uppercaseChars + lowercaseChars + numbers;

    const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;

    let password = '';
    password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
    password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    const specialChar = specialChars[Math.floor(Math.random() * specialChars.length)];
    const remainingLength = length - 4;
    for (let i = 0; i < remainingLength; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    const middleIndex = Math.floor(password.length / 2);
    password = password.slice(0, middleIndex) + specialChar + password.slice(middleIndex);
    return password;
  }

  // Install Agent
  async downloadAgent(): Promise<void> {
    if (
      this.journeyEnabled &&
      this.currentStepId === 'private_test' &&
      !this.privateTestDownloadRequestedByUser
    ) {
      this.isDownloading = false;
      return;
    }
    this.isDownloading = true;
    // Private-test downloads must follow the mode the hero chose — not a stale
    // version_type left on a reused test employee from an earlier Silent run.
    if (this.journeyEnabled && this.currentStepId === 'private_test') {
      const mode = this.resolvePrivateTestMode();
      const versionFromMode = this.getModeToVersionType(mode);
      this.selectedMode = mode;
      if (this.createdEmployee) {
        this.createdEmployee = { ...this.createdEmployee, version_type: versionFromMode };
      }
      this.activationJourney.setMode(mode);
      if (this.createdEmployee?.id) {
        await this.persistPrivateTestModeBeforeDownload(this.createdEmployee.id, versionFromMode);
      }
    }
    const versionType = this.createdEmployee?.version_type || this.getModeToVersionType(this.selectedMode);
    const osMap: { [key: string]: string } = { windows: 'Windows', windows_legacy: 'Windows legacy', windows_7_8: 'Windows 7 & 8', mac: 'Mac', linux: 'Linux' };
    const osKey =
      this.journeyEnabled && (this.currentStepId === 'private_test' || this.currentStepId === 'mode_education')
        ? this.getPrivateTestOs()
        : this.getCreatedEmployeeOS();
    const os = osMap[osKey] || 'Windows';
    const password = this.resolveInstallerPassword();

    const fileNameOverride = this.buildActivationPackageFileName(
      this.createdEmployee,
      versionType,
      this.journeyEnabled && this.currentStepId === 'first_employee' ? 2 : 1,
      osKey
    );

    // Win 7/8 stay on Electron even when the Win 10/11 Rust beta is enabled.
    const agentRuntime = resolveWindowsAgentRuntime(osKey, sysConfig.rustAgentBetaEnabled);
    // Private try must log in as the test user shown after download, not the
    // SuperSee account already saved on this PC.
    const effectiveInstallerType: 'general' | 'silent' | 'restricted' =
      this.journeyEnabled && password && versionType === 'general'
        ? 'restricted'
        : versionType;
    try {
      if (agentRuntime === 'rust') {
        await this._projectService.downloadRustAgentInstaller(
          this.createdEmployee,
          effectiveInstallerType,
          password,
          fileNameOverride ? { fileNameOverride } : undefined
        );
      } else {
        this._projectService.downloadAgentInstaller(
          this.createdEmployee,
          os,
          effectiveInstallerType,
          password,
          fileNameOverride ? { fileNameOverride } : undefined
        );
      }
    } finally {
      this.lastDownloadedPackageName = fileNameOverride || '';
      this.isDownloading = false;
      this.isDownloaded = true;
      if (this.journeyEnabled) {
        this.activationJourney.trackEvent('installer_downloaded', {
          employeeId: this.createdEmployee?.id,
          mode: versionType,
          fileName: fileNameOverride || null,
          runtime: agentRuntime === 'rust' ? 'rust' : 'standard',
        });
        this.activationJourney.trackEvent('download_package_saved', {
          os: osKey,
          file: fileNameOverride || '',
        });
        this.activationJourney.setStatusPhase('package_prepared');
        this.beginPrivateTestConnectWait();
        this.scheduleHelpNudge();
      }
    }
  }

  private resolveInstallerPassword(): string | undefined {
    if (this.createdEmployeePassword) {
      return this.createdEmployeePassword;
    }
    const saved = this.onboardingService.getCreatedEmployee();
    if (
      saved?.password &&
      saved.employee?.id &&
      this.createdEmployee?.id &&
      Number(saved.employee.id) === Number(this.createdEmployee.id)
    ) {
      return saved.password;
    }
    return undefined;
  }

  private buildActivationPackageFileName(
    employee: any,
    versionType: 'general' | 'silent' | 'restricted',
    sequence: number,
    os?: string
  ): string {
    if (!this.journeyEnabled || !employee?.id) {
      return '';
    }
    const displayName = resolveEmployeeDisplayName(employee, 'Employee');
    const mode: AgentInstallerMode =
      versionType === 'silent' ? 'silent' : versionType === 'restricted' ? 'restricted' : 'general';
    const rust = resolveWindowsAgentRuntime(os, sysConfig.rustAgentBetaEnabled) === 'rust';
    const password = this.resolveInstallerPassword();
    const usesMsiPackage = resolveRustAgentUsesMsiPackage(environment as any);
    const credentialed = !!(employee?.email && password);
    const windowsPackage: 'bat' | 'msi' | 'zip' = !rust
      ? 'bat'
      : !usesMsiPackage
        ? 'bat'
        : rustAgentMsiNeedsSidecar(mode, employee?.email, password) || credentialed
          ? 'zip'
          : 'msi';
    return buildNamedAgentPackageFileName({
      displayName,
      employeeId: employee.id,
      mode: credentialed && mode === 'general' ? 'restricted' : mode,
      sequence,
      os,
      windowsPackage,
    });
  }

  startPollingAgentStatus(): void {
    const watchEmployeeId = this.resolveConnectionWatchEmployeeId();
    if (!watchEmployeeId) {
      return;
    }
    if (this.pollingActive && this.connectionWatchEmployeeId === watchEmployeeId) {
      return;
    }
    if (this.pollingActive) {
      this.stopPolling({ preserveRecovery: true });
    }

    this.clearConnectionTimers();
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }

    this.connectionWatchEmployeeId = watchEmployeeId;
    this.pollingActive = true;
    this.isCheckingStatus = true;
    this.connectionCheckTimedOut = false;
    this.showConnectionRecovery = false;
    this.pollingStartTime = Date.now();
    if (this.currentStepId === 'private_test') {
      const persisted = this.activationJourney.getState()?.connectWaitStartedAt;
      this.connectWaitStartedAt = this.connectWaitStartedAt || persisted || null;
    } else {
      this.connectWaitStartedAt = this.connectWaitStartedAt || Date.now();
    }
    this.syncConnectWaitClock();

    const dbPath = this.tokenEncryptionService.getEncryptedItem<string>('dbPath') as string;
    if (!dbPath) {
      console.error('[Onboarding] dbPath not found, cannot check agent status via Firebase');
      this.isCheckingStatus = false;
      this.pollingActive = false;
      this.connectionWatchEmployeeId = null;
      this.connectionCheckTimedOut = true;
      this.showConnectionRecovery = true;
      this._snackBar.open('Configuration error. Please try again.', 'OK', { duration: 4000 });
      return;
    }

    this.connectionRecoveryTimer = setTimeout(() => {
      if (!this.isAgentOnline) {
        this.showConnectionRecovery = true;
      }
    }, this.CONNECTION_RECOVERY_MS);

    this.connectionTimeoutTimer = setTimeout(() => {
      // Soft timeout: keep Firebase watch alive so an already-running agent can still flip Connected.
      if (this.pollingActive && !this.isAgentOnline) {
        this.connectionCheckTimedOut = true;
        this.showConnectionRecovery = true;
        this.isCheckingStatus = false;
      }
    }, this.POLLING_TIMEOUT_MS);

    const employeeRef = runInInjectionContext(this.injector, () =>
      this.db.object(`${dbPath}/${watchEmployeeId}`)
    );
    this.pollingSubscription = employeeRef.valueChanges().subscribe(
      (user: any) => {
        // First snapshot (including null) ends Checking; keep watching while offline.
        this.isCheckingStatus = false;
        if (this.isAgentOnline) {
          return;
        }

        let isOnline = false;
        if (user?.lastOnlineAtTz) {
          isOnline = this._sharedService.getUserCurrentStatus(user.lastOnlineAtTz, true) === 'online';
        } else if (user?.lastOnlineAt) {
          isOnline = this._sharedService.getUserCurrentStatus(user.lastOnlineAt, false) === 'online';
        }

        if (isOnline) {
          this.onAgentHeartbeatDetected(this.connectWaitSawOffline ? 'watched' : 'already_online');
        } else {
          this.connectWaitSawOffline = true;
        }
      },
      (err) => {
        console.error('Firebase polling error:', err);
        this.isCheckingStatus = false;
        this.connectionCheckTimedOut = true;
        this.showConnectionRecovery = true;
        this.stopPolling({ preserveRecovery: true });
      }
    );
  }

  /** Heartbeat success: clear timers, set Connected, refresh evidence. */
  private onAgentHeartbeatDetected(how: 'watched' | 'already_online' = 'watched'): void {
    this.isAgentOnline = true;
    this.clearHelpNudge();
    this.isCheckingStatus = false;
    this.connectionCheckTimedOut = false;
    this.showConnectionRecovery = false;
    this.stopPolling({ preserveRecovery: false });

    if (this.journeyEnabled) {
      this.activationJourney.setStatusPhase('auth_heartbeat');
      this.activationJourney.markDeviceConnectedFromWatch({
        how,
        employeeId: this.createdEmployee?.id,
        step: this.currentStepId,
      });
      this.connectWaitStartedAt = null;
      this.syncConnectWaitClock();
      const isFirstEmployee =
        this.currentStepId === 'first_employee' && this.hasRealFirstEmployee;
      this.activationReadiness.setEvidence({
        packagePrepared: true,
        privateTestEmployeeId: isFirstEmployee
          ? this.activationJourney.getState().privateTestEmployeeId
          : this.createdEmployee?.id
            ? Number(this.createdEmployee.id)
            : this.activationJourney.getState().privateTestEmployeeId,
        firstEmployeeId: isFirstEmployee ? Number(this.createdEmployee.id) : undefined,
        agentAuthenticated: true,
        recentHeartbeat: true,
        onlineNow: true,
      });
      if (isFirstEmployee) {
        // Persist collecting pause so the next login goes to the dashboard
        // instead of reopening this status tab. Stay on the page this session.
        this.activationJourney.completeFirstEmployee({ reviewReady: false });
        this.syncFirstDayEvidenceRefreshTimer();
      }
    } else {
      const employeeOS = this.createdEmployee?.operating_system || this.employeeForm.os || 'windows';
      this.onboardingService.completeStep('install_agent', { os: employeeOS }).subscribe({
        next: () => {},
        error: () => this.onboardingService.completeStepLocal('install_agent'),
      });
    }
  }

  /**
   * Stop Firebase watch. When preserveRecovery=true (timeout/error), keep recovery flags.
   */
  stopPolling(opts: { preserveRecovery?: boolean } = {}): void {
    this.pollingActive = false;
    this.isCheckingStatus = false;
    this.connectionWatchEmployeeId = null;
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    this.clearConnectionTimers();
    if (!opts.preserveRecovery && !this.isAgentOnline) {
      // Leave recovery flags as-is only when preserveRecovery requested;
      // successful Connected path clears them before calling stopPolling.
    }
    if (!opts.preserveRecovery && this.isAgentOnline) {
      this.connectionCheckTimedOut = false;
      this.showConnectionRecovery = false;
    }
  }

  private clearConnectionTimers(): void {
    if (this.connectionRecoveryTimer) {
      clearTimeout(this.connectionRecoveryTimer);
      this.connectionRecoveryTimer = null;
    }
    if (this.connectionTimeoutTimer) {
      clearTimeout(this.connectionTimeoutTimer);
      this.connectionTimeoutTimer = null;
    }
  }

  private syncConnectWaitClock(): void {
    if (this.connectWaitStartedAt && !this.isAgentOnline) {
      this.connectWaitTick = Date.now();
      if (!this.connectWaitClockSub) {
        this.connectWaitClockSub = interval(1000).subscribe(() => {
          this.connectWaitTick = Date.now();
        });
      }
      return;
    }
    this.stopConnectWaitClock();
  }

  private stopConnectWaitClock(): void {
    if (this.connectWaitClockSub) {
      this.connectWaitClockSub.unsubscribe();
      this.connectWaitClockSub = null;
    }
    this.connectWaitTick = 0;
  }

  private resetConnectionRecoveryFlags(): void {
    this.connectionCheckTimedOut = false;
    this.showConnectionRecovery = false;
  }

  checkAgentStatus(): void {
    this.resetConnectionRecoveryFlags();
    // Force a fresh watch even if we were already polling the wrong/stale target.
    if (this.pollingActive) {
      this.stopPolling({ preserveRecovery: false });
    }
    this.startPollingAgentStatus();
    if (this.journeyEnabled && this.hasRealFirstEmployee && this.isAgentOnline) {
      this.refreshFirstDaySignals();
    }
  }

  // Request Screenshot
  private screenshotSub: any;
  private screenshotTimeout: any;

  requestScreenshot(): void {
    this.proofPreviewFailed = false;
    // If no employee from onboarding flow, fetch existing employees
    if (!this.createdEmployee?.id) {
      // Guard against multiple clicks while we resolve which employee to request from
      if (this.isResolvingScreenshotTarget) {
        return;
      }
      console.log('[Onboarding] No createdEmployee, fetching existing employees');
      this.isResolvingScreenshotTarget = true;
      this._projectService.getUserList(0, 0, true).subscribe((employees: any[]) => {
        this.isResolvingScreenshotTarget = false;
        if (employees && employees.length > 0) {
          // Check if any employee has screenshots already
          const employeeWithScreenshots = employees.find(emp =>
            emp.meta?.last_screenshot ||
            emp.meta?.last_application_name ||
            emp.meta?.screenshot_count > 0
          );
          
          if (employeeWithScreenshots) {
            console.log('[Onboarding] Screenshots already exist, navigating to screenshots page');
            this._snackBar.open('Screenshots already captured! Taking you to the screenshots page.', 'OK', { duration: 3000 });
            // Mark step as complete and navigate to screenshots
            this.onboardingService.completeStep('request_screenshot', { 
              screenshot_request_count: 1,
              existing_screenshots: true 
            }).subscribe();
            this.onboardingService.setPermanentlyCompleted();
            this.router.navigate(['/screenshots']);
            return;
          }
          
          // No screenshots exist, find an online/active employee to request from.
          // Do NOT request from invited/offline employees — it will never deliver and looks broken.
          const onlineEmployee = employees.find(emp => emp?.status === 'using' || emp?.meta?.is_online === true);

          if (!onlineEmployee) {
            this._snackBar.open('No active employee found yet. Please install the agent and log in on an employee device first.', 'OK', {
              duration: 6000
            });
            return;
          }

          this.createdEmployee = onlineEmployee;
          console.log('[Onboarding] Using online employee:', this.createdEmployee);
          
          // Now call requestScreenshot again with the employee set
          this.requestScreenshot();
        } else {
          this._snackBar.open('No employee found. Please create an employee first.', 'OK', { duration: 3000 });
        }
      }, (err) => {
        this.isResolvingScreenshotTarget = false;
        console.error('[Onboarding] Failed to fetch employees:', err);
        this._snackBar.open('Unable to fetch employees. Please try again.', 'OK', { duration: 4000 });
      });
      return;
    }

    // Guard against multiple clicks
    if (this.isRequestingScreenshot) {
      return;
    }

    if (this.journeyEnabled && this.currentStepId === 'private_proof') {
      const privateId = this.journeyState?.privateTestEmployeeId || this.activationJourney.getState().privateTestEmployeeId;
      if (privateId && Number(this.createdEmployee?.id) !== Number(privateId)) {
        this.ensurePrivateTestConnectionTarget(this.activationJourney.getState());
      }
    }

    // If the chosen employee is not active/online, don't send screenshot request.
    // Journey private-proof may use a hydrated stub without status — trust isAgentOnline there.
    const missingOnlineSignals =
      this.createdEmployee?.status &&
      this.createdEmployee.status !== 'using' &&
      this.createdEmployee?.meta?.is_online !== true;
    if (
      missingOnlineSignals &&
      !(this.journeyEnabled && this.currentStepId === 'private_proof' && this.isAgentOnline)
    ) {
      this._snackBar.open('Employee is not online yet. Please install the agent and log in on the employee device, then try again.', 'OK', {
        duration: 6000
      });
      return;
    }

    const dbPath = this.tokenEncryptionService.getEncryptedItem<string>('dbPath') as string;
    if (!dbPath) {
      console.error('[Onboarding] dbPath not found, cannot request screenshot');
      this._snackBar.open('Configuration error. Please try again.', 'OK', { duration: 3000 });
      return;
    }

    this.screenshotFailed = false;
    this.isRequestingScreenshot = true;
    this.screenshotRequestCount++;
    const employeeId = this.createdEmployee.id;
    // Display full name if last name is present, otherwise just first name
    const employeeName =
      this.journeyEnabled && this.currentStepId === 'private_proof'
        ? 'This computer'
        : this.createdEmployee.last_name
          ? `${this.createdEmployee.first_name} ${this.createdEmployee.last_name}`
          : this.createdEmployee.first_name || 'Employee';

    // Clean up any previous subscription/timeout
    if (this.screenshotSub) {
      this.screenshotSub.unsubscribe();
      this.screenshotSub = null;
    }
    if (this.screenshotTimeout) {
      clearTimeout(this.screenshotTimeout);
      this.screenshotTimeout = null;
    }

    // Block messaging toast for this employee only while private capture is in flight
    this._sharedService.beginOnboardingScreenshotCapture(employeeId);
    // Show requesting toast
    this._sharedService.showToast({ type: 'Requesting Screenshot', userName: employeeName });

    let handled = false;

    // Listen directly to the specific employee's Firebase node for the screenshot response
    const employeeRef = runInInjectionContext(this.injector, () => this.db.object(`${dbPath}/${employeeId}`));
    this.screenshotSub = employeeRef.valueChanges().subscribe((user: any) => {
      if (!user || handled) return;

      // Only react when ssRequest is an object with imgURL (agent's response)
      if (user.ssRequest && typeof user.ssRequest === 'object' && user.ssRequest.imgURL) {
        handled = true;

        // Capture data locally before clearing Firebase
        const screenshotData = {
          userName: user.ssRequest.userName || employeeName,
          userId: user.ssRequest.userId || employeeId,
          appName: user.ssRequest.applicationName || '',
          imgURL: user.ssRequest.imgURL
        };

        this.isRequestingScreenshot = false;
        this.isScreenshotCaptured = true;
        this.clearHelpNudge();
        if (this.journeyEnabled && this.currentStepId === 'private_proof') {
          this.proofPreviewFailed = false;
          this.privateProofEvidence = {
            imgURL: screenshotData.imgURL,
            appName: screenshotData.appName || 'Unknown app',
            userName: screenshotData.userName,
            capturedAt: Date.now(),
            screenshotId:
              (user.ssRequest as any)?.screenshotId ||
              (user.ssRequest as any)?.id ||
              (user.ssRequest as any)?.ssId ||
              null,
            uploadAcknowledged: !!(screenshotData.imgURL || (user.ssRequest as any)?.imgURL),
          };
          this.persistPrivateProofEvidence();
          this.syncSetupStepUrl('private_proof');
          this.activationReadiness.setEvidence({
            firstScreenshotReceived: true,
            screenshotPixelsDisplayable: !!screenshotData.imgURL && !this.proofPreviewFailed,
            screenshotCount: Math.max(1, this.activationReadiness.getSnapshot().screenshotCount || 0),
            privateTestEmployeeId: this.createdEmployee?.id
              ? Number(this.createdEmployee.id)
              : this.activationJourney.getState().privateTestEmployeeId,
          });
          this.activationJourney.markPrivateProofViewed({
            appName: screenshotData.appName,
            mode: this.selectedMode,
            screenshotId: this.privateProofEvidence.screenshotId,
          });
        } else if (this.journeyEnabled && (this.currentStepId === 'first_employee' || this.currentStepId === 'first_day_review')) {
          this.firstDaySignals = {
            ...this.firstDaySignals,
            screenshotsCount: (this.firstDaySignals.screenshotsCount || 0) + 1,
            appsCount: this.firstDaySignals.appsCount ?? (screenshotData.appName ? 1 : null),
          };
          this.activationJourney.setStatusPhase('first_data');
        }

        // Unsubscribe and clear timeout
        if (this.screenshotSub) {
          this.screenshotSub.unsubscribe();
          this.screenshotSub = null;
        }
        if (this.screenshotTimeout) {
          clearTimeout(this.screenshotTimeout);
          this.screenshotTimeout = null;
        }

        // Clean up Firebase flags THEN re-enable messaging service
        this.db.database.ref(`${dbPath}`).child(`${employeeId}`).update({ ssRequest: false, isreceive: true }).then(() => {
          this._sharedService.endOnboardingScreenshotCapture();
        });

        this._sharedService.showToast({
          type: 'Request',
          userName: screenshotData.userName,
          userId: screenshotData.userId,
          appName: screenshotData.appName,
          imgURL: screenshotData.imgURL,
          isDemo: true
        });
        this._sharedService.attachOnDemandScreenshotIfPending(
          employeeId,
          screenshotData.imgURL,
          (user.ssRequest as any)?.largeImg
        );

        this.onboardingService.completeStep('request_screenshot', { screenshot_request_count: this.screenshotRequestCount }).subscribe({
          next: () => {
            this.onboardingService.syncStateFromBackend().subscribe({ error: () => {} });
          },
          error: () => this.onboardingService.completeStepLocal('request_screenshot')
        });

        // Classic path: hide checklist permanently once screenshots exist.
        // Journey path: keep the hero on private proof until they confirm the evidence.
        if (!this.journeyEnabled) {
          this.onboardingService.setPermanentlyCompleted();
        }
      }
    });

    // Now send the request: clear stale data then trigger agent
    this.db.database
      .ref(`${dbPath}`)
      .child(`${employeeId}`)
      .update({ ssRequest: false, isreceive: true })
      .then(() => {
        this._sharedService.startOnDemandScreenshotRequest({
          employeeId,
          dbPath,
          source: 'onboarding',
        });
        return Promise.resolve();
      })
      .then(() => { })
      .catch((err) => {
        console.error('[Onboarding] Error sending screenshot request:', err);
        this.isRequestingScreenshot = false;
        this.screenshotFailed = true;
        this._sharedService.endOnboardingScreenshotCapture();
        if (this.screenshotSub) {
          this.screenshotSub.unsubscribe();
          this.screenshotSub = null;
        }
        if (!(this.journeyEnabled && this.currentStepId === 'private_proof')) {
          this._snackBar.open('Failed to request screenshot. Please try again.', 'OK', { duration: 3000 });
        }
      });

    // Timeout after 30 seconds — end the spin honestly; never leave the hero locked.
    this.screenshotTimeout = setTimeout(() => {
      if (this.isRequestingScreenshot && !handled) {
        this.isRequestingScreenshot = false;
        this._sharedService.endOnboardingScreenshotCapture();
        if (this.screenshotSub) {
          this.screenshotSub.unsubscribe();
          this.screenshotSub = null;
        }

        if (this.maybeAutoRetryScreenshot()) {
          return;
        }

        this.screenshotFailed = true;
        if (!(this.journeyEnabled && this.currentStepId === 'private_proof')) {
          if (this.screenshotRequestCount < 2) {
            this._snackBar.open('Screenshot request timed out. Make sure the agent is running.', 'OK', {
              duration: 5000,
            });
          }
        }
      }
    }, 30000);
  }

  viewScreenshots(): void {
    window.location.href = '/screenshots';
  }

  contactScreenshotSupport(): void {
    window.open('https://api.whatsapp.com/send/?phone=916354719703&text=Hi%2C+Screenshots+not+getting+captured.&type=phone_number&app_absent=0', '_blank');
  }

  toggleSmartScreenPreview(): void {
    this.smartScreenPreviewOpen = !this.smartScreenPreviewOpen;
    if (this.smartScreenPreviewOpen && !this.hasTrackedSmartScreenPreview) {
      this.hasTrackedSmartScreenPreview = true;
      this.activationJourney.trackEvent('smartscreen_visual_opened');
    }
  }

  toggleBrowserKeepGuidance(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.browserKeepGuidanceOpen = !this.browserKeepGuidanceOpen;
    // Defer analytics so state$ side effects cannot fight the open animation/layout.
    if (this.browserKeepGuidanceOpen && !this.hasTrackedBrowserKeepGuidance) {
      this.hasTrackedBrowserKeepGuidance = true;
      setTimeout(() => {
        try {
          this.activationJourney.trackEvent('browser_keep_guidance_opened');
        } catch {
          /* analytics is best-effort */
        }
      }, 0);
    }
  }

  toggleInstallOpenHelp(): void {
    this.installOpenHelpOpen = !this.installOpenHelpOpen;
    if (this.installOpenHelpOpen) {
      this.activationJourney.trackEvent('install_open_help_opened');
      if (this.isPrivateTestWindowsFamily() && !this.hasTrackedBrowserKeepGuidance) {
        this.hasTrackedBrowserKeepGuidance = true;
        setTimeout(() => {
          try {
            this.activationJourney.trackEvent('browser_keep_guidance_opened');
          } catch {
            /* analytics is best-effort */
          }
        }, 0);
      }
    }
  }

  openWhatYouControl(): void {
    this.activationJourney.trackEvent('data_transparency_opened');
    this.dialog.open(WhatYouControlDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      autoFocus: false,
    });
  }

  saveWorkspaceName(): void {
    this.commitWorkspaceName();
  }

  beginWorkspaceNameEdit(): void {
    if (this.workspaceNameStatus === 'saving') {
      return;
    }
    this.workspaceName = this.workspaceNameSaved;
    this.workspaceNameEditing = true;
    this.workspaceNameStatus = 'idle';
    setTimeout(() => this.workspaceNameInput?.nativeElement?.focus(), 0);
  }

  cancelWorkspaceNameEdit(): void {
    this.workspaceName = this.workspaceNameSaved;
    this.workspaceNameEditing = true;
    this.workspaceNameStatus = 'idle';
  }

  commitWorkspaceNameFromEdit(): void {
    this.workspaceNameEditing = true;
    const trimmed = String(this.workspaceName || '').trim();
    if (trimmed === String(this.workspaceNameSaved || '').trim() && this.isWorkspacePromptCompleted()) {
      this.workspaceNameStatus = 'idle';
      return;
    }
    this.commitWorkspaceName({ leaveEditingOnSuccess: true });
  }

  /**
   * Persist optional workspace name silently.
   * No success snackbar — Ready should feel owned, not like a settings form.
   */
  commitWorkspaceName(options?: { leaveEditingOnSuccess?: boolean }): void {
    if (this.workspaceNameStatus === 'saving') {
      return;
    }
    const trimmed = String(this.workspaceName || '').trim();
    const previous = String(this.workspaceNameSaved || '').trim();
    const nameChanged = trimmed !== previous;
    if (!nameChanged && this.isWorkspacePromptCompleted()) {
      if (options?.leaveEditingOnSuccess) {
        this.workspaceNameEditing = true;
      }
      return;
    }

    const meta: Record<string, string> = {
      ss_workspace_prompt_completed: '1',
    };
    if (trimmed) {
      meta.company_name = trimmed;
    }
    this.workspaceNameStatus = 'saving';
    this.userService.updateUser({ meta }).subscribe({
      next: () => {
        const current = this.userService.getCurrentUserValue() || this.onboardingService.getCurrentUser() || {};
        const nextMeta = { ...(current.meta || {}), ...meta };
        if (!trimmed) {
          delete nextMeta.company_name;
        }
        const merged = {
          ...current,
          meta: nextMeta,
        };
        this.userService.setCurrentUser(merged);
        this.workspaceNameSaved = trimmed;
        this.workspaceName = trimmed;
        if (nameChanged) {
          this.activationJourney.trackEvent('workspace_named_inline', { named: !!trimmed });
        }
        if (options?.leaveEditingOnSuccess !== false) {
          // Ready uses always-visible inputs; keep edit mode so blur-save keeps working.
          this.workspaceNameEditing = true;
        }
        this.workspaceNameStatus = 'saved';
        if (this.workspaceNameSavedStatusTimer) {
          clearTimeout(this.workspaceNameSavedStatusTimer);
        }
        this.workspaceNameSavedStatusTimer = setTimeout(() => {
          if (this.workspaceNameStatus === 'saved') {
            this.workspaceNameStatus = 'idle';
          }
          this.workspaceNameSavedStatusTimer = null;
        }, 1200);
      },
      error: () => {
        this.workspaceName = previous;
        this.workspaceNameStatus = 'error';
        this._snackBar.open('Could not save workspace name. Try again.', 'OK', { duration: 4000 });
      },
    });
  }

  onTeamSizeSelected(value: string): void {
    this.teamSize = value == null ? '' : String(value);
    this.commitTeamSize();
  }

  /**
   * Persist team size silently. Required before Continue; empty clears meta.
   */
  commitTeamSize(): void {
    if (this.teamSizeStatus === 'saving') {
      return;
    }
    const next = normalizeTeamSizeForSelect(this.teamSize);
    const previous = String(this.teamSizeSaved || '');
    if (next === previous) {
      this.teamSize = next;
      return;
    }
    this.teamSizeStatus = 'saving';
    this.userService.updateUser({ meta: { teamSize: next } }).subscribe({
      next: () => {
        const current = this.userService.getCurrentUserValue() || this.onboardingService.getCurrentUser() || {};
        const nextMeta = { ...(current.meta || {}), teamSize: next };
        if (!next) {
          delete nextMeta.teamSize;
        }
        this.userService.setCurrentUser({
          ...current,
          meta: nextMeta,
        });
        this.teamSizeSaved = next;
        this.teamSize = next;
        if (next) {
          this.activationJourney.trackEvent('team_size_selected', { band: next });
        } else {
          this.activationJourney.trackEvent('team_size_cleared');
        }
        this.teamSizeStatus = 'saved';
        if (this.teamSizeSavedStatusTimer) {
          clearTimeout(this.teamSizeSavedStatusTimer);
        }
        this.teamSizeSavedStatusTimer = setTimeout(() => {
          if (this.teamSizeStatus === 'saved') {
            this.teamSizeStatus = 'idle';
          }
          this.teamSizeSavedStatusTimer = null;
        }, 1200);
      },
      error: () => {
        this.teamSizeStatus = 'error';
        this._snackBar.open('Could not save team size. Try again.', 'OK', { duration: 4000 });
      },
    });
  }

  beginContactPhoneEdit(): void {
    if (this.contactPhoneStatus === 'saving') {
      return;
    }
    this.contactPhone = this.contactPhoneSaved;
    this.contactPhoneEditing = true;
    this.contactPhoneStatus = 'idle';
    this.readyContactPhoneStrip?.focusInput();
  }

  cancelContactPhoneEdit(): void {
    this.contactPhone = this.contactPhoneSaved;
    this.contactPhoneStatus = 'idle';
    this.contactPhoneEditing = true;
    this.readyContactPhoneStrip?.cancelEdit();
  }

  commitContactPhoneFromEdit(): void {
    this.contactPhoneEditing = true;
    const trimmed = String(this.contactPhone || '').trim();
    const previous = String(this.contactPhoneSaved || '').trim();
    if (trimmed === previous) {
      this.contactPhoneStatus = 'idle';
      return;
    }
    this.commitContactPhone({ leaveEditingOnSuccess: true });
  }

  /**
   * Contact capture — writes meta.phone_number only via ContactPhoneCaptureService.
   * No OTP. Soft-insist only (never a hard gate).
   */
  commitContactPhone(options?: { leaveEditingOnSuccess?: boolean; source?: 'ready' | 'private_proof' | 'fair_setup' }): void {
    if (this.contactPhoneStatus === 'saving') {
      return;
    }
    const trimmed = String(this.contactPhone || '').trim();
    const previous = String(this.contactPhoneSaved || '').trim();
    if (trimmed === previous) {
      if (options?.leaveEditingOnSuccess) {
        this.contactPhoneEditing = !trimmed;
      }
      this.contactPhoneStatus = 'idle';
      return;
    }
    if (trimmed && !this.contactPhoneCapture.isValidPhone(trimmed)) {
      this.contactPhoneStatus = 'invalid';
      return;
    }

    const source = options?.source || 'ready';
    this.contactPhoneStatus = 'saving';
    this.contactPhoneCapture.savePhone(trimmed, source).subscribe({
      next: () => {
        this.contactPhoneSaved = trimmed;
        this.contactPhone = trimmed;
        if (trimmed) {
          this.contactPhoneNudgeVisible = false;
        }
        if (options?.leaveEditingOnSuccess !== false) {
          this.contactPhoneEditing = true;
        }
        this.contactPhoneStatus = 'saved';
        if (this.contactPhoneSavedStatusTimer) {
          clearTimeout(this.contactPhoneSavedStatusTimer);
        }
        this.contactPhoneSavedStatusTimer = setTimeout(() => {
          if (this.contactPhoneStatus === 'saved') {
            this.contactPhoneStatus = 'idle';
          }
          this.contactPhoneSavedStatusTimer = null;
        }, 1200);
      },
      error: (err) => {
        if (err?.message === 'invalid_phone') {
          this.contactPhoneStatus = 'invalid';
          return;
        }
        this.contactPhoneStatus = 'error';
        this._snackBar.open('Could not save mobile number. Try again.', 'OK', { duration: 4000 });
      },
    });
  }

  onReadyContactPhoneSaved(phone: string): void {
    this.contactPhoneSaved = phone;
    this.contactPhone = phone;
    if (phone) {
      this.contactPhoneNudgeVisible = false;
    }
  }

  onReadyContactPhoneDraft(phone: string): void {
    this.contactPhone = phone;
    this.contactPhoneEditing = true;
  }

  onProofContactPhoneSaved(phone: string): void {
    this.contactPhoneSaved = phone;
    this.contactPhone = phone;
    this.proofContactPhoneDismissed = false;
  }

  onProofContactPhoneSkipped(): void {
    this.proofContactPhoneDismissed = true;
  }

  onFairContactPhoneSaved(phone: string): void {
    this.contactPhoneSaved = phone;
    this.contactPhone = phone;
    this.fairContactPhoneDismissed = false;
  }

  onFairContactPhoneSkipped(): void {
    this.fairContactPhoneDismissed = true;
  }

  get showProofContactPhone(): boolean {
    return (
      !this.contactPhoneDisplay &&
      !this.proofContactPhoneDismissed &&
      (!!this.isScreenshotCaptured || !!this.privateProofEvidence)
    );
  }

  get showFairContactPhone(): boolean {
    return !this.contactPhoneDisplay && !this.fairContactPhoneDismissed;
  }

  continueWithoutContactPhone(): void {
    this.contactPhoneNudgeDismissed = true;
    this.contactPhoneNudgeVisible = false;
    this.activationJourney.trackEvent('contact_phone_nudge_skipped');
  }

  /**
   * Soft-insist: first Ready CTA without a number pauses on the contact beat.
   * Never OTP. Never a permanent wall — user can continue without.
   */
  private maybeNudgeContactPhone(): boolean {
    const draft = String(this.contactPhone || '').trim();
    const hasPhone =
      !!String(this.contactPhoneSaved || '').trim() ||
      (!!draft && this.contactPhoneCapture.isValidPhone(draft));
    if (hasPhone || this.contactPhoneNudgeDismissed) {
      this.contactPhoneNudgeVisible = false;
      return false;
    }
    if (this.contactPhoneNudgeVisible) {
      return true;
    }
    this.contactPhoneNudgeVisible = true;
    this.contactPhoneCapture.trackNudgeShown('ready');
    this.activationJourney.trackEvent('contact_phone_nudge_shown');
    this.ensureReadyContactCaptureOpen(true);
    return true;
  }

  /** Keep the phone input ready for capture / focus on nudge. */
  private ensureReadyContactCaptureOpen(focus = false): void {
    this.contactPhoneEditing = true;
    if (focus) {
      this.readyContactPhoneStrip?.focusInput();
    }
  }

  private isContactPhoneValid(value: string): boolean {
    return this.contactPhoneCapture.isValidPhone(value);
  }

  private normalizeStoredPhone(value: unknown): string {
    return this.contactPhoneCapture.normalizePhone(value);
  }

  private maybeTrackWorkspacePromptShown(): void {
    if (this.workspacePromptShownTracked) {
      return;
    }
    if (this.currentStepId !== 'goal_selected' && this.currentStepId !== 'plan_viewed') {
      return;
    }
    this.workspacePromptShownTracked = true;
    this.ensureReadyContactCaptureOpen();
    this.activationJourney.trackEvent('workspace_prompt_shown');
  }

  openInstallSupport(): void {
    const osLabel = this.getPrivateTestOs() || 'windows';
    const minutesWaiting = this.connectWaitStartedAt
      ? Math.max(0, Math.round((Date.now() - this.connectWaitStartedAt) / 60000))
      : 0;
    const chip = this.installRecoveryBranch || 'none';
    this.activationJourney.trackEvent('install_help_opened', { os: osLabel, chip });
    this._sharedService.openCreateRequestDialog({
      type: 'support',
      source: 'onboarding_installation_help',
      subject: `Help connecting SuperSee on ${osLabel}`,
      description:
        `I'm facing an issue during SuperSee installation on ${osLabel}. ` +
        `Selected help: ${chip}. Waiting about ${minutesWaiting} minute(s). ` +
        'The device has not connected yet. Please help me complete the installation.',
    });
  }

  private maybeAutoRetryScreenshot(): boolean {
    if (
      this.journeyEnabled &&
      this.currentStepId === 'private_proof' &&
      this.screenshotRequestCount === 1 &&
      !this.screenshotAutoRetried
    ) {
      this.screenshotAutoRetried = true;
      this.screenshotFailed = false;
      this.activationJourney.trackEvent('screenshot_auto_retry');
      this.screenshotRetryTimeout = setTimeout(() => {
        this.screenshotRetryTimeout = null;
        this.requestScreenshot();
      }, 5000);
      return true;
    }
    return false;
  }

  private scheduleHelpNudge(): void {
    this.clearHelpTimer();
    if (this.isAgentOnline || this.isScreenshotCaptured || this.helpNudgeShown) {
      return;
    }
    this.helpNudgeTimeoutId = setTimeout(() => {
      if (this.isAgentOnline || this.isScreenshotCaptured || this.helpNudgeShown) {
        return;
      }
      this.helpNudgeShown = true;
      this.installHelpNudged = true;
      this.activationJourney.trackEvent('install_help_shown');
      if (!this._snackBar?.open) {
        return;
      }
      this.helpNudgeSnackRef = this._snackBar.open(
        'Still waiting for this computer to connect?',
        'Get help',
        { duration: 0 }
      );
      this.helpNudgeSnackRef?.onAction?.().subscribe(() => this.openInstallSupport());
      this.helpNudgeSnackRef?.afterDismissed?.().subscribe((info) => {
        if (!info.dismissedByAction) {
          this.activationJourney.trackEvent('install_help_dismissed');
        }
      });
    }, this.HELP_NUDGE_DELAY_MS);
  }

  private clearHelpTimer(): void {
    if (this.helpNudgeTimeoutId) {
      clearTimeout(this.helpNudgeTimeoutId);
      this.helpNudgeTimeoutId = null;
    }
  }

  private clearHelpNudge(): void {
    this.clearHelpTimer();
    this.helpNudgeSnackRef?.dismiss();
    this.helpNudgeSnackRef = undefined;
    this.installHelpNudged = false;
  }

  isLinuxEmployee(): boolean {
    return this.getCreatedEmployeeOS() === 'linux';
  }


  // Step access control
  isStepAccessible(stepId: string): boolean {
    const stepIndex = this.state?.steps.findIndex(s => s.id === stepId);
    if (stepIndex <= 0) return true;
    // All previous steps must be completed
    return this.state.steps.slice(0, stepIndex).every(s => s.completed);
  }

  // Navigation
  goToStep(stepId: string): void {
    if (!this.isStepAccessible(stepId)) {
      this._snackBar.open('Please complete the previous steps first', 'OK', { duration: 3000 });
      return;
    }
    const index = this.state.steps.findIndex(s => s.id === stepId);
    if (index !== -1) {
      this.onboardingService.goToStep(index);
    }
  }

  goBack(): void {
    if (this.journeyEnabled) {
      this.activationJourney.goBack();
      return;
    }
    const currentIndex = this.state?.steps.findIndex(s => s.id === this.currentStepId);
    if (currentIndex > 0) {
      const previousStep = this.state.steps[currentIndex - 1];
      this.onboardingService.goToStep(currentIndex - 1);
      this.currentStepId = previousStep.id;
    }
  }

  finishOnboarding(): void {
    if (this.journeyEnabled) {
      this.activationJourney.finishJourney('dashboard');
    }
    // Mark onboarding as permanently completed
    localStorage.setItem('supersee_onboarding_completed', 'true');
    
    // Clear persisted employee data — onboarding is done
    this.onboardingService.clearCreatedEmployee();
    
    // Hide the widget permanently
    this.onboardingService.hideForSession();
    
    // Force the sidebar to show when navigating to dashboard
    this._sharedService.isOpenCloseSidebar.next({ force: true });
    this.router.navigate(['/dashboard']);
  }

  // Support
  bookDemo(): void {
    window.open('https://calendly.com/raoinformationtechnology/supersee-demo', '_blank');
  }

  bookAssistedSetup(): void {
    if (this.assistedSetupRequested) {
      return;
    }
    const band = String(this.teamSizeSaved || '');
    this.activationJourney.trackEvent('assisted_setup_clicked', {
      band,
      surface: this.currentStepId === 'plan_viewed' ? 'understand' : 'ready',
    });
    const user = this.userService.getCurrentUserValue() || this.onboardingService.getCurrentUser() || {};
    this.dialog.open(JourneyAssistedSetupDialogComponent, {
      data: {
        contactName: String(user.name || this.userName || '').trim(),
        contactEmail: String(user.email || this.userEmail || '').trim(),
        contactPhone: String(this.contactPhoneSaved || user.meta?.phone_number || ''),
        workspaceName: String(this.workspaceNameSaved || user.meta?.company_name || ''),
        teamSizeBand: band,
      },
      autoFocus: false,
      panelClass: 'journey-assisted-setup-panel',
    }).afterClosed().subscribe((ok) => {
      if (!ok) {
        return;
      }
      const latest: any = this.userService.getCurrentUserValue() || {};
      const savedPhone = String(latest?.meta?.phone_number || '').trim();
      if (savedPhone) {
        this.contactPhone = savedPhone;
        this.contactPhoneSaved = savedPhone;
      }
      this.assistedSetupRequested = true;
      this.persistAssistedSetupRequestedFlag(user);
      this.activationJourney.trackEvent('assisted_setup_submitted', {
        band,
        surface: this.currentStepId === 'plan_viewed' ? 'understand' : 'ready',
      });
    });
  }

  private assistedSetupStorageKey(user?: any): string {
    const id = user?.id || user?.userId || this.userService.getCurrentUserValue()?.id || 'anon';
    return `ss_assisted_setup_requested_${id}`;
  }

  private readAssistedSetupRequestedFlag(user?: any): boolean {
    try {
      return localStorage.getItem(this.assistedSetupStorageKey(user)) === '1';
    } catch {
      return false;
    }
  }

  private persistAssistedSetupRequestedFlag(user?: any): void {
    try {
      localStorage.setItem(this.assistedSetupStorageKey(user), '1');
    } catch {
      /* ignore quota / private mode */
    }
  }

  contactSupport(): void {
    window.open('https://api.whatsapp.com/send/?phone=916354719703&text=Hi%2C+I+need+help+in+onboarding.&type=phone_number&app_absent=0', '_blank');
  }

  /** Dev/ops: wipe journey progress and return to Ready. Does not remove the local agent or delete retained captures. */
  restartActivationJourney(): void {
    if (!this.journeyEnabled) {
      return;
    }
    const confirmed = window.confirm(
      'Reset setup progress on this account and return to Ready?\n\n' +
        'This does not uninstall SuperSee from this computer, and does not delete screenshots already saved in your account.\n' +
        'Use “Remove from this computer” for the local app, and Keep/Delete private-test data when adding an employee.'
    );
    if (!confirmed) {
      return;
    }
    this.isLoading = true;
    this.activationJourney.resetFromServer().subscribe({
      next: () => {
        this.isLoading = false;
        this.onboardingService.clearPermanentlyCompleted();
        this.createdEmployee = null;
        this.createdEmployeePassword = '';
        this.privateProofEvidence = null;
        this.proofPreviewFailed = false;
        this.isScreenshotCaptured = false;
        try {
          sessionStorage.removeItem('supersee_private_proof_evidence');
        } catch {
          /* ignore */
        }
        this.isDownloaded = false;
        this.isAgentOnline = false;
        this.setupMethod = null;
        this.authorisedUseConfirmed = false;
        this.showProofObjections = false;
        this.showProofMore = false;
        this.showFairSetupBasics = false;
        this.showFairSetupMore = false;
        this.showMissingSetupFileHelp = false;
        this.cleanupDecisionSaving = null;
        this.cleanupDecisionError = '';
        this.showFirstDayObjections = false;
        this.showProofBounds = false;
        this.showSampleWorkday = false;
        this.activationReadiness.clearEvidence();
        this.stopPolling();
        this.onboardingService.clearCreatedEmployee();
        this.activationJourney.goToStep('goal_selected');
        this.activationJourney.beginIntentionalReentry('restart');
        void this.activationNavigation.navigateSetupUrl(['/setup', 'ready'], {
          queryParams: { from: 'restart' },
          replaceUrl: true,
        });
        this._snackBar.open('Journey progress reset. Begin from the ready screen.', 'OK', { duration: 3500 });
      },
      error: () => {
        this.isLoading = false;
        this.activationJourney.reset();
        this.onboardingService.clearPermanentlyCompleted();
        this.createdEmployee = null;
        this.createdEmployeePassword = '';
        this.privateProofEvidence = null;
        this.onboardingService.clearCreatedEmployee();
        this.activationJourney.goToStep('goal_selected');
        this.activationJourney.beginIntentionalReentry('restart');
        void this.activationNavigation.navigateSetupUrl(['/setup', 'ready'], {
          queryParams: { from: 'restart' },
          replaceUrl: true,
        });
        this._snackBar.open('Journey restarted locally. Server reset may have failed.', 'OK', { duration: 4000 });
      },
    });
  }

  logoutUser(): void {
    this._authService.performLogout();
  }

  skipOnboarding(): void {
    Swal.fire({
      title: 'Skip setup?',
      html: `<p>You can come back to setup later from the dashboard.</p><div class="ss-impact"><strong>Not set up yet</strong>Employee tracking, the desktop app, and productivity insights stay unfinished.</div>`,
      showCancelButton: true,
      confirmButtonColor: '#32357d',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Skip setup',
      cancelButtonText: 'Continue setup',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        if (this.journeyEnabled) {
          this.activationJourney.skipJourney();
        }
        // Mark onboarding as skipped
        this.onboardingService.skipOnboarding();
        // Set timestamp to help guard detect recent skip action
        const timestamp = Date.now().toString();
        localStorage.setItem('onboardingSkippedTimestamp', timestamp);
        // Force the sidebar to show when navigating to dashboard
        this._sharedService.isOpenCloseSidebar.next({ force: true });
        // Navigate to dashboard
        this.router.navigate(['/dashboard']);
      }
    });
  }

  // --- Activation Journey helpers ---

  /** @deprecated Mode radios removed from Ready / Understand. */
  setPrivateTestVisibility(value: 'visible' | 'hidden'): void {
    this.privateTestVisibility = value === 'hidden' ? 'hidden' : 'visible';
  }

  get canShowHiddenPrivateTest(): boolean {
    const hidden = (this.modes || []).find((mode) => mode.id === 'hidden');
    return !!hidden && !hidden.locked;
  }

  /** Open SuperSee Setup uses the Hidden wait copy only after Collection chose Hidden. */
  get privateTestIsHidden(): boolean {
    return this.onboardingChoseHidden();
  }

  get canContinueFromReady(): boolean {
    return (
      String(this.workspaceName || '').trim().length > 0 &&
      isKnownTeamSize(this.teamSize || this.teamSizeSaved)
    );
  }

  get showEmployeeVisibilityNudge(): boolean {
    const js = this.journeyState || this.activationJourney.getState();
    if (!js || js.employeeVisibilityNudgeShown) {
      return false;
    }
    if (!this.hasRealFirstEmployee || !this.isAgentOnline) {
      return false;
    }
    const firstId = js.firstEmployeeId || this.createdEmployee?.id;
    if (!firstId || firstId === js.privateTestEmployeeId) {
      return false;
    }
    const versionType =
      this.createdEmployee?.version_type ||
      this.getModeToVersionType(js.firstEmployeeMode || this.selectedMode || 'employee_controlled');
    return versionType !== 'silent';
  }

  /** Dynamic first-employee / adjust-mode note from the private-test mode that was used. */
  get privateTestModeEducationNote(): string {
    return this.activationJourney.modeEducationNoteFor(
      this.journeyState?.mode || this.activationJourney.getState().mode
    );
  }

  /** Ready Continue → Understand (`plan_viewed`). Phone is soft-nudge only — never a hard gate. */
  continueFromReadyToUnderstand(): void {
    if (!this.canContinueFromReady) {
      return;
    }
    if (this.maybeNudgeContactPhone()) {
      return;
    }
    if (
      String(this.workspaceName || '').trim() !== String(this.workspaceNameSaved || '').trim() ||
      !this.isWorkspacePromptCompleted()
    ) {
      this.commitWorkspaceName({ leaveEditingOnSuccess: true });
    }
    this.readyContactPhoneStrip?.flush();
    if (String(this.contactPhone || '').trim() !== String(this.contactPhoneSaved || '').trim()) {
      this.commitContactPhone({ leaveEditingOnSuccess: true, source: 'ready' });
    }
    this.raiseReentryWalkCeiling('plan_viewed');
    this.holdUiStep('plan_viewed');
    this.cdr.markForCheck();
    this.syncSetupStepUrl('plan_viewed');
    this.journeyFreezeLog('continueFromReady_forced_purpose');
    try {
      this.activationJourney.advanceReadyToUnderstand();
    } finally {
      this.holdUiStep('plan_viewed');
      this.currentStepId = 'plan_viewed';
      this.cdr.markForCheck();
    }
  }

  /**
   * Understand secondary. Pause on every visit. The second click, while the
   * note is open, opens the Example workday. A saved Continue-later flag must
   * not skip this.
   */
  lookAroundFromUnderstand(): void {
    if (!this.privateTestExitNudgeVisible) {
      this.privateTestExitNudgeVisible = true;
      this.activationJourney.trackEvent('private_test_exit_nudge_shown', { source: 'understand' });
      return;
    }
    this.privateTestExitNudgeVisible = false;
    this.activationJourney.trackEvent('private_test_exit_nudge_skipped', { source: 'understand' });
    this.activationJourney.trackEvent('continued_later', {
      step: 'plan_viewed',
      source: 'understand',
      path: 'look_around_example',
    });
    this.exampleWorkday.openFromUnderstand();
  }

  dismissVisibilitySwitchTeach(): void {
    this.activationJourney.markVisibilitySwitchTaught();
  }

  dismissEmployeeVisibilityNudge(): void {
    this.activationJourney.markEmployeeVisibilityNudgeShown();
  }

  /** Preview “see it” → Install. Hidden/Automatic keep their installer; Manual stays Visible. */
  startPrivateTestFromReady(): void {
    this.showCaptureFacts = false;
    this.resetPrivateTestDownloadUi();
    const mode = this.resolvePrivateTestMode();
    this.selectedMode = mode;
    this.activationJourney.startPrivateTestFromReady(mode as 'employee_controlled' | 'hidden' | 'admin_controlled');
  }

  /** Ready must never start a file download, including a leftover click on the next CTA. */
  private resetPrivateTestDownloadUi(): void {
    this.isDownloaded = false;
    this.isDownloading = false;
    this.lastDownloadedPackageName = '';
    this.privateTestDownloadRequestedByUser = false;
    this.connectWaitStartedAt = null;
    this.connectWaitSawOffline = false;
    this.syncConnectWaitClock();
    this.activationJourney.clearConnectWait();
    this.installDownloadCtaLocked = true;
    if (this.installDownloadCtaUnlockTimer) {
      clearTimeout(this.installDownloadCtaUnlockTimer);
    }
    this.installDownloadCtaUnlockTimer = setTimeout(() => {
      this.installDownloadCtaLocked = false;
      this.installDownloadCtaUnlockTimer = null;
    }, 400);
  }

  skipModeEducationToInstall(): void {
    this.selectedMode = this.selectedMode || 'employee_controlled';
    this.activationJourney.setMode(this.selectedMode);
    this.activationJourney.completeModeEducation();
  }

  toggleCaptureFacts(): void {
    this.showCaptureFacts = !this.showCaptureFacts;
    if (this.showCaptureFacts) {
      this.activationJourney.trackEvent('quiet_link_capture_facts');
    }
  }

  togglePrivateTestLogin(): void {
    this.privateTestLoginOpen = !this.privateTestLoginOpen;
    if (this.privateTestLoginOpen) {
      this.activationJourney.trackEvent('private_test_login_details_opened');
    }
  }

  /** Expand desktop login as soon as Download starts so paste is ready if SuperSee asks. */
  private openPrivateTestLoginForDownload(): void {
    if (this.privateTestLoginOpen) {
      return;
    }
    this.privateTestLoginOpen = true;
    this.activationJourney.trackEvent('private_test_login_details_opened', { source: 'download' });
  }

  toggleInstallAntivirusTip(): void {
    this.installAntivirusOpen = !this.installAntivirusOpen;
    if (this.installAntivirusOpen) {
      this.activationJourney.trackEvent('install_antivirus_tip_opened');
    }
  }

  toggleRemovalHow(): void {
    this.showRemovalHow = !this.showRemovalHow;
    if (this.showRemovalHow) {
      this.showCaptureFacts = false;
      this.activationJourney.trackEvent('quiet_link_removal_how');
    }
  }

  get activePreview(): (typeof JOURNEY_GUIDE_COPY.outcomes)[number] | undefined {
    return this.guideCopy.outcomes.find((o) => o.id === this.selectedPreviewId);
  }

  selectPreview(id: (typeof JOURNEY_GUIDE_COPY.outcomes)[number]['id']): void {
    this.selectedPreviewId = id;
    this.activationJourney.trackEvent('ready_preview_selected', {
      preview: id,
      data_source: 'inline_example',
      variant: id === 'trends' ? 'signals_deepwork_v1' : 'v1',
    });
  }

  /** Tertiary path after private proof — scoped guided route, not global sample mode. */
  openExampleWorkday(): void {
    this.showProofObjections = false;
    this.persistPrivateProofEvidence();
    this.activationJourney.trackEvent('proof_sample_workday_opened', {
      data_source: 'scoped_example',
    });
    this.exampleWorkday.openGuidedExample({ fromProof: true });
  }

  /** Ready secondary CTA — full Example workday (not an activation milestone). */
  openExampleFromReady(): void {
    this.showCaptureFacts = false;
    this.activationJourney.trackEvent('ready_example_workday_opened', {
      data_source: 'scoped_example',
      entry: 'ready',
    });
    this.exampleWorkday.openFromReady();
  }

  /** @deprecated Inline drawer removed — use openExampleWorkday(). */
  toggleSampleWorkday(): void {
    this.openExampleWorkday();
  }

  private persistPrivateProofEvidence(): void {
    if (!this.privateProofEvidence) {
      return;
    }
    try {
      sessionStorage.setItem(
        'supersee_private_proof_evidence',
        JSON.stringify(this.privateProofEvidence)
      );
    } catch {
      /* ignore */
    }
  }

  private restorePrivateProofEvidence(): void {
    if (this.privateProofEvidence) {
      this.isScreenshotCaptured = true;
      return;
    }
    try {
      const raw = sessionStorage.getItem('supersee_private_proof_evidence');
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.imgURL || parsed?.capturedAt) {
        this.privateProofEvidence = parsed;
        this.isScreenshotCaptured = true;
      }
    } catch {
      /* ignore */
    }
  }

  chooseProofForkPrivateReview(): void {
    this.activationJourney.trackEvent('proof_fork_chosen', { path: 'private_review' });
    this.keepTestingOnThisComputer();
  }

  chooseProofForkOneEmployee(): void {
    this.activationJourney.trackEvent('proof_fork_chosen', { path: 'one_employee' });
    if (!this.isScreenshotCaptured && !this.privateProofEvidence) {
      this._snackBar.open('Capture a private screenshot first so you can see what SuperSee collected.', 'OK', {
        duration: 4000,
      });
      return;
    }
    this.activationJourney.completePrivateProofThenInvite();
  }

  toggleProofBounds(): void {
    this.showProofBounds = !this.showProofBounds;
    if (this.showProofBounds) {
      this.activationJourney.markFairSetupAckSeen('proof_bounds');
    }
  }

  toggleProofObjections(): void {
    this.showProofObjections = !this.showProofObjections;
    if (this.showProofObjections) {
      this.activationJourney.markFairSetupAckSeen('proof_concerns');
    }
  }

  keepTestingOnThisComputer(): void {
    const employeeId =
      this.journeyState?.privateTestEmployeeId || this.createdEmployee?.id || null;
    this.activationJourney.startPrivateEvaluation();
    this._sharedService.isOpenCloseSidebar.next({ force: true });
    // Prefer employee profile (lighter, already scoped) over the company Screenshots grid.
    if (employeeId) {
      void this.router.navigate(['/user-details', employeeId]);
      return;
    }
    void this.router.navigate(['/screenshots']);
  }

  downloadRemovalUtility(): void {
    this.removalDownloadStarted = true;
    this.activationJourney.trackEvent('removal_utility_downloaded', {
      employeeId: this.journeyState?.privateTestEmployeeId || this.createdEmployee?.id || null,
    });
    const os = this.getPrivateTestOs();
    const link = document.createElement('a');
    if (os === 'mac') {
      link.href = 'assets/Mac/Remove_SuperSee.command';
      link.download = 'Remove_SuperSee.command';
    } else if (os === 'linux') {
      link.href = 'assets/Linux/Remove_SuperSee.sh';
      link.download = 'Remove_SuperSee.sh';
    } else {
      link.href = RUST_AGENT_ASSET_PATHS.removeBat;
      link.download = 'Remove_SuperSee.bat';
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this._snackBar.open(
      os === 'mac'
        ? 'Downloaded. Open the file to remove SuperSee from this Mac. Screenshots already saved in your account are not deleted.'
        : os === 'linux'
          ? 'Downloaded. Run the file to remove SuperSee from this computer. Screenshots already saved in your account are not deleted.'
          : 'Downloaded. Right-click → Run as administrator to remove SuperSee from this computer. Screenshots already saved in your account are not deleted.',
      'OK',
      { duration: 7000 }
    );
  }

  selectGoal(goalId: ActivationGoalId): void {
    this.selectedGoal = goalId;
    this.activationJourney.setGoal(goalId);
  }

  confirmGoalSelection(): void {
    if (!this.selectedGoal) {
      this._snackBar.open('Please choose a goal to continue', 'OK', { duration: 3000 });
      return;
    }
    this.activationJourney.completeGoalAndAdvance();
  }

  continuePlanViewed(): void {
    this.activationJourney.markPlanViewed();
    this.modeEducationPage = 'compare';
  }

  continueModeEducationCompare(): void {
    this.modeEducationPage = 'choose';
    // Only seed the recommendation when nothing is chosen yet.
    // Never overwrite an explicit choice (or a previously confirmed journey mode).
    if (!this.selectedMode) {
      this.selectedMode = this.recommendedModeForGoal || 'employee_controlled';
    }
  }

  preparePrivateTestEmployee(downloadAfter = false): void {
    if (this.isLoading) {
      return;
    }
    // Reuse only the dedicated test login. A real employee must never become
    // the "install on this PC" app.
    if (this.createdEmployee?.id && this.isDedicatedPrivateTestEmployee(this.createdEmployee)) {
      this.privateTestReady = true;
      this.activationJourney.savePrivateTestIdentity({
        employeeId: Number(this.createdEmployee.id),
        email: this.createdEmployee.email || null,
        password: null,
      });
      if (downloadAfter) {
        this.downloadAgent();
      }
      return;
    }
    if (this.createdEmployee?.id) {
      this.createdEmployee = null;
      this.createdEmployeePassword = '';
      this.onboardingService.clearCreatedEmployee();
    }
    this.isLoading = true;
    this._createTestEmployee(downloadAfter);
  }

  private _createTestEmployee(downloadAfter: boolean, forceNew = false): void {
    const password = this._projectService.generatePassword();
    const email = this.buildPrivateTestPlaceholderEmail(forceNew);
    const payload = forceNew
      ? { email, password, reset_password: true }
      : { email, password };
    this.onboardingService.createOrReuseTestEmployee(payload).subscribe({
      next: (res: any) => {
        const employeeId = res?.employee_id;
        const finalPassword = res?.temporary_password || password;
        if (!employeeId) {
          if (!forceNew) {
            this._createTestEmployee(downloadAfter, true);
            return;
          }
          this._provisionFreshPrivateTestEmployee(downloadAfter);
          return;
        }

        this._loginService.getUser(employeeId).subscribe({
          next: (user: any) => {
            if (!user?.id) {
              this._recoverMissingPrivateTestUser(downloadAfter, forceNew);
              return;
            }
            this._finishPrivateTestSetup(
              employeeId,
              user?.email || user?.user_email || `test-${employeeId}@supersee.testaccount`,
              user?.name || user?.display_name || user?.first_name || 'This computer',
              downloadAfter,
              finalPassword
            );
          },
          error: (err) => {
            if (this.isMissingEmployeeError(err)) {
              this._recoverMissingPrivateTestUser(downloadAfter, forceNew);
              return;
            }
            this._finishPrivateTestSetup(
              employeeId,
              `test-${employeeId}@supersee.testaccount`,
              'This computer',
              downloadAfter,
              finalPassword
            );
          },
        });
      },
      error: (err) => {
        const code = String(err?.error?.code || '');
        if (code === 'email_exists' && !forceNew) {
          this._createTestEmployee(downloadAfter, true);
          return;
        }
        if (this.isMissingEmployeeError(err) || forceNew) {
          this._provisionFreshPrivateTestEmployee(downloadAfter);
          return;
        }
        this.privateTestIdentityRecreateInFlight = false;
        this.isLoading = false;
        const msg = err?.error?.message || 'Failed to prepare install on this computer';
        this._snackBar.open(msg, 'OK', { duration: 5000 });
      }
    });
  }

  private _recoverMissingPrivateTestUser(downloadAfter: boolean, alreadyForcingNew: boolean): void {
    this.forgetStalePrivateTestIdentity();
    if (!alreadyForcingNew) {
      this._createTestEmployee(downloadAfter, true);
      return;
    }
    this._provisionFreshPrivateTestEmployee(downloadAfter);
  }

  /** Last-resort create when /test-employee still points at a deleted WP user. */
  private _provisionFreshPrivateTestEmployee(downloadAfter: boolean): void {
    const password = this._projectService.generatePassword();
    const email = this.buildPrivateTestPlaceholderEmail(true);
    const os = this.deviceDetection.detectDesktopAgentOs();
    const operatingSystem = os === 'windows_legacy' ? 'windows' : os;
    const versionType = this.getModeToVersionType(this.resolvePrivateTestMode());
    this._loginService
      .addUser({
        first_name: 'This',
        last_name: 'computer',
        email,
        username: email,
        password,
        send_email: false,
        roles2: 'employee',
        version_type: versionType,
        operating_system: operatingSystem,
      })
      .subscribe({
        next: (response: any) => {
          const employeeId = response?.id;
          if (!employeeId) {
            this.privateTestIdentityRecreateInFlight = false;
            this.isLoading = false;
            this._snackBar.open('Could not prepare install on this computer', 'OK', { duration: 4000 });
            return;
          }
          this._finishPrivateTestSetup(
            employeeId,
            email,
            'This computer',
            downloadAfter,
            password
          );
        },
        error: (err) => {
          this.privateTestIdentityRecreateInFlight = false;
          this.isLoading = false;
          const msg = err?.error?.message || 'Failed to prepare install on this computer';
          this._snackBar.open(msg, 'OK', { duration: 5000 });
        },
      });
  }

  private _finishPrivateTestSetup(
    employeeId: number | string,
    email: string,
    displayName: string,
    downloadAfter: boolean,
    password?: string,
    firstName?: string,
    lastName?: string
  ): void {
    const mode = this.resolvePrivateTestMode();
    const versionType = this.getModeToVersionType(mode);
    this.selectedMode = mode;
    this.createdEmployee = {
      id: employeeId,
      email,
      first_name: firstName || displayName.split(' ')[0] || 'Employee',
      last_name: lastName ?? (displayName.split(' ').slice(1).join(' ') || ''),
      name: displayName,
      operating_system: this.deviceDetection.detectDesktopAgentOs(),
      version_type: versionType,
    };

    const finalize = (finalPassword: string): void => {
      this.createdEmployeePassword = finalPassword;
      this.onboardingService.saveCreatedEmployee(this.createdEmployee, finalPassword);
      this.activationJourney.setMode(mode);
      this.activationJourney.savePrivateTestIdentity({
        employeeId: Number(employeeId),
        email,
        password: null,
      });
      this.activationReadiness.setEvidence({
        packagePrepared: true,
        privateTestEmployeeId: Number(employeeId),
      });
      this.privateTestReady = true;
      this.isLoading = false;
      this.privateTestIdentityRecreateInFlight = false;

      this.applyPrivateTestModeSettings(employeeId, versionType);

      if (downloadAfter) {
        this.downloadAgent();
      }
    };

    if (password) {
      finalize(password);
      return;
    }

    // All installer types need a password so the downloaded package can
    // auto-login (general is upgraded to restricted at download time).
    const generatedPassword = this._projectService.generatePassword();
    this._projectService
      .resetUserPassword({ id: employeeId, email, password: generatedPassword } as any)
      .pipe(switchMap(() => this._projectService.updateEmployeeVersionType(employeeId, versionType)))
      .subscribe({
        next: () => finalize(generatedPassword),
        error: (err) => {
          this.isLoading = false;
          if (this.isMissingEmployeeError(err) && !this.privateTestIdentityRecreateInFlight) {
            this.recreatePrivateTestIdentityAndDownload();
            return;
          }
          this.privateTestIdentityRecreateInFlight = false;
          const msg =
            err?.error?.message || 'Failed to reset password for setup download';
          this._snackBar.open(msg, 'OK', { duration: 5000 });
        }
      });
  }

  /**
   * Collection stores Hidden as tray off (and, after save, mode hidden).
   * Older saves wrote that same choice as Manual. Tray off still means Hidden
   * unless the founder explicitly chose Automatic.
   */
  private onboardingChoseHidden(): boolean {
    return this.resolvePrivateTestMode() === 'hidden';
  }

  /** Journey mode wins over any stale employee.version_type from an earlier silent run. */
  private resolvePrivateTestMode(): string {
    const state = this.journeyState || this.activationJourney?.getState?.() || null;
    const fromCollection = installModeFromCollection(state);
    if (fromCollection === 'hidden' || fromCollection === 'admin_controlled') {
      return fromCollection;
    }
    return (
      this.selectedMode ||
      state?.mode ||
      this.recommendedModeForGoal ||
      'employee_controlled'
    );
  }

  /**
   * Hidden and Automatic on this install screen must land on the server before
   * the Settings / admin MSI is saved. Manual stays fire-and-forget.
   */
  private persistPrivateTestModeBeforeDownload(
    employeeId: number | string,
    versionType: 'general' | 'silent' | 'restricted'
  ): Promise<void> {
    if (versionType === 'general') {
      this.applyPrivateTestModeSettings(employeeId, versionType);
      return Promise.resolve();
    }
    const updateVersion = this._projectService.updateEmployeeVersionType?.bind(this._projectService);
    const version$ = updateVersion ? updateVersion(employeeId, versionType) : of(null);
    const settings$ =
      versionType === 'silent'
        ? this._projectService.createSilentUserSettings(String(employeeId))
        : this._projectService.createAdminControlUserSettings(String(employeeId));
    return new Promise((resolve) => {
      version$
        .pipe(
          switchMap(() => settings$),
          catchError(() => of(null))
        )
        .subscribe({
          next: () => resolve(),
          error: () => resolve(),
        });
    });
  }

  private applyPrivateTestModeSettings(
    employeeId: number | string,
    versionType: 'general' | 'silent' | 'restricted'
  ): void {
    const applyModeSettings =
      versionType === 'silent'
        ? this._projectService.createSilentUserSettings(String(employeeId))
        : versionType === 'restricted'
          ? this._projectService.createAdminControlUserSettings(String(employeeId))
          : this._projectService.createGeneralUserSettings(String(employeeId));
    applyModeSettings.subscribe({ error: () => {} });
  }

  downloadPrivateTestPackage(): void {
    if (this.installDownloadCtaLocked) {
      return;
    }
    if (this.privateTestIdentityRecreateInFlight) {
      return;
    }
    this.openPrivateTestLoginForDownload();
    this.privateTestDownloadRequestedByUser = true;
    if (!this.createdEmployee?.id || !this.isDedicatedPrivateTestEmployee(this.createdEmployee)) {
      this.preparePrivateTestEmployee(true);
      return;
    }

    // Junk placeholder domains look fake in Details — mint a fresh private-test@ identity.
    if (this.isJunkPrivateTestEmail(this.createdEmployee.email)) {
      this.recreatePrivateTestIdentityAndDownload();
      return;
    }

    // Ensure credentials exist so admin/hidden MSI packages include secrets.
    if (!this.resolveInstallerPassword() && this.createdEmployee?.email) {
      const password = this._projectService.generatePassword();
      this._projectService
        .resetUserPassword({
          id: this.createdEmployee.id,
          email: this.createdEmployee.email,
          password,
        } as any)
        .subscribe({
          next: () => {
            this.createdEmployeePassword = password;
            this.onboardingService.saveCreatedEmployee(this.createdEmployee, password);
            this.downloadAgent();
          },
          error: (err) => {
            if (this.isMissingEmployeeError(err)) {
              this.recreatePrivateTestIdentityAndDownload();
              return;
            }
            console.error('[Onboarding] Could not prepare credentials for the private-test download', err);
            const reason = err?.error?.message || err?.message;
            this._snackBar.open(
              reason ? `Could not prepare credentials: ${reason}` : 'Could not prepare credentials. Try again.',
              'OK',
              { duration: 6000 }
            );
          },
        });
      return;
    }
    this.downloadAgent();
  }

  private recreatePrivateTestIdentityAndDownload(): void {
    if (this.privateTestIdentityRecreateInFlight) {
      return;
    }
    this.privateTestIdentityRecreateInFlight = true;
    this.forgetStalePrivateTestIdentity();
    this.isLoading = true;
    this._createTestEmployee(true, true);
  }

  selectInstallRecovery(branch: InstallRecoveryBranch): void {
    this.installRecoveryBranch = branch;
    this.activationJourney.setInstallRecoveryBranch(branch);
    this.activationJourney.trackEvent('install_recovery_chip_selected', { branch });
    if (branch === 'continue_later') {
      this.continueLaterFromJourney();
    }
  }

  getInstallRecoveryCopy(branch: InstallRecoveryBranch | null): string {
    return this.getInstallRecoverySteps(branch).join(' ');
  }

  getInstallRecoverySteps(branch: InstallRecoveryBranch | null): string[] {
    const os = this.getPrivateTestOs();
    switch (branch) {
      case 'not_opened':
        if (os === 'mac') {
          return [
            'Open Finder and find the downloaded SuperSee setup file.',
            'If macOS blocks it, right-click the file → Open.',
            'Allow SuperSee under Accessibility and Screen & Audio Recording.',
          ];
        }
        if (os === 'linux') {
          return [
            'Open the downloaded setup file from your Downloads folder.',
            'Allow it to run as a program if asked, then enter your system password.',
            'Return here and wait for Connected.',
          ];
        }
        const hiddenWindows =
          this.createdEmployee?.version_type === 'silent' || this.selectedMode === 'hidden';
        const windowsUsesMsi = resolveRustAgentUsesMsiPackage(environment as any);
        const windowsFileHint = windowsUsesMsi ? 'SuperSee .msi' : 'SuperSee setup file';
        return [
          hiddenWindows
            ? 'Open your Downloads folder and find the Settings setup zip.'
            : `Open your Downloads folder and find the ${windowsFileHint}.`,
          hiddenWindows
            ? 'Unzip the whole folder first. Do not run the MSI from inside the zip.'
            : `Double-click that ${windowsFileHint}. When Windows asks to allow the app, choose Yes.`,
          hiddenWindows
            ? 'Double-click the Settings .msi (keep the secrets file next to it). When Windows asks to allow the app, choose Yes.'
            : 'If SuperSee asks you to log in, open Details on this page and paste the email and password.',
        ];
      case 'uac_denied':
        if (os === 'mac') {
          return [
            'macOS blocked the installer. Right-click the file → Open.',
            'Then allow SuperSee in System Settings → Privacy & Security.',
          ];
        }
        if (os === 'linux') {
          return [
            'Permission was denied. Re-run the installer from Terminal.',
            'Enter your system password when asked.',
          ];
        }
        const uacFileHint = resolveRustAgentUsesMsiPackage(environment as any)
          ? '.msi'
          : 'setup file';
        return [
          `Double-click the ${uacFileHint} again.`,
          'When Windows asks “Do you want to allow this app…?”, select Yes.',
          'Administrator permission is required so SuperSee can install and start on this PC.',
        ];
      case 'av_blocked':
        if (os === 'mac' || os === 'linux') {
          return [
            'Open Antivirus / security software and allow SuperSee if it was blocked.',
            'Pause Antivirus briefly if needed, then re-run the downloaded setup file.',
            'Turn Antivirus back on after SuperSee is installed.',
          ];
        }
        return [
          'Pause Antivirus temporarily (install only).',
          'Or open Windows Security → Protection history and Restore SuperSee if Antivirus quarantined it.',
          'Re-run setup, then turn Antivirus back on.',
        ];
      case 'pending':
        return [
          'SuperSee is installed. Restart the SuperSee app on this computer.',
          'Keep this page open and wait about a minute for Connected.',
          'If it stays offline, download again and re-run setup.',
        ];
      case 'continue_later':
        return ['Progress is saved. Come back to setup whenever you are ready.'];
      default:
        return [];
    }
  }

  continuePrivateTestToProof(): void {
    if (!this.isAgentOnline) {
      this._snackBar.open(
        'Wait until SuperSee connects on this computer, then continue. Pending install is not enough for a screenshot.',
        'OK',
        { duration: 5000 }
      );
      return;
    }
    this.activationJourney.completePrivateTest();
  }

  continuePrivateProofWithoutScreenshot(): void {
    // Soft escape removed from journey UI — kept as no-op guard.
    this._snackBar.open('Capture a private screenshot first.', 'OK', { duration: 3500 });
  }

  continuePrivateProofWithEvidence(): void {
    if (!this.isScreenshotCaptured && !this.privateProofEvidence) {
      this._snackBar.open('Capture a private screenshot first so you can see what SuperSee collected.', 'OK', {
        duration: 4000,
      });
      return;
    }
    this.activationJourney.completePrivateProof();
  }

  continueSetupConfirmed(): void {
    this.continueAccess();
  }

  get showPrivateTestCleanupCard(): boolean {
    const js = this.journeyState;
    return !!(
      js?.privateTestEmployeeId &&
      js?.firstEmployeeId &&
      js.privateTestEmployeeId !== js.firstEmployeeId &&
      js.keepPrivateTestData === null
    );
  }

  adjustModeFromSetup(): void {
    this.adjustingModeFromReview = true;
    this.modeEducationPage = 'choose';
    this.activationJourney.goToStep('mode_education');
  }

  cancelModeAdjustment(): void {
    this.adjustingModeFromReview = false;
    this.modeEducationPage = 'compare';
    this.activationJourney.goToStep('first_day_review');
  }

  formatProofCapturedAt(ts: number | null | undefined): string {
    if (!ts) {
      return 'Captured just now';
    }
    try {
      const ageMs = Date.now() - ts;
      if (ageMs >= 0 && ageMs < 5 * 60 * 1000) {
        return 'Captured just now';
      }
      return new Date(ts).toLocaleString();
    } catch {
      return 'Captured just now';
    }
  }

  onProofPreviewError(): void {
    this.proofPreviewFailed = true;
    this.activationReadiness.setEvidence({
      firstScreenshotReceived: true,
      screenshotPixelsDisplayable: false,
    });
  }

  retryProofPreview(): void {
    this.proofPreviewFailed = false;
    if (this.privateProofEvidence?.imgURL) {
      // Force img reload by rewriting URL with cache-buster.
      const base = this.privateProofEvidence.imgURL.split('#')[0];
      this.privateProofEvidence = {
        ...this.privateProofEvidence,
        imgURL: `${base}${base.includes('?') ? '&' : '?'}retry=${Date.now()}`,
      };
    }
  }

  openPrivateTestScreenshots(): void {
    const id = this.journeyState?.privateTestEmployeeId || this.createdEmployee?.id;
    if (id) {
      void this.router.navigate(['/screenshots'], { queryParams: { employeeId: String(id) } });
      return;
    }
    void this.router.navigate(['/screenshots']);
  }

  decidePrivateTestCleanup(decision: CleanupDecision): void {
    if (this.cleanupDecisionSaving) {
      return;
    }
    const privateId = this.journeyState?.privateTestEmployeeId;
    const firstId = this.journeyState?.firstEmployeeId;
    if (!privateId || !firstId || privateId === firstId) {
      this.cleanupDecisionError = 'Cleanup is only available after a real employee is added.';
      return;
    }

    const run = () => {
      this.cleanupDecisionSaving = decision;
      this.cleanupDecisionError = '';
      this.onboardingService.saveCleanupDecision(decision, privateId, firstId).subscribe({
        next: (res) => {
          this.cleanupDecisionSaving = null;
          if (res?.state) {
            this.activationJourney.replaceWithServerState(res.state as any);
          }
          this.activationJourney.trackEvent('private_test_cleanup_decision', {
            decision,
            result: res?.result,
          });
          if (decision === 'remove') {
            this._snackBar.open('Private-test employee removed. Cleanup of related records has started.', 'OK', {
              duration: 4500,
            });
          } else {
            this._snackBar.open('Private-test kept for comparison.', 'OK', { duration: 3500 });
          }
        },
        error: () => {
          this.cleanupDecisionSaving = null;
          this.cleanupDecisionError = 'Could not save that choice. Try again.';
        },
      });
    };

    if (decision === 'remove') {
      Swal.fire({
        title: 'Remove SuperSee from this computer?',
        html:
          '<p>This removes the private-test employee and its SuperSee records.</p><div class="ss-impact"><strong>This computer</strong>SuperSee stays installed. This does not uninstall the app.</div>',
        showCancelButton: true,
        confirmButtonColor: '#b42318',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Remove from this computer',
        cancelButtonText: 'Keep it for now',
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          run();
        }
      });
      return;
    }

    run();
  }

  selectSetupMethod(method: SetupMethod): void {
    this.setupMethod = method;
    this.firstEmployeeSilent = method === 'computer_setup';
    this.authorisedUseConfirmed = false;
    if (method === 'invite') {
      this.selectedMode =
        this.selectedMode === 'hidden'
          ? this.recommendedModeForGoal === 'hidden'
            ? 'employee_controlled'
            : this.recommendedModeForGoal
          : this.selectedMode || 'employee_controlled';
      this.employeeForm.os = this.employeeForm.os || 'windows';
    } else {
      this.selectedMode = 'hidden';
      this.employeeForm.os = 'windows';
    }
    const route = this.activationJourney.actorRouteForSetupMethod(method);
    this.actorRoute = route;
    this.activationJourney.setFirstEmployeeMeta({
      actorRoute: route,
      mode: method === 'computer_setup' ? 'hidden' : this.selectedMode,
    });
  }

  selectActorRoute(route: ActorRoute): void {
    this.actorRoute = route;
    this.setupMethod = this.activationJourney.setupMethodFromActorRoute(route);
    this.firstEmployeeSilent = this.setupMethod === 'computer_setup';
    this.activationJourney.setFirstEmployeeMeta({ actorRoute: route });
  }

  applyGoalRecommendedMode(): void {
    this.selectedMode = this.recommendedModeForGoal;
    this.firstEmployeeSilent = this.selectedMode === 'hidden';
  }

  onFirstEmployeeVisibilityChange(silent: boolean): void {
    this.firstEmployeeSilent = silent;
    const mode = silent ? 'hidden' : (this.selectedMode === 'admin_controlled' ? 'admin_controlled' : 'employee_controlled');
    this.activationJourney.setFirstEmployeeMeta({ mode });
  }

  isFirstEmployeeFormValid(): boolean {
    if (!this.employeeForm.name?.trim() || !this.employeeForm.os) {
      return false;
    }
    if (this.setupMethod === 'invite') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.employeeForm.email || '');
    }
    // Computer setup: email optional (placeholder generated).
    return this.setupMethod === 'computer_setup' && this.authorisedUseConfirmed;
  }

  private generateSilentPlaceholderEmail(): string {
    const slug = (this.employeeForm.name || 'employee')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '.')
      .replace(/^\.+|\.+$/g, '')
      .slice(0, 24) || 'employee';
    return `${slug}.${Date.now().toString(36)}@supersee.testaccount`;
  }

  /** Connected success CTAs: Review when ready, otherwise Done for now → dashboard. */
  finishFirstEmployeeAfterHeartbeat(reviewReady: boolean): void {
    this.activationJourney.completeFirstEmployee({ reviewReady });
    if (reviewReady) {
      this.activationJourney.markFirstDayReviewActionable();
      void this.router.navigate(['/setup/review'], { replaceUrl: true });
      return;
    }
    this._sharedService.isOpenCloseSidebar.next({ force: true });
    void this.router.navigate(['/dashboard'], { replaceUrl: true });
  }

  /**
   * Safe package recovery: rotate credentials, reapply mode, open canonical DownloadModal.
   * Never persists the fresh password.
   */
  createFreshFirstEmployeePackage(): void {
    if (this.isReissuingPackage || !this.hasRealFirstEmployee || !this.createdEmployee?.id) {
      return;
    }

    void Swal.fire({
      title: 'Create a fresh setup file?',
      html: '<p>This replaces the previous setup file for this employee.</p><div class="ss-impact"><strong>Important</strong>Use only the new file. The previous file should not be used.</div>',
      showCancelButton: true,
      confirmButtonText: 'Create fresh setup',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.runFreshPackageReissue();
    });
  }

  private resolveFirstEmployeeInstallContext(): {
    employee: any;
    versionType: 'general' | 'silent' | 'restricted';
    operatingSystem: 'windows' | 'mac' | 'linux' | 'windows_7_8';
  } {
    const employee = this.createdEmployee;
    const versionType: 'general' | 'silent' | 'restricted' =
      employee.version_type ||
      this.getModeToVersionType(
        this.journeyState?.firstEmployeeMode || this.selectedMode || 'employee_controlled'
      );
    const rawOs = (employee.operating_system || this.employeeForm?.os || 'windows').toLowerCase();
    const operatingSystem: 'windows' | 'mac' | 'linux' | 'windows_7_8' =
      rawOs === 'mac' || rawOs === 'macos'
        ? 'mac'
        : rawOs === 'linux'
        ? 'linux'
        : rawOs === 'windows_7_8' || rawOs === 'windows 7 & 8' || rawOs === 'win 7 & 8'
        ? 'windows_7_8'
        : 'windows';
    return { employee, versionType, operatingSystem };
  }

  /**
   * The setup file can be handed out again unchanged only when it needs no password (General)
   * or the password is still in this page's memory. It is never persisted, so after a reload
   * the only safe way to a file is the fresh-file flow, which replaces the old one.
   */
  get canRedownloadSetupFile(): boolean {
    if (!this.isHeroInstallsDelivery || !this.hasRealFirstEmployee || !this.createdEmployee?.id) {
      return false;
    }
    const { versionType } = this.resolveFirstEmployeeInstallContext();
    return versionType === 'general' || !!this.createdEmployeePassword;
  }

  /** Reopen the download for the first employee without changing their credentials. */
  getSetupFile(): void {
    if (!this.canRedownloadSetupFile) {
      this.createFreshFirstEmployeePackage();
      return;
    }
    const { employee, versionType, operatingSystem } = this.resolveFirstEmployeeInstallContext();
    this.openFreshPackageDownloadModal(
      employee,
      versionType,
      operatingSystem,
      this.createdEmployeePassword || '',
      false
    );
  }

  private runFreshPackageReissue(): void {
    if (this.isReissuingPackage || !this.createdEmployee?.id) {
      return;
    }
    this.isReissuingPackage = true;
    const { employee, versionType, operatingSystem } = this.resolveFirstEmployeeInstallContext();
    const employeeId = employee.id;
    const password = this._projectService.generatePassword();

    this._projectService
      .resetUserPassword({ id: employeeId, email: employee.email, password })
      .pipe(
        switchMap(() => this._projectService.updateEmployeeVersionType(employeeId, versionType)),
        switchMap(() => {
          if (versionType === 'silent') {
            return this._projectService.createSilentUserSettings(String(employeeId));
          }
          if (versionType === 'restricted') {
            return this._projectService.createAdminControlUserSettings(String(employeeId));
          }
          return this._projectService.createGeneralUserSettings(String(employeeId));
        }),
        catchError((err) => {
          console.error('[Onboarding] Fresh package reissue failed', err);
          this.isReissuingPackage = false;
          this._snackBar.open(
            'Could not create a fresh setup file. Try again, or update the employee from Employees.',
            'OK',
            { duration: 6000 }
          );
          return EMPTY;
        })
      )
      .subscribe(() => {
        // Keep password in component memory for this page lifetime only — never persist.
        this.createdEmployeePassword = password;
        this.openFreshPackageDownloadModal(employee, versionType, operatingSystem, password);
      });
  }

  private openFreshPackageDownloadModal(
    employee: any,
    versionType: 'general' | 'silent' | 'restricted',
    operatingSystem: 'windows' | 'mac' | 'linux' | 'windows_7_8',
    password: string,
    isReissue = true
  ): void {
    if (this.modalService.isAlreadyOpenDialog(DownloadModalComponent)) {
      this.modalService.closeDialogIfOpen(DownloadModalComponent);
    }

    const modalRef = this.modalService.openCommonMatDialog(DownloadModalComponent, {
      className: 'download-modal',
      disableClose: true,
      maxHeight: '90vh',
      componentData: {
        message: isReissue ? 'Fresh setup file ready' : 'Setup file ready',
        versionType,
        operatingSystem,
        email: employee.email,
        password,
        name: resolveEmployeeDisplayName(employee),
        first_name: employee.first_name,
        last_name: employee.last_name,
        activationHandoff: {
          source: 'activation_journey' as const,
          returnRoute: '/setup/first-employee' as const,
        },
      },
    });

    if (!modalRef?.afterClosed) {
      this.isReissuingPackage = false;
      return;
    }

    modalRef.afterClosed().pipe(take(1)).subscribe((closeResult: DownloadModalCloseResult | undefined) => {
      this.isReissuingPackage = false;
      if (closeResult?.downloadTriggered) {
        this.activationJourney.trackEvent(isReissue ? 'installer_reissued' : 'installer_redownloaded', {
          employeeId: employee.id,
          versionType,
          operatingSystem,
          source: 'first_employee_recovery',
        });
      }
      this.isDownloaded = true;
      this.resetConnectionRecoveryFlags();
      if (!this.isAgentOnline) {
        this.beginPrivateTestConnectWait();
      }
    });
  }

  statusPhaseIndex(phase: ActivationStatusPhase): number {
    return this.activationStatusPhases.findIndex((p) => p.id === phase);
  }

  isStatusPhaseReached(phase: ActivationStatusPhase): boolean {
    const current = this.journeyState?.activationStatusPhase || 'package_prepared';
    return this.statusPhaseIndex(phase) <= this.statusPhaseIndex(current);
  }

  refreshFirstDaySignals(): void {
    const js = this.journeyState || this.activationJourney.getState();
    const employeeId = js?.inviteFirstFromReady
      ? js.firstEmployeeId || this.createdEmployee?.id
      : this.journeyState?.firstEmployeeId || this.createdEmployee?.id;
    if (!employeeId || employeeId === this.journeyState?.privateTestEmployeeId) {
      return;
    }
    if (this.isRefreshingFirstDayEvidence) {
      return;
    }

    this.isRefreshingFirstDayEvidence = true;
    this.activationEvidence.load(Number(employeeId)).subscribe({
      next: (mapped) => {
        this.isRefreshingFirstDayEvidence = false;
        this.firstDayEvidenceRefreshFailed = false;
        this.firstDaySignals = {
          trackedMinutes: mapped.trackedActivityMinutes || null,
          activeMinutes: this.firstDaySignals.activeMinutes,
          idleMinutes: this.firstDaySignals.idleMinutes,
          appsCount: mapped.appLogSessionCount || null,
          sitesCount: this.firstDaySignals.sitesCount,
          screenshotsCount: mapped.screenshotCount || null,
          gapsNote:
            'Gaps appear when the agent is offline, paused, or not yet connected. We do not label time as “productive”.',
        };
        if (mapped.screenshotCount || mapped.trackedActivityMinutes > 0 || mapped.appLogSessionCount > 0) {
          this.activationJourney.setStatusPhase('first_data');
        }
        this.activationReadiness.setEvidence({
          ...mapped,
          privateTestEmployeeId: this.journeyState?.privateTestEmployeeId ?? null,
          agentAuthenticated: this.isAgentOnline,
          recentHeartbeat: this.isAgentOnline,
          onlineNow: this.isAgentOnline,
          packagePrepared: true,
        });
        if (this.activationReadiness.evaluate().earlyReviewReady) {
          this.activationJourney.markFirstDayReviewActionable();
        }
        this.syncFirstDayEvidenceRefreshTimer();
      },
      error: () => {
        this.isRefreshingFirstDayEvidence = false;
        this.firstDayEvidenceRefreshFailed = true;
        this.firstDaySignals.gapsNote =
          'Early signals will appear after the agent connects. We do not show undefended productive-time scores.';
      },
    });
  }

  /** Poll evidence immediately and every 60s while connected collecting/review screens are open. */
  private syncFirstDayEvidenceRefreshTimer(): void {
    const shouldRun =
      this.journeyEnabled &&
      (this.currentStepId === 'first_employee' || this.currentStepId === 'first_day_review') &&
      this.hasRealFirstEmployee &&
      this.isAgentOnline;
    if (!shouldRun) {
      this.stopFirstDayEvidenceRefresh();
      return;
    }
    if (this.evidenceRefreshSub) {
      return;
    }
    this.evidenceRefreshSub = timer(0, 60_000).subscribe(() => {
      if (!this.isAgentOnline || this.isRefreshingFirstDayEvidence) {
        return;
      }
      this.refreshFirstDaySignals();
    });
  }

  private stopFirstDayEvidenceRefresh(): void {
    this.evidenceRefreshSub?.unsubscribe();
    this.evidenceRefreshSub = undefined;
  }

  finishFirstDayReview(action: 'keep' | 'adjust' | 'scale'): void {
    const readiness = this.activationReadiness.evaluate();
    const transitionalOk = this.firstDayReviewReady;
    // Scale / add another employee requires early-review thresholds when evidence facade is active.
    if (action === 'scale' && readiness.hasEvidenceInput && !readiness.earlyReviewReady) {
      this._snackBar.open(
        'Add another employee unlocks after early-review readiness (real span, activity, and screenshots) — not from a first signal alone.',
        'OK',
        { duration: 6500 }
      );
      return;
    }
    if (!transitionalOk && action !== 'adjust') {
      this.refreshFirstDaySignals();
      this._snackBar.open(
        'First-day decisions need early signals. Wait for tracked time or a screenshot, Collect more data, or Adjust setup.',
        'OK',
        { duration: 6000 }
      );
      return;
    }

    if (action === 'adjust') {
      this.adjustingModeFromReview = true;
      this.modeEducationPage = 'choose';
      this.activationJourney.trackEvent('first_day_adjust_mode_opened');
      this.activationJourney.goToStep('mode_education');
      return;
    }

    this.activationJourney.finishJourney(action);
    if (action === 'scale') {
      this.onboardingService.setPermanentlyCompleted();
      this._sharedService.isOpenCloseSidebar.next({ force: true });
      this.router.navigate(['/employees']);
      return;
    }
    this.onboardingService.setPermanentlyCompleted();
    this.onboardingService.completeV2Onboarding('first_day_review_keep').subscribe({ error: () => {} });
    this.finishOnboarding();
  }

  /**
   * First Continue later before this computer connects pauses once for the
   * journey. Look around first pauses on its own, every visit.
   */
  private shouldHoldPrivateTestExit(): boolean {
    const onInstallBeforeConnect = this.currentStepId === 'private_test' && !this.isAgentOnline;
    if (!onInstallBeforeConnect) {
      return false;
    }
    if (this.privateTestExitNudgeVisible) {
      return false;
    }
    if (this.activationJourney.getState().privateTestExitNudgeShown) {
      return false;
    }
    this.privateTestExitNudgeVisible = true;
    this.activationJourney.markPrivateTestExitNudgeShown();
    return true;
  }

  stayForPrivateTest(): void {
    const source = this.currentStepId === 'plan_viewed' ? 'understand' : 'install';
    this.privateTestExitNudgeVisible = false;
    this.activationJourney.trackEvent('private_test_exit_nudge_stayed', { source });
    if (source === 'understand') {
      this.startPrivateTestFromReady();
    }
  }

  continueLaterFromJourney(): void {
    if (this.shouldHoldPrivateTestExit()) {
      return;
    }
    if (this.privateTestExitNudgeVisible) {
      this.privateTestExitNudgeVisible = false;
      this.activationJourney.trackEvent('private_test_exit_nudge_skipped', { source: 'install' });
    }
    this.activationJourney.continueLater();
    if (this.activationJourney.isPaidCompany()) {
      this.onboardingService.setPermanentlyCompleted();
    }
    const dest = this.activationDestination.resolveContinueLaterLanding(
      this.activationJourney.getState()
    );
    this._sharedService.isOpenCloseSidebar.next({ force: true });
    const go = (): void => {
      void this.router.navigate([dest.route], {
        queryParams: dest.queryParams || {},
        replaceUrl: dest.route.startsWith('/setup'),
      });
    };
    // Flush journey state to server before navigating so paid-complete / trial-pause survives sessions.
    this.activationJourney.flushServerSync().subscribe({
      next: () => go(),
      error: () => go(),
    });
  }

  checkingDBPath() {
    const dbPath = this.tokenEncryptionService.getEncryptedItem<string>('dbPath') as string;
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const userId = user.id;
    if (dbPath) {
      const isIncludeDefault = dbPath.includes("default");
      if (isIncludeDefault) {
        if (userId) {
          let path = `/users/${userId}`;
          this.tokenEncryptionService.setEncryptedItem('dbPath', path);
        }
      }
    } else {
      let path = `/users/${userId}`;
      this.tokenEncryptionService.setEncryptedItem('dbPath', path);
    }
    const dbPathAfter = this.tokenEncryptionService.getEncryptedItem<string>('dbPath') as string;
  }
}
